/* ════════════════════════════════════════════════════════════════════════════
   AMSX-Analytix — SALES TRACKER dashboards (sell-in / sell-out panel)  v2

   Seven dashboards modeled on the G04E commercial-diagnostic deck, now with
   the SAME control header as the pharma dashboards:
     · Filters:  Product · Molecule · ATC3 · ATC4   (multi-select, searchable)
     · Period:   Quarter | Year pills + value dropdown (latest auto-selected)
     · Measure:  Value (LC) | Units | Gross pills
     · Growth:   vs Previous Period | vs Similar Period (YoY)
   plus filter-aware TOTAL CARDS on every dashboard (with growth arrows).

   Data: one shared server payload (dashKey st_* → same blob):
     rows              flow × gov × maker × brand × molecule × atc3/4 × class × qtr
     mp._stExtra.monthly / .pharma / .sku   (all carrying md/mol/a3/a4)
   Deck metric language: sell-through % = sell-out ÷ sell-in; stock overhang =
   sell-in ÷ sell-out (gross units); values in LC (IQD).
   ════════════════════════════════════════════════════════════════════════════ */

import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, Cell, ReferenceLine, ScatterChart, Scatter, ZAxis, LineChart, LabelList
} from "recharts";
import { MONTH_S } from "../constants";

/* ── palette — IDENTICAL to the pharma dashboards (Dashboard_index) ────────
   Categorical series come off the pharma ramp in order: navy -> mid blue ->
   light blue -> pale blue. Nothing outside this ramp is used for a data series;
   amber and red appear only as semantic accents (warning / decline), exactly
   as the pharma dashboards use them. */
var PH_COLS = ["#0F5C66", "#17707C", "#2E8792", "#4EAEB8", "#7BC6CE", "#3FA3AD", "#55AFB8", "#6BBCC4", "#8ECBD2", "#B2DDE2"];
/* same ten pharma colours for overlaid line series, with the navy (#0F5C66)
   moved to the end: navy is the focus-manufacturer highlight, so it must not
   also land on a rival line. Rivals therefore walk the blue ramp, greens only
   appear once a chart carries more than five series. */
var PH_SERIES = ["#0F5C66", "#17707C", "#2E8792", "#4EAEB8", "#7BC6CE", "#55AFB8", "#6BBCC4", "#8ECBD2", "#B2DDE2", "#3FA3AD"];
var C_IN  = "#0F5C66";   /* Sell-In  — deep teal  (paired series #1) */
var C_OUT = "#3FA3AD";   /* Sell-Out — light aqua (paired series #2) */
var C_ACC = "#D98E32";   /* amber accent (growth / overlay lines) */
var C_POS = "#0F5C66", C_NEG = "#dc2626", C_WARN = "#b45309";
var GRAY = "#94a3b8";

/* ── Axis titles ───────────────────────────────────────────────────────────
   No axis is left unexplained: each one states what it carries and in which
   unit. Recharts draws these inside the band reserved by XAxis height /
   YAxis width, so every chart below sets those explicitly instead of relying
   on chart margins (which the legend also competes for). */
var AX_TXT = { fontSize: 9.5, fill: "#64748b", fontWeight: 700, letterSpacing: ".04em" };
function axX(t) { return { value: t, position: "insideBottom", offset: 0, style: AX_TXT }; }
function axY(t) { return { value: t, angle: -90, position: "insideLeft", offset: 14, style: Object.assign({ textAnchor: "middle" }, AX_TXT) }; }
function axYR(t) { return { value: t, angle: 90, position: "insideRight", offset: 14, style: Object.assign({ textAnchor: "middle" }, AX_TXT) }; }
/* Padded numeric domains for scatter axes. Recharts' "auto" collapses to a
   zero-width axis when every point shares a value (one SKU, one maker), which
   printed the same tick five times; these keep 10% of air on each side and
   handle negative minima. */
function axRound(v) { var a = Math.abs(v); return +v.toFixed(a >= 1000 ? 0 : a >= 10 ? 1 : 3); }
function axDMin(d) { if (typeof d !== "number" || !isFinite(d)) return 0; if (d === 0) return 0; return axRound(d < 0 ? d * 1.1 : d * 0.9); }
function axDMax(d) { if (typeof d !== "number" || !isFinite(d)) return 1; if (d === 0) return 1; return axRound(d < 0 ? d * 0.9 : d * 1.1); }
var AX_NUM = [axDMin, axDMax];
/* bubble captions — a bubble chart is unreadable without the names on it */
function bubbleLbl(props) {
  return <LabelList dataKey={props && props.dk ? props.dk : "name"} position="top" offset={7}
    style={{ fontSize: 8.5, fontWeight: 700, fill: "#334155", pointerEvents: "none" }} />;
}

function fmtN(n) { return (n == null || isNaN(n)) ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }); }
function fmtM(n) {
  if (n == null || isNaN(n)) return "—";
  var a = Math.abs(n);
  if (a >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (a >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (a >= 1e3) return (n / 1e3).toFixed(0) + "K";
  return String(Math.round(n));
}
function pct1(a, b) { return b > 0 ? (a / b * 100) : 0; }
/* r178 (Noura): FOC % = FOC units ÷ PAID units (gross − FOC), no longer ÷ gross */
function focPct9(fu, gu) { fu = Number(fu) || 0; return pct1(fu, (Number(gu) || 0) - fu); }
function gpct(cur, base) { return (base != null && base > 0 && cur != null) ? (cur - base) / base * 100 : null; }

/* ── pharma dashboard template tokens (same as Dashboard_index) ── */
var PAGE_BG = "linear-gradient(160deg,#F2F8F8 0%,#E9F2F3 40%,#F4F9F9 100%)";
var CARD_ST = { background: "#fff", borderRadius: 18, border: "1px solid rgba(0,0,0,.06)", boxShadow: "0 1px 3px rgba(0,0,0,.04), 0 8px 30px rgba(0,0,0,.04)", position: "relative", overflow: "hidden" };
var G_NAVY = "linear-gradient(90deg,#0F5C66,#17707C)", G_BLUE = "linear-gradient(90deg,#17707C,#4EAEB8)",
    G_TEAL = "linear-gradient(90deg,#3FA3AD,#6BBCC4)", G_VIOLET = "linear-gradient(90deg,#2E8792,#7BC6CE)",
    G_AMBER = "linear-gradient(90deg,#b45309,#d8973c)";

/* ════════════════════════ CONTROL STATE + HEADER ════════════════════════ */

/* Multi-select filter dropdown (searchable checklist — pharma header styling) */
function STFilter(props) {
  var open = props.open, setOpen = props.setOpen, id = props.id;
  var [q, setQ] = useState("");
  var sel = props.value || [];
  var opts = props.options || [];
  var shown = q ? opts.filter(function (o) { return o.toLowerCase().indexOf(q.toLowerCase()) >= 0; }) : opts;
  var isOpen = open === id;
  var active = sel.length > 0;
  var tog = function (v) {
    var nx = sel.indexOf(v) >= 0 ? sel.filter(function (x) { return x !== v; }) : sel.concat([v]);
    props.onChange(nx);
  };
  return (
    <div style={{ position: "relative" }}>
      <div style={{ fontSize: 8, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: 3 }}>{props.label}</div>
      <button onClick={function () { setOpen(isOpen ? "" : id); setQ(""); }}
        style={{ background: active ? "#EAF4F5" : "#fff", border: "1.5px solid " + (active ? "#17707C" : "#e2e8f0"), borderRadius: 10,
          padding: "6px 10px", fontSize: 10, fontWeight: 700, color: active ? "#17707C" : "#475569", fontFamily: "inherit",
          cursor: "pointer", outline: "none", maxWidth: 150, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {active ? props.label + " (" + sel.length + ")" : "All"} ▾
      </button>
      {isOpen && (
        <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 60, width: 240, background: "#fff",
          border: "1px solid #cbd5e1", borderRadius: 10, boxShadow: "0 12px 32px rgba(15,23,42,.18)", padding: 8 }}>
          <input autoFocus value={q} onChange={function (e) { setQ(e.target.value); }} placeholder={"Search " + props.label.toLowerCase() + "…"}
            style={{ width: "100%", boxSizing: "border-box", padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 10.5, fontFamily: "inherit", outline: "none", marginBottom: 6 }} />
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 9, color: "#94a3b8" }}>{sel.length + " selected"}</span>
            {sel.length > 0 && <span onClick={function () { props.onChange([]); }} style={{ fontSize: 9, color: "#dc2626", fontWeight: 700, cursor: "pointer" }}>Clear</span>}
          </div>
          <div style={{ maxHeight: 210, overflowY: "auto" }}>
            {shown.slice(0, 300).map(function (o) {
              var on = sel.indexOf(o) >= 0;
              return (
                <div key={o} onClick={function () { tog(o); }}
                  style={{ display: "flex", alignItems: "center", gap: 7, padding: "4px 6px", borderRadius: 6, cursor: "pointer",
                    background: on ? "#EAF4F5" : "transparent", marginBottom: 1 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, flexShrink: 0, border: "1.5px solid " + (on ? "#17707C" : "#cbd5e1"),
                    background: on ? "#17707C" : "#fff", color: "#fff", fontSize: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>{on ? "✓" : ""}</div>
                  <span style={{ fontSize: 10.5, color: on ? "#17707C" : "#334155", fontWeight: on ? 700 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o}</span>
                </div>
              );
            })}
            {shown.length === 0 && <div style={{ fontSize: 10, color: "#94a3b8", padding: 6 }}>No matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* Control state: filters + period + measure + growth (one hook per dashboard) */
function useSTControls(rows, mp) {
  var [flt, setFlt] = useState({ md: [], mol: [], a3: [], a4: [] });
  var [ptype, setPtype] = useState("quarter"); /* quarter | year */
  var [pval, setPval] = useState(null);        /* qk = yr*10+q  |  year */
  /* USD (view v4: ValueUSD = LC × FX) is the default when the payload carries it —
     same as the pharma dashboards; LC / Units / Gross remain one click away. */
  var hasUSD = !!(mp && mp.valUSD);
  var [measure, setMeasure] = useState(hasUSD ? "d" : "l");   /* d | l | u | gu */
  useEffect(function () { if (!hasUSD && measure === "d") setMeasure("l"); }, [hasUSD]);
  var [growth, setGrowth] = useState("prev");  /* prev | yoy */
  var [openF, setOpenF] = useState("");
  /* Segment lens (R1): All | Local | Imported — from Local_Imported (li).
     Generic | Originator is wired the same way once a product flag exists (mp.productType). */
  var [seg, setSeg] = useState("");

  var opts = useMemo(function () {
    var md = {}, mol = {}, a3 = {}, a4 = {}, qs = {}, ys = {}, li = {};
    (rows || []).forEach(function (r) {
      if (r.md) md[r.md] = 1; if (r.mol) mol[r.mol] = 1;
      if (r.a3) a3[r.a3] = 1; if (r.a4) a4[r.a4] = 1;
      if (r.li) li[r.li] = 1;
      if (r.yr && r.qr) { qs[r.yr * 10 + r.qr] = 1; ys[r.yr] = 1; }
    });
    var srt = function (o) { return Object.keys(o).sort(); };
    return { md: srt(md), mol: srt(mol), a3: srt(a3), a4: srt(a4), segs: srt(li),
      quarters: Object.keys(qs).map(Number).sort(function (a, b) { return a - b; }),
      years: Object.keys(ys).map(Number).sort(function (a, b) { return a - b; }) };
  }, [rows]);

  /* auto-select latest period when type changes / data arrives */
  useEffect(function () {
    if (ptype === "quarter" && opts.quarters.length) setPval(opts.quarters[opts.quarters.length - 1]);
    else if (ptype === "year" && opts.years.length) setPval(opts.years[opts.years.length - 1]);
  }, [ptype, opts.quarters.length, opts.years.length]);

  var eff = useMemo(function () {
    var pv = pval || (ptype === "quarter" ? opts.quarters[opts.quarters.length - 1] : opts.years[opts.years.length - 1]) || 0;
    /* current + BOTH comparison windows (prev period / similar period LY) as sets of qk */
    var curSet = {}, prevSet = {}, yoySet = {};
    var curLbl = "—", prevLbl = "—", yoyLbl = "—";
    if (ptype === "quarter" && pv) {
      curSet[pv] = 1; curLbl = "Q" + (pv % 10) + " " + Math.floor(pv / 10);
      var py = Math.floor(pv / 10), pq = pv % 10;
      var prev = pq > 1 ? py * 10 + (pq - 1) : (py - 1) * 10 + 4;
      var yoy = (py - 1) * 10 + pq;
      prevSet[prev] = 1; prevLbl = "Q" + (prev % 10) + " " + Math.floor(prev / 10);
      yoySet[yoy] = 1; yoyLbl = "Q" + (yoy % 10) + " " + Math.floor(yoy / 10);
    } else if (ptype === "year" && pv) {
      [1, 2, 3, 4].forEach(function (q) { curSet[pv * 10 + q] = 1; prevSet[(pv - 1) * 10 + q] = 1; yoySet[(pv - 1) * 10 + q] = 1; });
      curLbl = String(pv); prevLbl = String(pv - 1); yoyLbl = String(pv - 1);
    }
    /* base = the growth-mode-selected window (drives share change, momentum, tables) */
    var baseSet = growth === "yoy" ? yoySet : prevSet;
    var baseLbl = growth === "yoy" ? yoyLbl : prevLbl;
    var dimOk = function (r) {
      if (seg && r.li !== seg) return false;
      if (flt.md.length && flt.md.indexOf(r.md) < 0) return false;
      if (flt.mol.length && flt.mol.indexOf(r.mol) < 0) return false;
      if (flt.a3.length && flt.a3.indexOf(r.a3) < 0) return false;
      if (flt.a4.length && flt.a4.indexOf(r.a4) < 0) return false;
      return true;
    };
    var inCur = function (r) { return curSet[r.yr * 10 + r.qr] === 1; };
    var inBase = function (r) { return baseSet[r.yr * 10 + r.qr] === 1; };
    var inPrev = function (r) { return prevSet[r.yr * 10 + r.qr] === 1; };
    var inYoy = function (r) { return yoySet[r.yr * 10 + r.qr] === 1; };
    var monQSet = function (r) { return r.yr * 10 + (Math.ceil(r.mn / 3)); };
    var monInCur = function (r) { return curSet[monQSet(r)] === 1; };
    var nFlt = flt.md.length + flt.mol.length + flt.a3.length + flt.a4.length + (seg ? 1 : 0);
    return { pv: pv, curSet: curSet, baseSet: baseSet, curLbl: curLbl, baseLbl: baseLbl,
      prevLbl: prevLbl, yoyLbl: yoyLbl, gsel: growth, seg: seg,
      dimOk: dimOk, inCur: inCur, inBase: inBase, inPrev: inPrev, inYoy: inYoy, monInCur: monInCur, nFlt: nFlt };
  }, [ptype, pval, growth, flt, opts, seg]);

  return { flt: flt, setFlt: setFlt, ptype: ptype, setPtype: setPtype, pval: pval, setPval: setPval,
    measure: measure, setMeasure: setMeasure, growth: growth, setGrowth: setGrowth,
    seg: seg, setSeg: setSeg,
    openF: openF, setOpenF: setOpenF, opts: opts, eff: eff,
    hasUSD: hasUSD, isValue: measure === "d" || measure === "l",
    priceKey: measure === "d" ? "pu" : "pl",
    msrLbl: measure === "d" ? "Value (USD)" : measure === "l" ? "Value (LC)" : measure === "u" ? "Units" : "Gross Units",
    msrFmt: (measure === "d" || measure === "l") ? fmtM : fmtN };
}

/* The pharma-style dashboard header: icon/title + Filters · Period · Measure · Growth */
function STHeader(props) {
  var C = props.ctl;
  var divider = <div style={{ width: 1, height: 28, background: "#e2e8f0", flexShrink: 0 }} />;
  var setF = function (k) { return function (v) { var nx = Object.assign({}, C.flt); nx[k] = v; C.setFlt(nx); }; };
  var pOptions = C.ptype === "quarter"
    ? C.opts.quarters.map(function (q) { return { key: q, label: "Q" + (q % 10) + " " + Math.floor(q / 10) }; })
    : C.opts.years.map(function (y) { return { key: y, label: String(y) }; });
  return (
    <div style={{ position: "relative", zIndex: 50 }}>
      {C.openF && <div onClick={function () { C.setOpenF(""); }} style={{ position: "fixed", inset: 0, zIndex: 40 }} />}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8, flexWrap: "wrap" }}>
        <div style={{ width: 44, height: 44, borderRadius: 14, background: props.iconBg || "linear-gradient(135deg,#0F5C66,#17707C)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, boxShadow: "0 4px 16px rgba(15,92,102,.25)" }}><span>{props.icon || "📊"}</span></div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{props.title}</div>
          <div style={{ fontSize: 10.5, color: "#64748b" }}>{props.subtitle || ""}</div>
        </div>
        <div style={{ flex: 1 }} />
        {/* Filters */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, position: "relative", zIndex: 55 }}>
          <STFilter id="md" label="Product" options={C.opts.md} value={C.flt.md} onChange={setF("md")} open={C.openF} setOpen={C.setOpenF} />
          <STFilter id="mol" label="Molecule" options={C.opts.mol} value={C.flt.mol} onChange={setF("mol")} open={C.openF} setOpen={C.setOpenF} />
          <STFilter id="a3" label="ATC3" options={C.opts.a3} value={C.flt.a3} onChange={setF("a3")} open={C.openF} setOpen={C.setOpenF} />
          <STFilter id="a4" label="ATC4" options={C.opts.a4} value={C.flt.a4} onChange={setF("a4")} open={C.openF} setOpen={C.setOpenF} />
        </div>
        {divider}
        {/* Segment lens: All | Local | Imported (Generic | Originator when a product flag exists) */}
        {C.opts.segs.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 3, background: "#f1f5f9", borderRadius: 10, padding: 3 }}>
            <span style={{ fontSize: 8, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", padding: "0 4px" }}>Segment</span>
            {[{ k: "", l: "All" }].concat(C.opts.segs.map(function (v) { return { k: v, l: v }; })).map(function (o) {
              var on = C.seg === o.k;
              return (
                <button key={o.k || "all"} onClick={function () { C.setSeg(o.k); }}
                  style={{ padding: "5px 11px", borderRadius: 6, border: "none", fontSize: 10, fontWeight: on ? 800 : 500,
                    background: on ? "#0F5C66" : "transparent", color: on ? "#fff" : "#64748b", cursor: "pointer", fontFamily: "inherit" }}>{o.l}</button>
              );
            })}
          </div>
        )}
        {C.opts.segs.length > 0 && divider}
        {/* Period: type pills + value dropdown */}
        <div style={{ display: "flex", alignItems: "center", background: "#f1f5f9", borderRadius: 10, padding: 3, gap: 3 }}>
          {[{ k: "quarter", l: "Quarter" }, { k: "year", l: "Year" }].map(function (t) {
            var on = C.ptype === t.k;
            return (
              <button key={t.k} onClick={function () { C.setPtype(t.k); }}
                style={{ padding: "5px 12px", borderRadius: 6, border: "none", fontSize: 10, fontWeight: on ? 800 : 500,
                  background: on ? "#17707C" : "transparent", color: on ? "#fff" : "#64748b", cursor: "pointer",
                  fontFamily: "inherit", boxShadow: on ? "0 1px 4px rgba(15,92,102,.35)" : "none" }}>{t.l}</button>
            );
          })}
          <div style={{ width: 1, height: 18, background: "#cbd5e1", margin: "0 2px" }} />
          <select value={String(C.eff.pv)} onChange={function (e) { C.setPval(Number(e.target.value)); }}
            style={{ padding: "5px 10px", borderRadius: 6, border: "none", fontSize: 10.5, fontWeight: 700, color: "#17707C",
              background: "#fff", cursor: "pointer", fontFamily: "inherit", minWidth: 90, outline: "none", boxShadow: "0 1px 3px rgba(0,0,0,.08)" }}>
            {pOptions.map(function (o) { return <option key={o.key} value={o.key}>{o.label}</option>; })}
          </select>
        </div>
        {divider}
        {/* Measure pills */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {(C.hasUSD ? [{ k: "d", l: "USD" }] : []).concat([{ k: "l", l: "Value (LC)" }, { k: "u", l: "Units" }, { k: "gu", l: "Gross" }]).map(function (o) {
            var on = C.measure === o.k;
            return (
              <button key={o.k} onClick={function () { C.setMeasure(o.k); }}
                style={{ padding: "5px 12px", border: on ? "1.5px solid #0F5C66" : "1.5px solid #e2e8f0", borderRadius: 20,
                  background: on ? "#E2F1F3" : "#fff", color: on ? "#0F5C66" : "#64748b", fontSize: 10, fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit" }}>{o.l}</button>
            );
          })}
        </div>
        {divider}
        {/* Growth */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 8.5, color: "#94a3b8", fontWeight: 600, letterSpacing: ".04em" }}>GROWTH</span>
          <select value={C.growth} onChange={function (e) { C.setGrowth(e.target.value); }}
            style={{ padding: "5px 8px", border: "1.5px solid #e2e8f0", borderRadius: 20, background: "#fff", color: "#334155",
              fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", outline: "none" }}>
            <option value="prev">vs Previous Period</option>
            <option value="yoy">vs Similar Period</option>
          </select>
        </div>
      </div>
      {C.eff.nFlt > 0 && (
        <div style={{ fontSize: 9.5, color: "#17707C", fontWeight: 700, marginBottom: 8 }}>
          {"⚑ Filtered: "}
          {C.seg ? "Segment (" + C.seg + ") " : ""}
          {["md", "mol", "a3", "a4"].map(function (k) {
            return C.flt[k].length ? ({ md: "Product", mol: "Molecule", a3: "ATC3", a4: "ATC4" })[k] + " (" + C.flt[k].join(", ").slice(0, 60) + ") " : "";
          }).join("")}
          <span onClick={function () { C.setFlt({ md: [], mol: [], a3: [], a4: [] }); C.setSeg(""); }} style={{ color: "#dc2626", cursor: "pointer", marginLeft: 6 }}>✕ clear</span>
        </div>
      )}
    </div>
  );
}

/* Filter-aware TOTAL CARDS — pharma dashboard template: gradient top bar, icon,
   big value, twin growth boxes (vs Previous Period / vs Similar Period) */
function STTotals(props) {
  var fp = function (g) { return g != null ? ((g >= 0 ? "▲ " : "▼ ") + Math.abs(g).toFixed(1) + "%") : "—"; };
  var gc = function (g) { return g == null ? "#94a3b8" : g > 0 ? "#0F5C66" : g < 0 ? "#dc2626" : "#64748b"; };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 14 }}>
      {props.items.map(function (it, i) {
        var hasG = it.pg !== undefined || it.yg !== undefined;
        return (
          <div key={i} style={Object.assign({}, CARD_ST, { padding: "11px 14px 10px" })}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: it.grad || G_NAVY, borderRadius: "18px 18px 0 0" }} />
            {/* r65 compact KPI (same as the pharma dashboards) */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginTop: 2 }}>
              <span style={{ fontSize: 8.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".06em", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.label}</span>
              {it.icon && <span style={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }}>{it.icon}</span>}
            </div>
            <div style={{ fontSize: 21, fontWeight: 800, color: it.color || "#0f172a", letterSpacing: "-.03em", lineHeight: 1, margin: "4px 0 7px", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.value}</div>
            {hasG ? (
              <div style={{ display: "flex", gap: 0, alignItems: "stretch" }}>
                <div style={{ flex: 1, padding: "5px 8px", background: "#f8fafb", borderRadius: "8px 0 0 8px", border: "1px solid #f1f5f9", borderRight: "none" }}>
                  <div style={{ fontSize: 6.8, color: "#94a3b8", fontWeight: 600, marginBottom: 3, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>vs Previous</div>
                  <span style={{ fontWeight: 700, fontSize: 10, color: gc(it.pg) }}>{fp(it.pg)}</span>
                </div>
                <div style={{ width: 1, background: "#e2e8f0", flexShrink: 0 }} />
                <div style={{ flex: 1, padding: "5px 8px", background: "#f8fafb", borderRadius: "0 8px 8px 0", border: "1px solid #f1f5f9", borderLeft: "none" }}>
                  <div style={{ fontSize: 6.8, color: "#94a3b8", fontWeight: 600, marginBottom: 3, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>vs Similar</div>
                  <span style={{ fontWeight: 700, fontSize: 10, color: gc(it.yg) }}>{fp(it.yg)}</span>
                </div>
              </div>
            ) : (it.sub ? <div style={{ fontSize: 9.5, color: "#94a3b8", lineHeight: 1.5 }}>{it.sub}</div> : null)}
          </div>
        );
      })}
    </div>
  );
}

/* ── PANEL DISCLOSURE strip (same statement as the Market tab) — panel size from
   the pharmacy grain already in the payload; per-class from PharmacyClass. ── */
function STPanelNote(props) {
  var mp = props.mp;
  var info = useMemo(function () {
    var ph = {}, cls = {};
    ((mp && mp._stExtra && mp._stExtra.pharma) || []).forEach(function (p) {
      if (!p.ph) return;
      ph[p.ph] = 1;
      if (p.phc) cls[p.phc] = cls[p.phc] || {}; if (p.phc) cls[p.phc][p.ph] = 1;
    });
    var n = Object.keys(ph).length;
    var clsList = Object.keys(cls).sort().map(function (k) { return "Class " + k + ": " + Object.keys(cls[k]).length.toLocaleString(); });
    return { n: n, cls: clsList };
  }, [mp]);
  if (!info.n) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "6px 12px", marginBottom: 12,
                  background: "linear-gradient(90deg,#F0F7F7,#FAFCFC)", border: "1px solid #C6E0E3", borderRadius: 9, fontSize: 10.5, color: "#0F4A52" }}>
      <span style={{ fontSize: 13 }}>🏪</span>
      <span style={{ fontWeight: 800, letterSpacing: ".02em" }}>{"PANEL OF " + info.n.toLocaleString() + " PHARMACIES"}</span>
      {info.cls.length > 0 && <span style={{ color: "#33707A" }}>{"(" + info.cls.join(" · ") + ")"}</span>}
      <span style={{ color: "#58767C", fontStyle: "italic", marginLeft: "auto" }}>Figures reflect the actual purchasing &amp; dispensing behaviour of the panel pharmacies — not projected to the total market.</span>
    </div>
  );
}

/* -- Manufacturer picker: searchable single-select menu (scales past a handful
   of chips - a therapeutic class can hold 15+ makers). items = [{name, rank,
   sub}] where sub is an optional right-aligned metric string. -- */
function STMakerPicker(props) {
  var items = props.items || [];
  var value = props.value || "";
  var [open, setOpen] = useState(false);
  var [q, setQ] = useState("");
  var accent = props.accent || "#0F5C66";
  var cur = items.filter(function (m) { return m.name === value; })[0] || null;
  var shown = q ? items.filter(function (m) { return String(m.name).toLowerCase().indexOf(q.toLowerCase()) >= 0; }) : items;
  return (
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 6 }}>
      {props.label && <span style={{ fontSize: 9, fontWeight: 800, color: accent, textTransform: "uppercase", letterSpacing: ".05em" }}>{props.label}</span>}
      <button onClick={function () { setOpen(!open); setQ(""); }}
        style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 11px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
          background: accent, color: "#fff", border: "1.5px solid " + accent, fontSize: 10.5, fontWeight: 800, maxWidth: 320, whiteSpace: "nowrap" }}>
        {cur && cur.rank != null && <span style={{ background: "rgba(255,255,255,.22)", borderRadius: 8, padding: "0 6px", fontSize: 9.5 }}>{"#" + cur.rank}</span>}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{cur ? cur.name : (items.length ? "Select manufacturer" : "No manufacturers")}</span>
        {cur && cur.sub && <span style={{ fontSize: 9.5, fontWeight: 700, opacity: .85 }}>{cur.sub}</span>}
        <span style={{ fontSize: 8, opacity: .8 }}>{open ? "\u25B2" : "\u25BC"}</span>
      </button>
      {items.length > 1 && <span style={{ fontSize: 9, color: "#94a3b8" }}>{items.length + " makers"}</span>}
      {open && <div onClick={function () { setOpen(false); }} style={{ position: "fixed", inset: 0, zIndex: 60 }} />}
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 5, zIndex: 61, width: 330, background: "#fff",
          border: "1px solid #cbd5e1", borderRadius: 10, boxShadow: "0 14px 34px rgba(15,23,42,.2)", padding: 8 }}>
          <input autoFocus value={q} onChange={function (e) { setQ(e.target.value); }} placeholder="Search manufacturer..."
            style={{ width: "100%", boxSizing: "border-box", padding: "6px 9px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 10.5, fontFamily: "inherit", outline: "none", marginBottom: 6 }} />
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {shown.map(function (m) {
              var on = m.name === value;
              return (
                <div key={m.name} title={m.name} onClick={function () { props.onChange(m.name); setOpen(false); }}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 7px", borderRadius: 6, cursor: "pointer", marginBottom: 1,
                    background: on ? "#E2F1F3" : "transparent" }}>
                  {m.rank != null && <span style={{ width: 26, flexShrink: 0, textAlign: "right", fontSize: 9.5, fontWeight: 800, color: "#94a3b8" }}>{"#" + m.rank}</span>}
                  <span style={{ flex: 1, fontSize: 10.5, fontWeight: on ? 800 : 600, color: on ? accent : "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</span>
                  {m.sub && <span style={{ fontSize: 9.5, fontWeight: 700, color: "#64748b", whiteSpace: "nowrap" }}>{m.sub}</span>}
                </div>
              );
            })}
            {shown.length === 0 && <div style={{ fontSize: 10, color: "#94a3b8", padding: 6 }}>No matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── panel / bars / notes (deck-styled, unchanged from v1) ── */
function STPanel(props) {
  return (
    <div style={Object.assign({}, CARD_ST, { padding: "18px 20px", marginBottom: 16, flex: props.flex, minWidth: props.minWidth || 0 })}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: props.grad || G_NAVY, borderRadius: "18px 18px 0 0" }} />
      <div style={{ fontSize: 12, fontWeight: 800, color: "#0F5C66", letterSpacing: ".04em", textTransform: "uppercase", marginTop: 2 }}>{props.title}</div>
      {props.sub && <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 2 }}>{props.sub}</div>}
      <div style={{ marginTop: 12 }}>{props.children}</div>
    </div>
  );
}
function STBars(props) {
  var items = props.items || [];
  var max = 0;
  items.forEach(function (it) { if (it.val > max) max = it.val; });
  return (
    <div>
      {items.map(function (it, i) {
        var w = max > 0 ? Math.max(2, it.val / max * 100) : 0;
        /* highlighted row = AMS blue, the field = a light slate so the highlight
           carries the eye; slimmer bar + tighter row than the first build. */
        var col = it.color || (it.hl ? C_IN : "#b6c3d1");
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div style={{ width: props.labelW || 130, fontSize: 10.5, fontWeight: it.hl ? 800 : 600, color: it.hl ? C_IN : "#475569", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</div>
            <div style={{ flex: 1, height: 10, background: "#f1f5f9", borderRadius: 5, overflow: "hidden" }}>
              <div style={{ width: w + "%", height: "100%", background: col, borderRadius: 5 }} />
            </div>
            <div style={{ width: props.valW || 120, fontSize: 10, fontWeight: 700, color: "#334155", whiteSpace: "nowrap" }}>{it.lbl}</div>
          </div>
        );
      })}
      {items.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No data for this selection.</div>}
    </div>
  );
}
function STNote(props) {
  /* Market Summary style box (pharma template) */
  return (
    <div style={{ padding: "14px 18px", background: "linear-gradient(135deg,#f8fafc,#eef6fa)", borderRadius: 14, border: "1px solid #d6e4ed", boxShadow: "0 1px 4px rgba(15,92,102,.04)", marginBottom: 16, marginTop: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div style={{ width: 24, height: 24, borderRadius: 6, background: "linear-gradient(135deg,#0F5C66,#2E8792)", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ fontSize: 11, filter: "brightness(10)" }}>📋</span></div>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#0F5C66", textTransform: "uppercase", letterSpacing: ".05em" }}>{props.tag || "Summary"}</span>
      </div>
      <div style={{ fontSize: 12, color: "#1e293b", lineHeight: 1.8, letterSpacing: ".01em" }}>{props.children}</div>
    </div>
  );
}
function STRow(props) { return <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "stretch" }}>{props.children}</div>; }

/* ── maker digest for the CURRENT window (+ base window) under active filters ── */
function digestMakers(rows, eff, msr) {
  var makers = {}, tot = { curIn: 0, curOut: 0, prevIn: 0, prevOut: 0, yoyIn: 0, yoyOut: 0, curFU: 0, curGU: 0, curOutU: 0 };
  (rows || []).forEach(function (r) {
    if (!eff.dimOk(r)) return;
    var cur = eff.inCur(r), prev = eff.inPrev(r), yoy = eff.inYoy(r);
    if (!cur && !prev && !yoy) return;
    var v = Number(r[msr]) || 0;
    var m = makers[r.mf];
    if (!m) m = makers[r.mf] = { name: r.mf, curIn: 0, curOut: 0, prevIn: 0, prevOut: 0, yoyIn: 0, yoyOut: 0, curFU: 0, curGU: 0 };
    if (r.fl === "Sell-In") {
      if (cur) { m.curIn += v; tot.curIn += v; m.curFU += Number(r.fu) || 0; m.curGU += Number(r.gu) || 0; tot.curFU += Number(r.fu) || 0; tot.curGU += Number(r.gu) || 0; }
      if (prev) { m.prevIn += v; tot.prevIn += v; }
      if (yoy) { m.yoyIn += v; tot.yoyIn += v; }
    } else {
      if (cur) { m.curOut += v; tot.curOut += v; tot.curOutU += Number(r.u) || 0; }
      if (prev) { m.prevOut += v; tot.prevOut += v; }
      if (yoy) { m.yoyOut += v; tot.yoyOut += v; }
    }
  });
  /* base = the growth-mode-selected window, kept for share-change / momentum / QoQ tables */
  var yoyMode = eff.gsel === "yoy";
  tot.baseIn = yoyMode ? tot.yoyIn : tot.prevIn;
  tot.baseOut = yoyMode ? tot.yoyOut : tot.prevOut;
  var list = Object.keys(makers).map(function (k) {
    var m = makers[k];
    m.baseIn = yoyMode ? m.yoyIn : m.prevIn;
    m.baseOut = yoyMode ? m.yoyOut : m.prevOut;
    return m;
  }).sort(function (a, b) { return b.curOut - a.curOut; });
  return { makers: makers, list: list, leader: list[0] || null, tot: tot };
}

/* Generic digest by any row key (a3 / li / md / gv / mf) for the current, prev and yoy windows */
function digestBy(rows, eff, msr, keyFn) {
  var out = {};
  (rows || []).forEach(function (r) {
    if (!eff.dimOk(r)) return;
    var cur = eff.inCur(r), prev = eff.inPrev(r), yoy = eff.inYoy(r);
    if (!cur && !prev && !yoy) return;
    var k = keyFn(r); if (k == null || k === "") k = "—";
    var o = out[k]; if (!o) o = out[k] = { name: k, curIn: 0, curOut: 0, prevIn: 0, prevOut: 0, yoyIn: 0, yoyOut: 0, curFU: 0, curGU: 0, prevFU: 0, prevGU: 0 };
    var v = Number(r[msr]) || 0;
    if (r.fl === "Sell-In") {
      if (cur) { o.curIn += v; o.curFU += Number(r.fu) || 0; o.curGU += Number(r.gu) || 0; }
      if (prev) { o.prevIn += v; o.prevFU += Number(r.fu) || 0; o.prevGU += Number(r.gu) || 0; }
      if (yoy) o.yoyIn += v;
    } else {
      if (cur) o.curOut += v; if (prev) o.prevOut += v; if (yoy) o.yoyOut += v;
    }
  });
  var yoyMode = eff.gsel === "yoy";
  return Object.keys(out).map(function (k) { var o = out[k]; o.baseIn = yoyMode ? o.yoyIn : o.prevIn; o.baseOut = yoyMode ? o.yoyOut : o.prevOut; return o; })
    .sort(function (a, b) { return b.curOut - a.curOut; });
}

/* ── VALUE / VOLUME / MIX DECOMPOSITION (shared by Market Trends and Price/Volume/Mix)
   Per product p (sell-out): units and value in the current and base windows.
     Volume effect = Σ (Δunits × base price)      — same prices, more/less boxes
     Price  effect = Σ (Δprice × base units)      — same boxes, different prices
     Mix / other   = ΔValue − Volume − Price      — interaction + new/lost products
   Value always uses a currency (USD when available, else LC) even when the user
   is looking at units, because a price decomposition needs money. ── */
function decomposeValue(rows, eff, valKey, keyFn) {
  var byP = {}, tot = { valCur: 0, valBase: 0, uCur: 0, uBase: 0 };
  (rows || []).forEach(function (r) {
    if (r.fl !== "Sell-Out" || !eff.dimOk(r)) return;
    var cur = eff.inCur(r), base = eff.inBase(r); if (!cur && !base) return;
    var k = keyFn ? keyFn(r) : (r.md || "—");
    var x = byP[k]; if (!x) x = byP[k] = { name: k, valCur: 0, valBase: 0, uCur: 0, uBase: 0 };
    var v = Number(r[valKey]) || 0, u = Number(r.u) || 0;
    if (cur) { x.valCur += v; x.uCur += u; tot.valCur += v; tot.uCur += u; }
    if (base) { x.valBase += v; x.uBase += u; tot.valBase += v; tot.uBase += u; }
  });
  var vol = 0, pri = 0, items = [];
  Object.keys(byP).forEach(function (k) {
    var x = byP[k];
    var pC = x.uCur > 0 ? x.valCur / x.uCur : null, pB = x.uBase > 0 ? x.valBase / x.uBase : null;
    var vEff = (pB != null ? (x.uCur - x.uBase) * pB : (pC != null ? x.uCur * pC : 0));
    var pEff = (pB != null && pC != null) ? (pC - pB) * x.uBase : 0;
    vol += vEff; pri += pEff;
    items.push({ name: k, valCur: x.valCur, valBase: x.valBase, uCur: x.uCur, uBase: x.uBase, priceCur: pC, priceBase: pB,
      dVal: x.valCur - x.valBase, volEff: vEff, priceEff: pEff });
  });
  var dVal = tot.valCur - tot.valBase;
  var priceCur = tot.uCur > 0 ? tot.valCur / tot.uCur : null, priceBase = tot.uBase > 0 ? tot.valBase / tot.uBase : null;
  return { dVal: dVal, vol: vol, price: pri, mix: dVal - vol - pri, tot: tot, priceCur: priceCur, priceBase: priceBase,
    valGrowth: gpct(tot.valCur, tot.valBase), unitGrowth: gpct(tot.uCur, tot.uBase),
    items: items.sort(function (a, b) { return Math.abs(b.dVal) - Math.abs(a.dVal); }) };
}

/* ── Period series (monthly when real months exist, else quarters) of any accumulator ── */
function periodSeries(rows, monthly, eff, accFn) {
  var per = {}, monOk = false;
  (monthly || []).forEach(function (r) {
    var mn = Number(r.mn); if (!(mn >= 1 && mn <= 12) || !eff.dimOk(r)) return;
    monOk = true;
    var k = r.yr * 100 + mn;
    var p = per[k]; if (!p) p = per[k] = { k: k, name: MONTH_S[mn - 1] + " " + String(r.yr).slice(2) };
    accFn(p, r);
  });
  if (!monOk) {
    per = {};
    (rows || []).forEach(function (r) {
      if (!eff.dimOk(r)) return;
      var k = r.yr * 10 + r.qr;
      var p = per[k]; if (!p) p = per[k] = { k: k, name: "Q" + r.qr + " " + String(r.yr).slice(2) };
      accFn(p, r);
    });
  }
  var keys = Object.keys(per).map(Number).sort(function (a, b) { return a - b; });
  return { rows: keys.map(function (k) { return per[k]; }), monthly: monOk };
}

/* ── Outlet grain ───────────────────────────────────────────────────────────
   Every door / distribution / concentration panel needs one row per outlet.
   The pharmacy aggregate carries a real pharmacy id when vw_SalesTracker
   exposes one; when it does not, we degrade to the finest outlet proxy the
   view DOES carry — governorate, split by pharmacy class where classes exist —
   and relabel every card with the proxy's own name, so nobody can mistake 12
   governorates for 12 pharmacies. Same shape as the monthly→quarterly and
   SKU→product fallbacks: the dashboard still answers its question, at the
   grain the data can actually support. */
function stOutlet(mp, rows) {
  var ph = (mp && mp._stExtra && mp._stExtra.pharma) || [];
  var real = false, anyCls = false, i;
  for (i = 0; i < ph.length; i++) { if (ph[i].ph) { real = true; break; } }
  for (i = 0; i < ph.length; i++) { if (ph[i].phc) { anyCls = true; break; } }
  if (real) {
    return { real: true, src: "pharmacy", anyCls: anyCls, hasSku: true, perPeriod: false, rows: ph,
      key: function (p) { return p.ph; },
      unit: "pharmacies", unitS: "pharmacy", doorS: "door", doors: "doors",
      panelLbl: "Panel pharmacies" };
  }
  var lbl = function (cls, src, hasSku, per, rs) {
    var u = cls ? "outlet groups" : "governorates", uS = cls ? "outlet group" : "governorate";
    return { real: false, src: src, anyCls: cls, hasSku: hasSku, perPeriod: per, rows: rs,
      key: function (p) { return cls ? ((p.gv || "—") + " · " + (p.phc || "—")) : (p.gv || ""); },
      unit: u, unitS: uS, doorS: uS, doors: u,
      panelLbl: cls ? "Panel outlet groups" : "Panel governorates" };
  };
  /* pharmacy aggregate present but with no id → governorate (× class) proxy on it */
  if (ph.length) return lbl(anyCls, "pharma-grain", true, false, ph);
  /* pharmacy aggregate missing altogether (older view, or the grain query failed
     server-side) → derive the same proxy from the MAIN rows, which every st_
     dashboard already has. These carry a period, so consumers must respect it. */
  var mCls = false, mr = rows || [];
  for (i = 0; i < mr.length; i++) { if (mr[i].phc) { mCls = true; break; } }
  return lbl(mCls, "main-rows", false, true, mr);
}

/* ── Data-gap note: pharmacy-level panels need a pharmacy identity in the view ── */
function STDataGap(props) {
  var d = props.diag || null;
  return (
    <div style={{ padding: "12px 16px", background: "linear-gradient(135deg,#fffbeb,#fef2f2)", border: "1px solid #fde68a", borderRadius: 12, marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
        <span style={{ fontSize: 15 }}>⚠️</span>
        <span style={{ fontSize: 11, fontWeight: 800, color: "#92400e", textTransform: "uppercase", letterSpacing: ".05em" }}>{props.title || "Pharmacy-level detail not available"}</span>
      </div>
      <div style={{ fontSize: 11.5, color: "#78350f", lineHeight: 1.7 }}>
        {props.children}
        {d ? <span style={{ display: "block", marginTop: 4, fontSize: 10, color: "#a16207" }}>
          {"Diagnostic — pharmacy rows: " + d.pharmaRows + " · rows carrying a pharmacy id: " + d.pharmaWithId + " · distinct pharmacies: " + d.pharmacies +
           " · governorates: " + (d.govs == null ? "?" : d.govs) + " · classes: " + (d.classes && d.classes.length ? d.classes.join(", ") : "none") +
           " · resolved columns: pharmacy=" + (d.phCol || "NOT FOUND") + ", class=" + (d.phcCol || "NOT FOUND") + ", sku=" + (d.skuCol || "NOT FOUND")}
        </span> : null}
        {d && d.pharmaErr ? <span style={{ display: "block", marginTop: 3, fontSize: 10, fontWeight: 700, color: "#991b1b" }}>
          {"Pharmacy-grain query error — " + d.pharmaErr}
        </span> : null}
        {d && d.viewCols && d.viewCols.length ? <span style={{ display: "block", marginTop: 3, fontSize: 9.5, color: "#b45309", lineHeight: 1.55 }}>
          {"Columns present in vw_SalesTracker — " + d.viewCols.join(" · ")}
        </span> : null}
      </div>
    </div>
  );
}

/* ── ALERT RULES (R1) — thresholds are the proposal defaults; each alert names
   the dashboard that explains it. Returns [{sev:"high|med|info", icon, text, dash}] */
function buildAlerts(D, C, extra) {
  var A = [];
  var st = pct1(D.tot.curOut, D.tot.curIn);
  if (D.tot.curIn > 0 && D.tot.curOut > 0) {
    if (st > 140) A.push({ sev: "high", icon: "🔺", text: "Market sell-through " + st.toFixed(0) + "% — dispensing is outrunning shipments; supply signal.", dash: "Sell-In vs Sell-Out" });
    else if (st < 60) A.push({ sev: "high", icon: "🔻", text: "Market sell-through " + st.toFixed(0) + "% — channel is loaded (over-shipping).", dash: "Sell-In vs Sell-Out" });
  }
  var foc = focPct9(D.tot.curFU, D.tot.curGU), focB = (extra && extra.focBase != null) ? extra.focBase : null;
  if (focB != null && foc - focB > 3) A.push({ sev: "med", icon: "🎁", text: "FOC intensity up " + (foc - focB).toFixed(1) + " pts to " + foc.toFixed(1) + "% vs " + C.eff.baseLbl + ".", dash: "FOC Intelligence" });
  D.list.slice(0, 10).forEach(function (m) {
    if (D.tot.baseOut <= 0) return;
    var d = pct1(m.curOut, D.tot.curOut) - pct1(m.baseOut, D.tot.baseOut);
    if (d <= -2) A.push({ sev: "med", icon: "📉", text: m.name + " lost " + Math.abs(d).toFixed(1) + " pts of share (" + pct1(m.curOut, D.tot.curOut).toFixed(1) + "%).", dash: "Competitive Arena" });
    else if (d >= 2) A.push({ sev: "info", icon: "📈", text: m.name + " gained " + d.toFixed(1) + " pts of share (" + pct1(m.curOut, D.tot.curOut).toFixed(1) + "%).", dash: "Competitive Arena" });
    var g = gpct(m.curOut, m.baseOut);
    if (g != null && Math.abs(g) >= 50 && pct1(m.curOut, D.tot.curOut) >= 2) A.push({ sev: "info", icon: g > 0 ? "⚡" : "⚠️", text: m.name + " sell-out " + (g > 0 ? "+" : "") + g.toFixed(0) + "% vs " + C.eff.baseLbl + ".", dash: "Brand & SKU Economics" });
    if (m.curIn > 0 && m.curOut > 0) { var mst = pct1(m.curOut, m.curIn); if (mst > 160 || mst < 50) A.push({ sev: "med", icon: "⚖️", text: m.name + " sell-through " + mst.toFixed(0) + "% — unusual sell-in / sell-out ratio.", dash: "Sell-In vs Sell-Out" }); }
  });
  if (extra && extra.coverDays != null && extra.coverDays < 30 && extra.coverDays >= 0) A.push({ sev: "high", icon: "📦", text: "Channel stock cover " + extra.coverDays + " days at the current dispensing rate.", dash: "Sell-In vs Sell-Out" });
  if (extra && extra.coverDays != null && extra.coverDays < 0) A.push({ sev: "med", icon: "📦", text: "Cumulative channel stock is negative — dispensing has exceeded shipments since data start.", dash: "Sell-In vs Sell-Out" });
  var rank = { high: 0, med: 1, info: 2 };
  return A.sort(function (a, b) { return rank[a.sev] - rank[b.sev]; }).slice(0, 8);
}

/* Shared standard totals row (used by most dashboards) */
function stdTotals(D, C) {
  var st = pct1(D.tot.curOut, D.tot.curIn);
  var stB = pct1(D.tot.baseOut, D.tot.baseIn);
  var foc = focPct9(D.tot.curFU, D.tot.curGU);
  return [
    { label: "Sell-Out — " + C.eff.curLbl, value: C.msrFmt(D.tot.curOut), icon: "💊", grad: G_VIOLET, pg: gpct(D.tot.curOut, D.tot.prevOut), yg: gpct(D.tot.curOut, D.tot.yoyOut) },
    { label: "Sell-In — " + C.eff.curLbl, value: C.msrFmt(D.tot.curIn), icon: "🚚", grad: G_NAVY, pg: gpct(D.tot.curIn, D.tot.prevIn), yg: gpct(D.tot.curIn, D.tot.yoyIn) },
    { label: "Sell-Through", value: D.tot.curIn > 0 ? st.toFixed(0) + "%" : "—", icon: "🔄", grad: G_TEAL, color: st > 110 ? C_POS : (st < 60 && D.tot.curIn > 0) ? C_NEG : undefined, sub: "sell-out ÷ sell-in" + (stB > 0 ? " · was " + stB.toFixed(0) + "% in " + C.eff.baseLbl : "") },
    { label: "Leader — " + C.eff.curLbl, value: D.leader ? D.leader.name : "—", icon: "🏆", grad: G_BLUE, sub: D.leader ? pct1(D.leader.curOut, D.tot.curOut).toFixed(1) + "% of sell-out" : "" },
    { label: "FOC Intensity", value: foc > 0 ? foc.toFixed(1) + "%" : "—", icon: "🎁", grad: G_AMBER, sub: "FOC ÷ gross (sell-in units)" }
  ];
}

/* ════════════ 1. EXECUTIVE SCORECARD ════════════ */
export function STScorecardDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  var view = useMemo(function () {
    var top10 = D.list.slice(0, 10).map(function (m, i) {
      return { name: (i + 1) + ". " + m.name, val: m.curOut, hl: i === 0,
        lbl: C.msrFmt(m.curOut) + " · " + pct1(m.curOut, D.tot.curOut).toFixed(1) + "%" };
    });
    var shifts = D.list.slice(0, 8).map(function (m) {
      var sq = pct1(m.curOut, D.tot.curOut), sp = pct1(m.baseOut, D.tot.baseOut);
      return { name: m.name, d: D.tot.baseOut > 0 ? sq - sp : 0 };
    }).sort(function (a, b) { return b.d - a.d; });
    /* ── R1: gap, segment split, growth drivers, alerts ── */
    var gapCur = D.tot.curIn - D.tot.curOut, gapBase = D.tot.baseIn - D.tot.baseOut;
    var segs = digestBy(rows, C.eff, C.measure, function (r) { return r.li; }).filter(function (o) { return o.name !== "—"; });
    var atc = digestBy(rows, C.eff, C.measure, function (r) { return r.a3; });
    var dTot = D.tot.curOut - D.tot.baseOut;
    var contrib = function (list) {
      return list.map(function (o) { return { name: o.name, d: o.curOut - o.baseOut, c: dTot !== 0 ? (o.curOut - o.baseOut) / Math.abs(dTot) * 100 : 0 }; })
        .filter(function (x) { return x.d !== 0; }).sort(function (a, b) { return b.d - a.d; });
    };
    var drvMk = contrib(D.list), drvAtc = contrib(atc);
    var drivers = { mkPos: drvMk.slice(0, 5), mkNeg: drvMk.slice(-5).reverse().filter(function (x) { return x.d < 0; }), atcPos: drvAtc.slice(0, 5), atcNeg: drvAtc.slice(-5).reverse().filter(function (x) { return x.d < 0; }) };
    /* stock cover (gross units, full history) for the alert engine */
    var inAll = 0, outAll = 0, outCur = 0;
    (rows || []).forEach(function (r) { if (!C.eff.dimOk(r)) return; var gu = Number(r.gu) || 0; if (r.fl === "Sell-In") inAll += gu; else { outAll += gu; if (C.eff.inCur(r)) outCur += gu; } });
    var days = Object.keys(C.eff.curSet).length * 91, perDay = days > 0 ? outCur / days : 0;
    var cover = perDay > 0 ? Math.round((inAll - outAll) / perDay) : null;
    var focBase = null; var pfu = 0, pgu = 0;
    (rows || []).forEach(function (r) { if (r.fl === "Sell-In" && C.eff.dimOk(r) && C.eff.inBase(r)) { pfu += Number(r.fu) || 0; pgu += Number(r.gu) || 0; } });
    if (pgu - pfu > 0) focBase = pfu / (pgu - pfu) * 100;
    var alerts = buildAlerts(D, C, { coverDays: cover, focBase: focBase });
    return { top10: top10, shifts: shifts, gapCur: gapCur, gapBase: gapBase, segs: segs, drivers: drivers, alerts: alerts, dTot: dTot };
  }, [rows, D, C.eff, C.measure, C.msrFmt]);
  var maxAbs = 0.1;
  view.shifts.forEach(function (s) { if (Math.abs(s.d) > maxAbs) maxAbs = Math.abs(s.d); });
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="🎯" title="Executive Overview" subtitle={"Sell-in / sell-out diagnostic · " + C.eff.curLbl + " vs " + C.eff.baseLbl} />
      <STPanelNote mp={mp} />
      <STTotals items={stdTotals(D, C)} />
      <STTotals items={[
        { label: "Sell-In − Sell-Out Gap — " + C.eff.curLbl, value: (view.gapCur >= 0 ? "+" : "−") + C.msrFmt(Math.abs(view.gapCur)), icon: "↔️", grad: G_AMBER, color: view.gapCur < 0 ? C_NEG : undefined, sub: "positive = channel building stock · was " + (view.gapBase >= 0 ? "+" : "−") + C.msrFmt(Math.abs(view.gapBase)) + " in " + C.eff.baseLbl },
        { label: "Sell-In / Sell-Out Ratio", value: D.tot.curOut > 0 ? (D.tot.curIn / D.tot.curOut).toFixed(2) + "×" : "—", icon: "⚖️", grad: G_TEAL, sub: "1.0× = balanced · >1.5× overstocked · <0.6× under-supplied" }
      ].concat(view.segs.slice(0, 2).map(function (sg) {
        return { label: sg.name + " share of sell-out", value: pct1(sg.curOut, D.tot.curOut).toFixed(1) + "%", icon: sg.name === "Local" ? "🏠" : "🌍", grad: sg.name === "Local" ? G_NAVY : G_BLUE, pg: gpct(sg.curOut, sg.prevOut), yg: gpct(sg.curOut, sg.yoyOut) };
      })).concat([
        { label: "Alerts", value: view.alerts.length, icon: "🚨", grad: view.alerts.some(function (a) { return a.sev === "high"; }) ? "linear-gradient(90deg,#991b1b,#dc2626)" : G_TEAL, sub: view.alerts.filter(function (a) { return a.sev === "high"; }).length + " high · " + view.alerts.filter(function (a) { return a.sev === "med"; }).length + " medium" }
      ])} />
      <STRow>
        <STPanel flex="1 1 460px" title={"Key market drivers — " + C.eff.baseLbl + " → " + C.eff.curLbl} sub={"contribution to the market's sell-out change (" + (view.dTot >= 0 ? "+" : "−") + C.msrFmt(Math.abs(view.dTot)) + ")"} grad={G_TEAL}>
          {[["Manufacturers", view.drivers.mkPos, view.drivers.mkNeg], ["ATC3 classes", view.drivers.atcPos, view.drivers.atcNeg]].map(function (grp, gi) {
            var mx = 1; grp[1].concat(grp[2]).forEach(function (x) { if (Math.abs(x.d) > mx) mx = Math.abs(x.d); });
            var row = function (x, i, pos) {
              return (
                <div key={(pos ? "p" : "n") + i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <div style={{ width: 130, fontSize: 10, fontWeight: 600, color: "#475569", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.name}</div>
                  <div style={{ flex: 1, height: 8, background: "#f1f5f9", borderRadius: 4 }}><div style={{ width: Math.max(2, Math.abs(x.d) / mx * 100) + "%", height: "100%", background: pos ? "#6BBCC4" : "#dc2626", borderRadius: 3 }} /></div>
                  <div style={{ width: 120, fontSize: 9.5, fontWeight: 700, color: pos ? C_POS : C_NEG, whiteSpace: "nowrap" }}>{(x.d >= 0 ? "+" : "−") + C.msrFmt(Math.abs(x.d)) + " · " + (x.c >= 0 ? "+" : "") + x.c.toFixed(0) + "%"}</div>
                </div>
              );
            };
            return (
              <div key={gi} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 9.5, fontWeight: 800, color: "#0F5C66", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>{grp[0]}</div>
                {grp[1].map(function (x, i) { return row(x, i, true); })}
                {grp[2].map(function (x, i) { return row(x, i, false); })}
                {grp[1].length + grp[2].length === 0 && <div style={{ fontSize: 10, color: "#94a3b8" }}>No change vs {C.eff.baseLbl}.</div>}
              </div>
            );
          })}
        </STPanel>
        <STPanel flex="1 1 380px" title="Key alerts" sub="rule-based · sell-through 60–140% · share moves ≥2 pts · FOC +3 pts · cover <30 days · growth ±50%" grad={view.alerts.some(function (a) { return a.sev === "high"; }) ? "linear-gradient(90deg,#991b1b,#dc2626)" : G_TEAL}>
          {view.alerts.map(function (a, i) {
            var col = a.sev === "high" ? { bg: "#fef2f2", bd: "#fecaca", tx: "#991b1b" } : a.sev === "med" ? { bg: "#fffbeb", bd: "#fde68a", tx: "#92400e" } : { bg: "#f0f9ff", bd: "#bae6fd", tx: "#075985" };
            return (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "7px 10px", marginBottom: 6, background: col.bg, border: "1px solid " + col.bd, borderRadius: 8 }}>
                <span style={{ fontSize: 13 }}>{a.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10.5, color: col.tx, fontWeight: 600, lineHeight: 1.45 }}>{a.text}</div>
                  <div style={{ fontSize: 8.5, color: "#94a3b8", marginTop: 2 }}>{"see " + a.dash}</div>
                </div>
                <span style={{ fontSize: 8, fontWeight: 800, color: col.tx, textTransform: "uppercase" }}>{a.sev}</span>
              </div>
            );
          })}
          {view.alerts.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No alerts for this selection — all rules within thresholds.</div>}
        </STPanel>
      </STRow>
      <STRow>
        <STPanel flex="2 1 480px" title={"Value share — the field around the leader (" + C.eff.curLbl + ")"} sub={"share of sell-out " + C.msrLbl.toLowerCase() + " · leader in blue"}>
          <STBars items={view.top10} labelW={150} />
        </STPanel>
        <STPanel flex="1 1 340px" title={"Share change by maker — " + C.eff.baseLbl + " → " + C.eff.curLbl + " (pts)"} sub="who is gaining, who is ceding">
          {view.shifts.map(function (s, i) {
            var w = Math.abs(s.d) / maxAbs * 46;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                <div style={{ width: 110, fontSize: 10.5, fontWeight: 600, color: "#475569", textAlign: "right", paddingRight: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
                <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <div style={{ width: "50%", display: "flex", justifyContent: "flex-end" }}>
                    {s.d < 0 && <div style={{ width: w + "%", minWidth: 2, height: 9, background: "#dc2626", borderRadius: "3px 0 0 3px" }} />}
                  </div>
                  <div style={{ width: 1, height: 18, background: "#94a3b8" }} />
                  <div style={{ width: "50%" }}>
                    {s.d >= 0 && <div style={{ width: w + "%", minWidth: 2, height: 9, background: "#6BBCC4", borderRadius: "0 3px 3px 0" }} />}
                  </div>
                </div>
                <div style={{ width: 58, fontSize: 10, fontWeight: 800, color: s.d >= 0 ? C_POS : C_NEG, textAlign: "right" }}>{(s.d >= 0 ? "+" : "") + s.d.toFixed(1)}</div>
              </div>
            );
          })}
          {view.shifts.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No data for this selection.</div>}
        </STPanel>
      </STRow>
      <STNote>
        {D.leader
          ? D.leader.name + " leads " + C.eff.curLbl + " on " + pct1(D.leader.curOut, D.tot.curOut).toFixed(1) + "% of dispensed " + C.msrLbl.toLowerCase() + ". Sell-through of " + pct1(D.tot.curOut, D.tot.curIn).toFixed(0) + "% " + (pct1(D.tot.curOut, D.tot.curIn) > 110 ? "means demand is outrunning shipments — a supply signal, not a demand problem." : pct1(D.tot.curOut, D.tot.curIn) < 60 ? "signals over-shipping — the channel is loaded." : "shows supply roughly matching demand.")
          : "No data for this selection."}
      </STNote>
    </div>
  );
}

/* ════════════ 2. SELL-IN vs SELL-OUT TRAJECTORY ════════════ */
function TrajectorySection({ rows, mp, C, D }) {
  var [sel, setSel] = useState("");
  var allTime = useMemo(function () { /* maker order over the whole window for the picker */
    var t = {};
    (rows || []).forEach(function (r) { if (r.fl === "Sell-Out" && C.eff.dimOk(r)) t[r.mf] = (t[r.mf] || 0) + (Number(C.hasUSD ? r.d : r.l) || 0); });
    return Object.keys(t).sort(function (a, b) { return t[b] - t[a]; }).map(function (k) { return { name: k }; });
  }, [rows, C.eff]);
  var mkName = sel || (allTime.length ? allTime[0].name : "");
  var view = useMemo(function () {
    /* monthly grain — only rows with a REAL month (1-12); quarterly-loaded studies have none */
    var mon = {};
    ((mp && mp._stExtra && mp._stExtra.monthly) || []).forEach(function (r) {
      if (!C.eff.dimOk(r)) return;
      var mnum = Number(r.mn);
      if (!(mnum >= 1 && mnum <= 12)) return;
      var k = r.yr * 100 + mnum;
      if (!mon[k]) mon[k] = { k: k, yr: r.yr, mn: mnum, inV: 0, outV: 0, mkOut: 0 };
      var v = Number(r[C.measure]) || 0;
      if (r.fl === "Sell-In") mon[k].inV += v;
      else { mon[k].outV += v; if (r.mf === mkName) mon[k].mkOut += v; }
    });
    var keys = Object.keys(mon).map(Number).sort(function (a, b) { return a - b; }).slice(-12);
    var data = keys.map(function (k) {
      var m = mon[k];
      return { name: MONTH_S[m.mn - 1] + " " + String(m.yr).slice(2), sellIn: m.inV, sellOut: m.outV,
        share: m.outV > 0 ? +(pct1(m.mkOut, m.outV)).toFixed(1) : 0,
        st: m.inV > 0 ? +(pct1(m.outV, m.inV)).toFixed(0) : null };
    });
    /* FALLBACK: no monthly data in this study → quarterly trend from the main rows */
    var isQtr = false;
    if (data.length === 0) {
      isQtr = true;
      var qagg = {};
      (rows || []).forEach(function (r) {
        if (!C.eff.dimOk(r)) return;
        var qk = r.yr * 10 + r.qr;
        if (!qagg[qk]) qagg[qk] = { yr: r.yr, qr: r.qr, inV: 0, outV: 0, mkOut: 0 };
        var qv = Number(r[C.measure]) || 0;
        if (r.fl === "Sell-In") qagg[qk].inV += qv;
        else { qagg[qk].outV += qv; if (r.mf === mkName) qagg[qk].mkOut += qv; }
      });
      data = Object.keys(qagg).map(Number).sort(function (a, b) { return a - b; }).slice(-8).map(function (qk) {
        var m = qagg[qk];
        return { name: "Q" + m.qr + " " + String(m.yr).slice(2), sellIn: m.inV, sellOut: m.outV,
          share: m.outV > 0 ? +(pct1(m.mkOut, m.outV)).toFixed(1) : 0,
          st: m.inV > 0 ? +(pct1(m.outV, m.inV)).toFixed(0) : null };
      });
    }
    var mk = D.makers[mkName];
    var t = null;
    if (mk) {
      var s1 = pct1(mk.baseOut, D.tot.baseOut), s2 = pct1(mk.curOut, D.tot.curOut);
      t = [
        { m: "Value share", a: D.tot.baseOut > 0 ? s1.toFixed(1) + "%" : "—", b: s2.toFixed(1) + "%", d: D.tot.baseOut > 0 ? (s2 - s1 >= 0 ? "+" : "") + (s2 - s1).toFixed(1) + " pts" : "—", good: s2 >= s1 },
        { m: "Sell-out (" + C.msrLbl + ")", a: C.msrFmt(mk.baseOut), b: C.msrFmt(mk.curOut), d: mk.baseOut > 0 ? ((mk.curOut - mk.baseOut) / mk.baseOut * 100).toFixed(0) + "%" : "—", good: mk.curOut >= mk.baseOut },
        { m: "Sell-in (" + C.msrLbl + ")", a: C.msrFmt(mk.baseIn), b: C.msrFmt(mk.curIn), d: mk.baseIn > 0 ? ((mk.curIn - mk.baseIn) / mk.baseIn * 100).toFixed(0) + "%" : "—", good: mk.curIn >= mk.baseIn },
        { m: "Sell-through %", a: mk.baseIn > 0 ? pct1(mk.baseOut, mk.baseIn).toFixed(0) + "%" : "—", b: mk.curIn > 0 ? pct1(mk.curOut, mk.curIn).toFixed(0) + "%" : "—", d: (mk.baseIn > 0 && mk.curIn > 0) ? ((pct1(mk.curOut, mk.curIn) - pct1(mk.baseOut, mk.baseIn)) >= 0 ? "+" : "") + (pct1(mk.curOut, mk.curIn) - pct1(mk.baseOut, mk.baseIn)).toFixed(0) + " pts" : "—", good: true }
      ];
    }
    return { data: data, table: t, isQtr: isQtr };
  }, [rows, mp, C.eff, C.measure, mkName, D, C.msrFmt, C.msrLbl]);
  var grainLbl = view.isQtr ? "Quarterly" : "Monthly";
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <STMakerPicker label="Maker on the share line" accent="#0F5C66" value={mkName} onChange={setSel}
          items={allTime.map(function (m, i) { return { name: m.name, rank: i + 1 }; })} />
      </div>
      <STPanel title={grainLbl + " sell-in vs sell-out — with " + mkName + " share of demand"} sub={"bars = " + C.msrLbl.toLowerCase() + " by flow · line = selected maker's share of sell-out · filters apply"}>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={view.data} margin={{ top: 12, right: 16, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} height={34} label={axX("Period")} />
            <YAxis yAxisId="v" tickFormatter={fmtM} tick={{ fontSize: 10 }} width={68} label={axY(C.msrLbl)} />
            <YAxis yAxisId="s" orientation="right" unit="%" tick={{ fontSize: 10 }} width={58} label={axYR("Share of sell-out (%)")} />
            <Tooltip formatter={function (v, n) { return n === "share" ? v + "%" : C.msrFmt(v); }} />
            <Legend verticalAlign="top" align="right" height={22} iconSize={9} wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="v" dataKey="sellIn" name="Sell-In" fill={C_IN} radius={[3, 3, 0, 0]} maxBarSize={20} />
            <Bar yAxisId="v" dataKey="sellOut" name="Sell-Out" fill={C_OUT} radius={[3, 3, 0, 0]} maxBarSize={20} />
            <Line yAxisId="s" dataKey="share" name="share" stroke="#4EAEB8" strokeWidth={2.6} dot={{ r: 3.2, fill: "#fff", stroke: "#4EAEB8", strokeWidth: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </STPanel>
      <STRow>
        <STPanel flex="1 1 380px" title={mkName + " — " + C.eff.baseLbl + " vs " + C.eff.curLbl} sub="the period-on-period shift">
          {view.table ? (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
              <thead><tr>
                {["Metric", C.eff.baseLbl, C.eff.curLbl, "Change"].map(function (h, i) {
                  return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "6px 8px", color: "#64748b", fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".05em", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>;
                })}
              </tr></thead>
              <tbody>
                {view.table.map(function (r, i) {
                  return (
                    <tr key={i}>
                      <td style={{ padding: "8px 8px", fontWeight: 700, color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{r.m}</td>
                      <td style={{ padding: "8px 8px", textAlign: "right", color: "#64748b", borderBottom: "1px solid #f1f5f9" }}>{r.a}</td>
                      <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 800, color: "#0f172a", borderBottom: "1px solid #f1f5f9" }}>{r.b}</td>
                      <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 800, color: r.good ? C_POS : C_NEG, borderBottom: "1px solid #f1f5f9" }}>{r.d}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : <div style={{ fontSize: 11, color: "#94a3b8" }}>No data for this maker in the selected window.</div>}
        </STPanel>
        <STPanel flex="1 1 380px" title={grainLbl + " sell-through %"} sub="sell-out ÷ sell-in · above 100% = channel drawing down stock">
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={view.data} margin={{ top: 10, right: 20, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} height={34} label={axX("Period")} />
              <YAxis unit="%" tick={{ fontSize: 10 }} width={64} label={axY("Sell-through (%)")} />
              <Tooltip formatter={function (v) { return v + "%"; }} />
              <ReferenceLine y={100} stroke={GRAY} strokeDasharray="4 4" label={{ value: "100% — demand = supply", fontSize: 9, fill: "#64748b", position: "insideTopRight" }} />
              <Line dataKey="st" name="sell-through" stroke={C_ACC} strokeWidth={2.4} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </STPanel>
      </STRow>
      <STNote>Sell-in is lumpy by nature (shipment timing); dispensing is the steadier demand read. Periods above the 100% line mean pharmacies sold more than they received — the channel funded demand from its own stock.</STNote>
    </div>
  );
}

export function STTrajectoryDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="📈" title="Sell-In vs Sell-Out" subtitle="flows, maker share & sell-through trend" />
      <STPanelNote mp={mp} />
      <STTotals items={stdTotals(D, C)} />
      <TrajectorySection rows={rows} mp={mp} C={C} D={D} />
    </div>
  );
}

/* ════════════ COMPETITIVE INTELLIGENCE (rebuilt) ════════════
   Reads competition FROM THE POINT OF VIEW OF ONE MANUFACTURER inside a chosen
   therapeutic battleground:
     · Focus maker  — default = #1 by sell-out in the current window; any maker selectable.
     · Compete within — the whole current selection, or one ATC3 / ATC4 / Molecule
       value. The category lists only offer what the account can see (server-side
       ATC scope), so a client granted several ATC3s picks among exactly those.
   Everything below is computed inside that battleground: rank & share, rank
   evolution, share trend, growth vs market, share transfers (who gains from
   whom), head-to-head economics, distribution, FOC effect, price positioning,
   molecule and regional battlegrounds. */
export function STArenaDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var [focusSel, setFocusSel] = useState("");
  var [grain, setGrain] = useState("scope");   /* scope | a3 | a4 | mol */
  var [gVal, setGVal] = useState("");          /* chosen value when grain !== scope */

  /* values available for the chosen grain, inside the current filters/segment */
  var gOpts = useMemo(function () {
    if (grain === "scope") return [];
    var o = {};
    (rows || []).forEach(function (r) { if (!C.eff.dimOk(r)) return; var v = r[grain === "mol" ? "mol" : grain]; if (v) o[v] = (o[v] || 0) + (r.fl === "Sell-Out" && C.eff.inCur(r) ? (Number(r[C.measure]) || 0) : 0); });
    return Object.keys(o).sort(function (a, b) { return o[b] - o[a]; });
  }, [rows, C.eff, C.measure, grain]);
  useEffect(function () { if (grain === "scope") { if (gVal) setGVal(""); } else if (gOpts.length && gOpts.indexOf(gVal) < 0) setGVal(gOpts[0]); }, [grain, gOpts.join("|")]);

  var inScope = function (r) {
    if (!C.eff.dimOk(r)) return false;
    if (grain === "scope" || !gVal) return true;
    return String(r[grain === "mol" ? "mol" : grain] || "") === gVal;
  };
  var GRAIN_LBL = { a3: "ATC3", a4: "ATC4", mol: "Molecule" };
  var OG = useMemo(function () { return stOutlet(mp, rows); }, [mp, rows]);   /* real pharmacies when the view carries an id, else the governorate proxy */
  var scopeLbl = (grain === "scope" || !gVal) ? "current selection" : (GRAIN_LBL[grain] || grain) + " " + gVal;

  /* ── battleground digest ── */
  var A = useMemo(function () {
    var mk = {}, tot = { outCur: 0, outBase: 0, inCur: 0, u: 0, val: 0, fu: 0, gu: 0 };
    var valKey = C.measure === "d" ? "d" : "l";
    (rows || []).forEach(function (r) {
      if (!inScope(r)) return;
      var cur = C.eff.inCur(r), base = C.eff.inBase(r); if (!cur && !base) return;
      var v = Number(r[C.measure]) || 0, name = r.mf || "—";
      var m = mk[name]; if (!m) m = mk[name] = { name: name, outCur: 0, outBase: 0, inCur: 0, u: 0, val: 0, fu: 0, gu: 0 };
      if (r.fl === "Sell-Out") {
        if (cur) { m.outCur += v; tot.outCur += v; m.u += Number(r.u) || 0; tot.u += Number(r.u) || 0; m.val += Number(r[valKey]) || 0; tot.val += Number(r[valKey]) || 0; }
        if (base) { m.outBase += v; tot.outBase += v; }
      } else if (cur) {
        m.inCur += v; tot.inCur += v;
        m.fu += Number(r.fu) || 0; m.gu += Number(r.gu) || 0; tot.fu += Number(r.fu) || 0; tot.gu += Number(r.gu) || 0;
      }
    });
    /* doors from the pharmacy grain (sell-out), same battleground */
    var doors = {}, panel = {};
    OG.rows.forEach(function (p) {
      if (p.fl !== "Sell-Out" || !inScope(p)) return;
      if (OG.perPeriod && !C.eff.inCur(p)) return;   /* main-rows fallback carries periods */
      var ok = OG.key(p); if (!ok) return;
      panel[ok] = 1;
      if (p.mf) (doors[p.mf] = doors[p.mf] || {})[ok] = 1;
    });
    var panelN = Object.keys(panel).length;
    var mktPrice = tot.u > 0 ? tot.val / tot.u : 0;
    var mktFoc = focPct9(tot.fu, tot.gu);
    var list = Object.keys(mk).map(function (k) {
      var m = mk[k];
      var dn = doors[k] ? Object.keys(doors[k]).length : 0;
      m.share = pct1(m.outCur, tot.outCur);
      m.shareBase = pct1(m.outBase, tot.outBase);
      m.dShare = tot.outBase > 0 ? m.share - m.shareBase : 0;
      m.growth = gpct(m.outCur, m.outBase);
      m.st = m.inCur > 0 ? pct1(m.outCur, m.inCur) : null;
      m.foc = focPct9(m.fu, m.gu);
      m.focIdx = mktFoc > 0 ? m.foc / mktFoc * 100 : null;
      m.price = m.u > 0 ? m.val / m.u : null;
      m.priceIdx = (m.price && mktPrice > 0) ? m.price / mktPrice * 100 : null;
      m.doors = dn;
      m.dist = panelN > 0 ? pct1(dn, panelN) : null;
      m.perDoor = dn > 0 ? m.outCur / dn : null;
      return m;
    }).sort(function (a, b) { return b.outCur - a.outCur; });
    list.forEach(function (m, i) { m.rank = i + 1; });
    var baseOrder = list.slice().sort(function (a, b) { return b.outBase - a.outBase; });
    baseOrder.forEach(function (m, i) { m.rankBase = tot.outBase > 0 ? i + 1 : null; });
    var mktGrowth = gpct(tot.outCur, tot.outBase);
    var mktSt = tot.inCur > 0 ? pct1(tot.outCur, tot.inCur) : null;
    return { list: list, tot: tot, panelN: panelN, mktPrice: mktPrice, mktFoc: mktFoc, mktGrowth: mktGrowth, mktSt: mktSt };
  }, [rows, mp, C.eff, C.measure, grain, gVal]);

  var focus = (focusSel && A.list.some(function (m) { return m.name === focusSel; })) ? focusSel : (A.list[0] ? A.list[0].name : "");
  var F = A.list.filter(function (m) { return m.name === focus; })[0] || null;
  var rivals = A.list.filter(function (m) { return m.name !== focus; });

  /* ── period series: rank + share evolution (monthly when real months exist, else quarters) ── */
  var series = useMemo(function () {
    var per = {}, monOk = false;
    ((mp && mp._stExtra && mp._stExtra.monthly) || []).forEach(function (r) {
      var mn = Number(r.mn); if (!(mn >= 1 && mn <= 12) || r.fl !== "Sell-Out" || !inScope(r)) return;
      monOk = true;
      var k = r.yr * 100 + mn;
      var p = per[k]; if (!p) p = per[k] = { k: k, label: MONTH_S[mn - 1] + " " + String(r.yr).slice(2), mk: {}, tot: 0 };
      var v = Number(r[C.measure]) || 0; p.mk[r.mf] = (p.mk[r.mf] || 0) + v; p.tot += v;
    });
    if (!monOk) {
      per = {};
      (rows || []).forEach(function (r) {
        if (r.fl !== "Sell-Out" || !inScope(r)) return;
        var k = r.yr * 10 + r.qr;
        var p = per[k]; if (!p) p = per[k] = { k: k, label: "Q" + r.qr + " " + String(r.yr).slice(2), mk: {}, tot: 0 };
        var v = Number(r[C.measure]) || 0; p.mk[r.mf] = (p.mk[r.mf] || 0) + v; p.tot += v;
      });
    }
    var keys = Object.keys(per).map(Number).sort(function (a, b) { return a - b; }).slice(-8);
    var top = [focus].concat(rivals.slice(0, 4).map(function (m) { return m.name; })).filter(Boolean);
    var shareRows = [], rankRows = [];
    keys.forEach(function (k) {
      var p = per[k];
      var sRow = { name: p.label }, rRow = { name: p.label };
      var ord = Object.keys(p.mk).sort(function (a, b) { return p.mk[b] - p.mk[a]; });
      top.forEach(function (n) {
        sRow[n] = p.tot > 0 ? +(pct1(p.mk[n] || 0, p.tot)).toFixed(1) : 0;
        var ri = ord.indexOf(n);
        rRow[n] = ri >= 0 ? ri + 1 : null;
      });
      shareRows.push(sRow); rankRows.push(rRow);
    });
    /* Rank axis: scale to the ranks actually PLOTTED (focus + 4 rivals), not to
       the size of the market. A 99-maker class used to give a [1,99] axis on
       which five leaders sit as one flat line at the top; now the axis is the
       occupied band with one position of headroom, so a #2→#4 slip is visible.
       Integer ticks only — there is no rank 2.5. */
    var lo = null, hi = null;
    rankRows.forEach(function (r) {
      top.forEach(function (n) {
        var v = r[n]; if (v == null) return;
        if (lo == null || v < lo) lo = v;
        if (hi == null || v > hi) hi = v;
      });
    });
    if (lo == null) { lo = 1; hi = 3; }
    var rLo = Math.max(1, lo - 1), rHi = Math.max(rLo + 2, hi + 1);
    var rTicks = [], step = Math.max(1, Math.ceil((rHi - rLo) / 8));
    for (var t = rLo; t <= rHi; t += step) rTicks.push(t);
    if (rTicks[rTicks.length - 1] !== rHi) rTicks.push(rHi);
    return { share: shareRows, rank: rankRows, players: top, rankLo: rLo, rankHi: rHi, rankTicks: rTicks };
  }, [rows, mp, C.eff, C.measure, grain, gVal, focus, rivals.length]);

  /* ── share transfers: who gains from whom (proportional matching of losses to gains) ── */
  var flows = useMemo(function () {
    var gain = A.list.filter(function (m) { return m.dShare > 0.05; }), loss = A.list.filter(function (m) { return m.dShare < -0.05; });
    var gSum = gain.reduce(function (a, m) { return a + m.dShare; }, 0), lSum = loss.reduce(function (a, m) { return a - m.dShare; }, 0);
    var moved = Math.min(gSum, lSum), out = [];
    if (moved > 0) {
      gain.forEach(function (g) {
        loss.forEach(function (l) {
          var pts = (g.dShare / gSum) * (-l.dShare / lSum) * moved;
          if (pts >= 0.05) out.push({ from: l.name, to: g.name, pts: pts });
        });
      });
    }
    return out.sort(function (a, b) { return b.pts - a.pts; });
  }, [A]);
  var focusFlows = flows.filter(function (f) { return f.from === focus || f.to === focus; }).slice(0, 6);

  /* ── molecule battleground (within scope) ── */
  var mols = useMemo(function () {
    var o = {};
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-Out" || !inScope(r) || !C.eff.inCur(r)) return;
      var k = r.mol || "—"; var v = Number(r[C.measure]) || 0;
      var x = o[k]; if (!x) x = o[k] = { name: k, tot: 0, mk: {} };
      x.tot += v; x.mk[r.mf] = (x.mk[r.mf] || 0) + v;
    });
    return Object.keys(o).map(function (k) {
      var x = o[k];
      var ord = Object.keys(x.mk).sort(function (a, b) { return x.mk[b] - x.mk[a]; });
      var lead = ord[0], rivalTop = ord.filter(function (n) { return n !== focus; })[0];
      return { name: k, tot: x.tot, fShare: pct1(x.mk[focus] || 0, x.tot), lead: lead, leadShare: pct1(x.mk[lead] || 0, x.tot),
        rival: rivalTop, rivalShare: rivalTop ? pct1(x.mk[rivalTop], x.tot) : 0, win: lead === focus };
    }).sort(function (a, b) { return b.tot - a.tot; }).slice(0, 10);
  }, [rows, C.eff, C.measure, grain, gVal, focus]);

  /* ── regional battleground ── */
  var regs = useMemo(function () {
    var o = {};
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-Out" || !inScope(r) || !C.eff.inCur(r)) return;
      var k = r.gv || "—"; var v = Number(r[C.measure]) || 0;
      var x = o[k]; if (!x) x = o[k] = { name: k, tot: 0, mk: {} };
      x.tot += v; x.mk[r.mf] = (x.mk[r.mf] || 0) + v;
    });
    return Object.keys(o).map(function (k) {
      var x = o[k];
      var ord = Object.keys(x.mk).sort(function (a, b) { return x.mk[b] - x.mk[a]; });
      var rivalTop = ord.filter(function (n) { return n !== focus; })[0];
      return { name: k, tot: x.tot, fShare: pct1(x.mk[focus] || 0, x.tot), rival: rivalTop, rivalShare: rivalTop ? pct1(x.mk[rivalTop], x.tot) : 0 };
    }).sort(function (a, b) { return b.tot - a.tot; }).slice(0, 12);
  }, [rows, C.eff, C.measure, grain, gVal, focus]);

  var COLS = PH_SERIES;
  var pillBtn = function (on) {
    return { padding: "4px 11px", borderRadius: 14, fontSize: 10, fontWeight: on ? 800 : 600, cursor: "pointer", userSelect: "none",
      background: on ? "#0F5C66" : "#fff", color: on ? "#fff" : "#475569", border: "1.5px solid " + (on ? "#0F5C66" : "#cbd5e1") };
  };
  var hhi = A.list.reduce(function (a, m) { return a + m.share * m.share; }, 0);

  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="⚔️" title="Competitive Intelligence" subtitle={"Battleground: " + scopeLbl + " · " + C.eff.curLbl + " vs " + C.eff.baseLbl} />
      <STPanelNote mp={mp} />

      {/* ── battleground selector ── */}
      <div style={Object.assign({}, CARD_ST, { padding: "10px 14px", marginBottom: 14, overflow: "visible", zIndex: 5 })}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: G_NAVY, borderRadius: "18px 18px 0 0" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: "#0F5C66", textTransform: "uppercase", letterSpacing: ".05em" }}>Compete within</span>
          {[["scope", "Current selection"], ["a3", "ATC3"], ["a4", "ATC4"], ["mol", "Molecule"]].map(function (o) {
            return <div key={o[0]} onClick={function () { setGrain(o[0]); }} style={pillBtn(grain === o[0])}>{o[1]}</div>;
          })}
          {grain !== "scope" && (
            <select value={gVal} onChange={function (e) { setGVal(e.target.value); }}
              style={{ padding: "4px 10px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 10.5, fontWeight: 700, color: "#17707C", background: "#fff", fontFamily: "inherit", outline: "none", maxWidth: 220 }}>
              {gOpts.map(function (v) { return <option key={v} value={v}>{v}</option>; })}
              {gOpts.length === 0 && <option value="">no values</option>}
            </select>
          )}
          <div style={{ width: 1, height: 22, background: "#e2e8f0" }} />
          <STMakerPicker label="Focus manufacturer" value={focus} onChange={setFocusSel}
            items={A.list.map(function (m) { return { name: m.name, rank: m.rank, sub: m.share.toFixed(1) + "%" + (m.dShare >= 0 ? " +" : " ") + m.dShare.toFixed(1) + " pts" }; })} />
          <span style={{ marginLeft: "auto", fontSize: 9.5, color: "#64748b" }}>
            {A.list.length + " competitors · " + A.panelN + " " + OG.unit + " · HHI " + fmtN(Math.round(hhi))}
          </span>
        </div>
        <div style={{ fontSize: 8.5, color: "#94a3b8", marginTop: 5 }}>
          Category lists offer only the ATC classes and molecules your account can access; competition, shares and ranks are computed inside the selected battleground.
        </div>
      </div>

      <STTotals items={F ? [
        { label: "Focus — " + scopeLbl, value: F.name, icon: "🎯", grad: G_TEAL, sub: "rank #" + F.rank + " of " + A.list.length + (F.rankBase ? " · was #" + F.rankBase + " in " + C.eff.baseLbl : "") },
        { label: "Sell-Out — " + C.eff.curLbl, value: C.msrFmt(F.outCur), icon: "💊", grad: G_VIOLET, pg: gpct(F.outCur, F.outBase), yg: null, sub: C.msrLbl },
        { label: "Share of battleground", value: F.share.toFixed(1) + "%", icon: "🥇", grad: G_NAVY, color: F.dShare > 0 ? C_POS : F.dShare < 0 ? C_NEG : undefined,
          sub: (F.dShare >= 0 ? "+" : "") + F.dShare.toFixed(1) + " pts vs " + C.eff.baseLbl + " (was " + F.shareBase.toFixed(1) + "%)" },
        { label: "Growth vs market", value: (F.growth == null || A.mktGrowth == null) ? "—" : ((F.growth - A.mktGrowth >= 0 ? "+" : "") + (F.growth - A.mktGrowth).toFixed(0) + " pts"), icon: "📈", grad: G_BLUE,
          color: (F.growth != null && A.mktGrowth != null && F.growth < A.mktGrowth) ? C_NEG : C_POS,
          sub: (F.growth == null ? "—" : F.growth.toFixed(0) + "%") + " vs market " + (A.mktGrowth == null ? "—" : A.mktGrowth.toFixed(0) + "%") },
        { label: "Sell-through", value: F.st == null ? "—" : F.st.toFixed(0) + "%", icon: "🔄", grad: G_TEAL, sub: "market " + (A.mktSt == null ? "—" : A.mktSt.toFixed(0) + "%") },
        { label: "FOC index", value: F.focIdx == null ? "—" : F.focIdx.toFixed(0), icon: "🎁", grad: G_AMBER, color: F.focIdx != null && F.focIdx > 130 ? C_WARN : undefined, sub: "FOC " + F.foc.toFixed(1) + "% vs market " + A.mktFoc.toFixed(1) + "% · 100 = market" },
        { label: "Distribution", value: F.dist == null ? "—" : F.dist.toFixed(0) + "%", icon: "🏪", grad: G_NAVY, sub: F.doors + " of " + A.panelN + " " + OG.unit + " · " + (F.perDoor == null ? "—" : C.msrFmt(F.perDoor) + "/" + OG.doorS) },
        { label: "Price index", value: F.priceIdx == null ? "—" : F.priceIdx.toFixed(0), icon: "💲", grad: G_VIOLET, sub: "avg price vs battleground average · 100 = market" }
      ] : [{ label: "No competitors", value: "—", icon: "⚔️", grad: G_NAVY, sub: "no data in this battleground" }]} />

      <STRow>
        <STPanel flex="1 1 430px" title="Rank evolution" sub={"position by sell-out, last " + series.rank.length + " periods · lower is better · axis #" + series.rankLo + "–#" + series.rankHi + " of " + A.list.length + " competitors"} grad={G_NAVY}>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={series.rank} margin={{ top: 12, right: 24, left: 6, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 9.5 }} height={34} label={axX("Period")} />
              <YAxis width={62} label={axY("Rank (1 = leader)")} reversed domain={[series.rankLo, series.rankHi]} ticks={series.rankTicks} allowDecimals={false} interval={0} tick={{ fontSize: 10 }} tickFormatter={function (v) { return "#" + v; }} />
              <Tooltip formatter={function (v) { return v == null ? "—" : "#" + v; }} />
              <Legend verticalAlign="top" align="right" height={22} iconSize={9} wrapperStyle={{ fontSize: 10 }} />
              {series.players.map(function (n, i) {
                return <Line key={n} dataKey={n} stroke={n === focus ? "#0F5C66" : COLS[i % COLS.length]} strokeWidth={n === focus ? 3 : 1.8} dot={{ r: n === focus ? 4 : 2.5 }} connectNulls />;
              })}
            </LineChart>
          </ResponsiveContainer>
        </STPanel>
        <STPanel flex="1 1 430px" title="Share trend" sub={"% of battleground sell-out · " + C.msrLbl.toLowerCase()} grad={G_TEAL}>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={series.share} margin={{ top: 12, right: 18, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 9.5 }} height={34} label={axX("Period")} />
              <YAxis unit="%" tick={{ fontSize: 10 }} width={66} label={axY("Share of battleground (%)")} />
              <Tooltip formatter={function (v) { return v + "%"; }} />
              <Legend verticalAlign="top" align="right" height={22} iconSize={9} wrapperStyle={{ fontSize: 10 }} />
              {series.players.map(function (n, i) {
                return <Line key={n} dataKey={n} stroke={n === focus ? "#0F5C66" : COLS[i % COLS.length]} strokeWidth={n === focus ? 3 : 1.8} dot={{ r: n === focus ? 4 : 2.5 }} />;
              })}
            </LineChart>
          </ResponsiveContainer>
        </STPanel>
      </STRow>

      <STPanel title={"Head-to-head — " + scopeLbl + " · " + C.eff.curLbl} sub="focus row highlighted · share change, growth, sell-through, distribution, per-door, FOC and price index" grad={G_BLUE}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5, minWidth: 900 }}>
            <thead><tr>{["#", "Manufacturer", "Sell-out", "Share", "Δ share", "Growth", "Sell-through", "Doors", "Distribution", "Per door", "FOC %", "Price idx"].map(function (h, i) {
              return <th key={i} style={{ textAlign: i <= 1 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>;
            })}</tr></thead>
            <tbody>
              {A.list.slice(0, 12).map(function (m) {
                var isF = m.name === focus;
                var bg = isF ? "#E2F1F3" : undefined;
                var td = { padding: "5px 6px", textAlign: "right", borderBottom: "1px solid #f1f5f9", background: bg };
                return (
                  <tr key={m.name}>
                    <td style={{ padding: "5px 6px", borderBottom: "1px solid #f1f5f9", background: bg, fontWeight: 800, color: "#64748b" }}>{m.rank}</td>
                    <td style={{ padding: "5px 6px", borderBottom: "1px solid #f1f5f9", background: bg, fontWeight: isF ? 800 : 700, color: isF ? "#0F5C66" : "#334155", whiteSpace: "nowrap" }}>{m.name}{isF ? " ◀" : ""}</td>
                    <td style={Object.assign({}, td, { fontWeight: 700, color: "#0F5C66" })}>{C.msrFmt(m.outCur)}</td>
                    <td style={Object.assign({}, td, { fontWeight: 800 })}>{m.share.toFixed(1) + "%"}</td>
                    <td style={Object.assign({}, td, { fontWeight: 800, color: m.dShare > 0.05 ? C_POS : m.dShare < -0.05 ? C_NEG : GRAY })}>{(m.dShare >= 0 ? "+" : "") + m.dShare.toFixed(1)}</td>
                    <td style={Object.assign({}, td, { fontWeight: 700, color: m.growth == null ? GRAY : m.growth >= 0 ? C_POS : C_NEG })}>{m.growth == null ? "—" : (m.growth >= 0 ? "+" : "") + m.growth.toFixed(0) + "%"}</td>
                    <td style={Object.assign({}, td, { color: m.st == null ? GRAY : m.st >= 100 ? C_POS : C_WARN, fontWeight: 700 })}>{m.st == null ? "—" : m.st.toFixed(0) + "%"}</td>
                    <td style={td}>{m.doors}</td>
                    <td style={Object.assign({}, td, { fontWeight: 700 })}>{m.dist == null ? "—" : m.dist.toFixed(0) + "%"}</td>
                    <td style={td}>{m.perDoor == null ? "—" : C.msrFmt(m.perDoor)}</td>
                    <td style={Object.assign({}, td, { color: m.focIdx != null && m.focIdx > 130 ? C_WARN : "#334155" })}>{m.foc.toFixed(1) + "%"}</td>
                    <td style={Object.assign({}, td, { fontWeight: 700, color: m.priceIdx == null ? GRAY : m.priceIdx > 110 ? C_WARN : m.priceIdx < 90 ? C_ACC : "#334155" })}>{m.priceIdx == null ? "—" : m.priceIdx.toFixed(0)}</td>
                  </tr>
                );
              })}
              {A.list.length === 0 && <tr><td colSpan={12} style={{ padding: 12, color: "#94a3b8" }}>No competitors in this battleground.</td></tr>}
            </tbody>
          </table>
        </div>
      </STPanel>

      <STRow>
        <STPanel flex="1 1 400px" title="Who gains from whom" sub={"share points transferred " + C.eff.baseLbl + " → " + C.eff.curLbl + " · proportional matching of losses to gains"} grad={G_VIOLET}>
          {focusFlows.length > 0 && <div style={{ fontSize: 9.5, fontWeight: 800, color: "#0F5C66", marginBottom: 5, textTransform: "uppercase", letterSpacing: ".04em" }}>{focus} — direct flows</div>}
          {focusFlows.map(function (f, i) {
            var win = f.to === focus;
            return (
              <div key={"f" + i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 9px", marginBottom: 5, borderRadius: 8, background: win ? "#ecfdf5" : "#fef2f2", border: "1px solid " + (win ? "#a7f3d0" : "#fecaca") }}>
                <span style={{ fontSize: 12 }}>{win ? "⬅️" : "➡️"}</span>
                <span style={{ fontSize: 10.5, color: "#334155", flex: 1 }}>
                  <strong style={{ color: win ? C_POS : C_NEG }}>{f.to}</strong>{" took "}<strong>{f.pts.toFixed(2)} pts</strong>{" from "}<strong>{f.from}</strong>
                </span>
              </div>
            );
          })}
          <div style={{ fontSize: 9.5, fontWeight: 800, color: "#0F5C66", margin: "8px 0 5px", textTransform: "uppercase", letterSpacing: ".04em" }}>All flows in the battleground</div>
          {flows.slice(0, 8).map(function (f, i) {
            return (
              <div key={"a" + i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <div style={{ width: 110, fontSize: 10, fontWeight: 600, color: C_NEG, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.from}</div>
                <div style={{ flex: 1, height: 9, background: "#f1f5f9", borderRadius: 3 }}><div style={{ width: Math.max(3, f.pts / flows[0].pts * 100) + "%", height: "100%", background: "linear-gradient(90deg,#dc2626,#6BBCC4)", borderRadius: 3 }} /></div>
                <div style={{ width: 110, fontSize: 10, fontWeight: 700, color: C_POS, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.to}</div>
                <div style={{ width: 52, fontSize: 9.5, fontWeight: 800, color: "#334155", textAlign: "right" }}>{f.pts.toFixed(2)}</div>
              </div>
            );
          })}
          {flows.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No material share movement in this window.</div>}
        </STPanel>
        <STPanel flex="1 1 430px" title="Positioning — share vs share change" sub="x = share now · y = Δ share pts · bubble = FOC % · focus in navy" grad={G_TEAL}>
          <ResponsiveContainer width="100%" height={260}>
            <ScatterChart margin={{ top: 18, right: 26, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis type="number" dataKey="x" name="share" unit="%" domain={AX_NUM} tick={{ fontSize: 10 }} height={36} label={axX("Share of sell-out (%)")} />
              <YAxis type="number" dataKey="y" name="Δ share" unit="pts" domain={AX_NUM} tick={{ fontSize: 10 }} width={64} label={axY("Share change (pts)")} />
              <ZAxis dataKey="z" range={[90, 620]} />
              <Tooltip content={function (o) {
                if (!o.active || !o.payload || !o.payload.length) return null;
                var p = o.payload[0].payload;
                return <div style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 12px", fontSize: 11 }}><b>{p.name}</b><div>{"share " + p.x + "% · Δ " + p.y + " pts · FOC " + p.foc + "%"}</div></div>;
              }} />
              <ReferenceLine y={0} stroke="#475569" />
              <Scatter data={A.list.slice(0, 12).map(function (m) { return { name: m.name, x: +m.share.toFixed(1), y: +m.dShare.toFixed(1), z: Math.max(m.foc, 0.4), foc: +m.foc.toFixed(1), hl: m.name === focus }; })}>
                {A.list.slice(0, 12).map(function (m, i) { return <Cell key={i} fill={m.name === focus ? "#0F5C66" : (m.dShare >= 0 ? "#6BBCC4" : "#dc2626")} fillOpacity={m.name === focus ? 0.95 : 0.75} />; })}
                {bubbleLbl()}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </STPanel>
      </STRow>

      <STRow>
        <STPanel flex="1 1 420px" title={"Molecule battleground — " + focus + " vs best rival"} sub="per molecule inside the scope · navy = focus share, grey = leading rival" grad={G_NAVY}>
          {mols.map(function (m, i) {
            return (
              <div key={i} style={{ marginBottom: 9 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: m.win ? "#0F5C66" : "#33606B" }}>{m.name}</span>
                  {m.win && <span style={{ fontSize: 8, fontWeight: 800, color: "#fff", background: "#0F5C66", borderRadius: 8, padding: "1px 6px" }}>LEADING</span>}
                  <span style={{ fontSize: 9, color: "#94a3b8", marginLeft: "auto" }}>{C.msrFmt(m.tot)}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 3 }}>
                  <div style={{ width: 52, fontSize: 8.5, color: "#0F5C66", textAlign: "right", fontWeight: 700 }}>focus</div>
                  <div style={{ flex: 1, height: 8, background: "#f1f5f9", borderRadius: 4 }}><div style={{ width: Math.max(1, Math.min(100, m.fShare)) + "%", height: "100%", background: "#0F5C66", borderRadius: 3 }} /></div>
                  <div style={{ width: 42, fontSize: 9.5, fontWeight: 800, color: "#0F5C66" }}>{m.fShare.toFixed(0) + "%"}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 2 }}>
                  <div style={{ width: 52, fontSize: 8.5, color: "#64748b", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.rival || "—"}</div>
                  <div style={{ flex: 1, height: 8, background: "#f1f5f9", borderRadius: 4 }}><div style={{ width: Math.max(1, Math.min(100, m.rivalShare)) + "%", height: "100%", background: "#b6c3d1", borderRadius: 3 }} /></div>
                  <div style={{ width: 42, fontSize: 9.5, fontWeight: 700, color: "#64748b" }}>{m.rivalShare.toFixed(0) + "%"}</div>
                </div>
              </div>
            );
          })}
          {mols.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No molecule data in this battleground.</div>}
        </STPanel>
        <STPanel flex="1 1 420px" title={"Regional battleground — " + focus + " share vs best rival"} sub="per governorate · gap = the prize (or the defence)" grad={G_BLUE}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>{["Governorate", "Market", focus, "Best rival", "Rival %", "Gap"].map(function (h, i) {
              return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>;
            })}</tr></thead>
            <tbody>
              {regs.map(function (r, i) {
                var gap = r.fShare - r.rivalShare;
                return (
                  <tr key={i}>
                    <td style={{ padding: "5px 6px", fontWeight: 700, color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{r.name}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#64748b", borderBottom: "1px solid #f1f5f9" }}>{C.msrFmt(r.tot)}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: "#0F5C66", borderBottom: "1px solid #f1f5f9" }}>{r.fShare.toFixed(1) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#475569", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{r.rival || "—"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#475569", borderBottom: "1px solid #f1f5f9" }}>{r.rivalShare.toFixed(1) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: gap >= 0 ? C_POS : C_NEG, borderBottom: "1px solid #f1f5f9" }}>{(gap >= 0 ? "+" : "") + gap.toFixed(1)}</td>
                  </tr>
                );
              })}
              {regs.length === 0 && <tr><td colSpan={6} style={{ padding: 12, color: "#94a3b8" }}>No regional data in this battleground.</td></tr>}
            </tbody>
          </table>
        </STPanel>
      </STRow>

      <STRow>
        <STPanel flex="1 1 400px" title="FOC effect on share" sub="x = FOC % · y = Δ share pts · is bonus buying share?" grad={G_AMBER}>
          <ResponsiveContainer width="100%" height={240}>
            <ScatterChart margin={{ top: 18, right: 24, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis type="number" dataKey="x" name="FOC %" unit="%" domain={AX_NUM} tick={{ fontSize: 10 }} height={36} label={axX("FOC intensity (%)")} />
              <YAxis type="number" dataKey="y" name="Δ share" unit="pts" domain={AX_NUM} tick={{ fontSize: 10 }} width={64} label={axY("Share change (pts)")} />
              <ZAxis dataKey="z" range={[90, 520]} />
              <Tooltip content={function (o) {
                if (!o.active || !o.payload || !o.payload.length) return null;
                var p = o.payload[0].payload;
                return <div style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 12px", fontSize: 11 }}><b>{p.name}</b><div>{"FOC " + p.x + "% · Δ share " + p.y + " pts"}</div></div>;
              }} />
              <ReferenceLine y={0} stroke="#475569" />
              <ReferenceLine x={A.mktFoc} stroke={GRAY} strokeDasharray="4 4" label={{ value: "market " + A.mktFoc.toFixed(1) + "%", fontSize: 9, fill: "#64748b", position: "insideTopRight" }} />
              <Scatter data={A.list.slice(0, 12).map(function (m) { return { name: m.name, x: +m.foc.toFixed(1), y: +m.dShare.toFixed(1), z: Math.max(m.share, 0.5) }; })}>
                {A.list.slice(0, 12).map(function (m, i) { return <Cell key={i} fill={m.name === focus ? "#0F5C66" : (m.dShare >= 0 ? "#6BBCC4" : "#dc2626")} fillOpacity={0.85} />; })}
                {bubbleLbl()}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </STPanel>
        <STPanel flex="1 1 300px" title="Price positioning" sub="average price per unit vs battleground average (100)" grad={G_VIOLET}>
          <STBars items={A.list.slice(0, 10).filter(function (m) { return m.priceIdx != null; }).map(function (m) {
            return { name: m.name, val: m.priceIdx, hl: m.name === focus, lbl: m.priceIdx.toFixed(0) + " · " + fmtM(m.price), color: m.name === focus ? "#0F5C66" : (m.priceIdx > 110 ? "#d8973c" : "#b6c3d1") };
          })} labelW={110} valW={110} />
        </STPanel>
        <STPanel flex="1 1 300px" title="Distribution vs yield" sub="numeric distribution % and sell-out per stocking pharmacy" grad={G_NAVY}>
          <STBars items={A.list.slice(0, 10).filter(function (m) { return m.dist != null; }).map(function (m) {
            return { name: m.name, val: m.dist, hl: m.name === focus, lbl: m.dist.toFixed(0) + "% · " + (m.perDoor == null ? "—" : C.msrFmt(m.perDoor) + "/door"), color: m.name === focus ? "#0F5C66" : "#b6c3d1" };
          })} labelW={110} valW={150} />
        </STPanel>
      </STRow>

      <STNote>
        {F ? (F.name + " holds " + F.share.toFixed(1) + "% of " + scopeLbl + " (rank #" + F.rank + " of " + A.list.length + "), " + (F.dShare >= 0 ? "up " : "down ") + Math.abs(F.dShare).toFixed(1) + " pts vs " + C.eff.baseLbl + ". "
          + (F.growth != null && A.mktGrowth != null ? (F.growth >= A.mktGrowth ? "It grew faster than the battleground (" + F.growth.toFixed(0) + "% vs " + A.mktGrowth.toFixed(0) + "%). " : "It grew slower than the battleground (" + F.growth.toFixed(0) + "% vs " + A.mktGrowth.toFixed(0) + "%). ") : "")
          + (F.dist != null ? "Distribution reaches " + F.dist.toFixed(0) + "% of the panel" + (F.perDoor != null ? " at " + C.msrFmt(F.perDoor) + " per stocking pharmacy" : "") + ". " : "")
          + (F.focIdx != null && F.focIdx > 130 ? "Its FOC intensity is " + F.focIdx.toFixed(0) + " vs the market's 100 — part of the position is bought with bonus. " : "")
          + (focusFlows.length ? "Largest direct movement: " + focusFlows[0].to + " took " + focusFlows[0].pts.toFixed(2) + " pts from " + focusFlows[0].from + "." : "Share movement in the battleground was immaterial.")
        ) : "Select a battleground with data to read the competitive position."}
      </STNote>
    </div>
  );
}

export function STRegionalDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  var [mode, setMode] = useState("value");
  var view = useMemo(function () {
    var govs = {}, mfGov = {};
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-Out" || !C.eff.dimOk(r) || !C.eff.inCur(r)) return;
      var v = Number(r[C.measure]) || 0;
      var g = r.gv || "—";
      govs[g] = (govs[g] || 0) + v;
      var key = r.mf + "|" + g;
      mfGov[key] = (mfGov[key] || 0) + v;
    });
    var govList = Object.keys(govs).sort(function (a, b) { return govs[b] - govs[a]; }).slice(0, 11);
    var topMk = D.list.slice(0, 10);
    var grid = topMk.map(function (m) {
      return { name: m.name, cells: govList.map(function (g) { var v = mfGov[m.name + "|" + g] || 0; return { g: g, v: v, sh: pct1(v, govs[g]) }; }) };
    });
    var colMax = {};
    govList.forEach(function (g) {
      var mx = 0;
      grid.forEach(function (row) { row.cells.forEach(function (c) { if (c.g === g && c.v > mx) mx = c.v; }); });
      colMax[g] = mx;
    });
    var leaderName = D.leader ? D.leader.name : "";
    var sizeShare = govList.map(function (g) { return { name: g, size: govs[g], lead: pct1(mfGov[leaderName + "|" + g] || 0, govs[g]) }; });
    return { govList: govList, grid: grid, colMax: colMax, sizeShare: sizeShare, leaderName: leaderName, nGov: Object.keys(govs).length };
  }, [rows, D, C.eff, C.measure]);
  /* same two hues and the same opacity curve the pharma regional heatmap uses:
     highlight blue for the leader row, navy for the field. */
  var tint = function (t, hl) {
    if (t <= 0.02) return "#f4f6f9";
    var base = hl ? [15, 110, 86] : [26, 63, 110];
    return "rgba(" + base[0] + "," + base[1] + "," + base[2] + "," + (0.12 + Math.pow(t, 0.8) * 0.88).toFixed(2) + ")";
  };
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="🗺️" title="Regional Analysis" subtitle={"Maker × governorate · " + C.eff.curLbl} />
      <STPanelNote mp={mp} />
      <STTotals items={stdTotals(D, C).slice(0, 4).concat([{ label: "Governorates with Demand", value: view.nGov, icon: "🗺️", grad: G_TEAL, sub: C.eff.curLbl + " · after filters" }])} />
      <STPanel title={"Sell-out — maker × governorate " + (mode === "value" ? "(" + C.msrLbl + ")" : "(share of governorate)")} sub="darker = larger within the governorate column · leader row in blue">
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {["value", "share"].map(function (m) {
            var on = mode === m;
            return <div key={m} onClick={function () { setMode(m); }} style={{ padding: "3px 12px", borderRadius: 14, fontSize: 10, fontWeight: 700, cursor: "pointer", background: on ? "#0F5C66" : "#f1f5f9", color: on ? "#fff" : "#475569" }}>{m === "value" ? C.msrLbl : "% of governorate"}</div>;
          })}
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 10, minWidth: 680 }}>
            <thead><tr>
              <th style={{ padding: "4px 8px" }}></th>
              {view.govList.map(function (g) { return <th key={g} style={{ padding: "4px 6px", fontSize: 9.5, color: "#475569", fontWeight: 700, whiteSpace: "nowrap" }}>{g}</th>; })}
            </tr></thead>
            <tbody>
              {view.grid.map(function (row, ri) {
                var hl = row.name === view.leaderName;
                return (
                  <tr key={ri}>
                    <td style={{ padding: "4px 8px", fontSize: 10, fontWeight: hl ? 800 : 600, color: hl ? "#17707C" : "#475569", whiteSpace: "nowrap", textAlign: "right" }}>{row.name}</td>
                    {row.cells.map(function (c, ci) {
                      var t = mode === "value" ? (view.colMax[c.g] > 0 ? c.v / view.colMax[c.g] : 0) : Math.min(1, c.sh / 50);
                      return (
                        <td key={ci} style={{ padding: 0 }}>
                          <div style={{ minWidth: 56, height: 30, display: "flex", alignItems: "center", justifyContent: "center", margin: 1, borderRadius: 4, background: tint(t, hl), fontSize: 9, fontWeight: 700, color: t > 0.55 ? "#fff" : "#334155" }}>
                            {c.v > 0 ? (mode === "value" ? C.msrFmt(c.v) : c.sh.toFixed(0) + "%") : ""}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </STPanel>
      <STPanel title={"Governorate size & " + (view.leaderName || "leader") + " share — " + C.eff.curLbl} sub="gray bar = market sell-out · blue bar = leader share within the governorate">
        {view.sizeShare.map(function (s, i) {
          var mx = view.sizeShare.length ? view.sizeShare[0].size : 1;
          return (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 110, fontSize: 10.5, fontWeight: 700, color: "#475569", textAlign: "right" }}>{s.name}</div>
                <div style={{ flex: 1, height: 9, background: "#f1f5f9", borderRadius: 5, overflow: "hidden" }}>
                  <div style={{ width: Math.max(1.5, s.size / mx * 100) + "%", height: "100%", background: "#b6c3d1", borderRadius: 4 }} />
                </div>
                <div style={{ width: 150, fontSize: 10, fontWeight: 700, color: "#334155", whiteSpace: "nowrap" }}>{C.msrFmt(s.size) + " · " + s.lead.toFixed(0) + "%"}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
                <div style={{ width: 110 }} />
                <div style={{ flex: 1, height: 5, background: "#eef2f7", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ width: Math.min(100, s.lead) + "%", height: "100%", background: "#17707C" }} />
                </div>
                <div style={{ width: 150 }} />
              </div>
            </div>
          );
        })}
        {view.sizeShare.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No data for this selection.</div>}
      </STPanel>
      <STNote>The prize is the large governorates where the leader sits below its national line — depth there beats new geography. Strongholds (small books with very high share) are defend-supply-first territory.</STNote>
    </div>
  );
}

/* ════════════ 5. BRAND & SKU ECONOMICS ════════════ */
export function STPortfolioDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  var [sel, setSel] = useState("");
  var mkName = sel || (D.leader ? D.leader.name : "");
  var view = useMemo(function () {
    var brands = {};
    (rows || []).forEach(function (r) {
      if (r.mf !== mkName || !C.eff.dimOk(r) || !C.eff.inCur(r)) return;
      var b = brands[r.md];
      if (!b) b = brands[r.md] = { name: r.md, mol: r.mol || "", inV: 0, outV: 0 };
      var v = Number(r[C.measure]) || 0;
      if (r.fl === "Sell-In") b.inV += v; else b.outV += v;
      if (r.mol) b.mol = r.mol;
    });
    var brandList = Object.keys(brands).map(function (k) { return brands[k]; }).sort(function (a, b) { return b.outV - a.outV; }).slice(0, 8);
    var skus = {};
    ((mp && mp._stExtra && mp._stExtra.sku) || []).forEach(function (r) {
      if (r.mf !== mkName || !C.eff.dimOk(r) || !C.eff.inCur(r)) return;
      var key = r.md + " · " + r.sku;
      var s = skus[key];
      if (!s) s = skus[key] = { name: r.sku || r.md, brand: r.md, inV: 0, outV: 0, pl: 0, pln: 0 };
      var v = Number(r[C.measure]) || 0;
      if (r.fl === "Sell-In") s.inV += v; else s.outV += v;
      var _pr = r[C.priceKey] != null ? r[C.priceKey] : r.pl;
      if (_pr) { s.pl += Number(_pr) || 0; s.pln += 1; }
    });
    var skuList = Object.keys(skus).map(function (k) { return skus[k]; }).sort(function (a, b) { return b.outV - a.outV; }).slice(0, 12);
    /* FALLBACK LADDER — the SKU panel must never sit empty:
         1. SKU grain (mp._stExtra.sku) in the selected window            ← preferred
         2. SKU grain has NO rows at all (view/study without SKU detail)  → pack-level
            table built from the main rows at Product grain (avg price = value ÷ units) */
    var skuGrain = "sku";
    if (skuList.length === 0) {
      var packs = {};
      (rows || []).forEach(function (r) {
        if (r.mf !== mkName || !C.eff.dimOk(r) || !C.eff.inCur(r)) return;
        var pk = r.md || "—";
        var s2 = packs[pk];
        if (!s2) s2 = packs[pk] = { name: pk, brand: r.mol || "", inV: 0, outV: 0, pl: 0, pln: 0, _l: 0, _u: 0 };
        var v2 = Number(r[C.measure]) || 0;
        if (r.fl === "Sell-In") s2.inV += v2; else s2.outV += v2;
        if (r.fl === "Sell-Out") { s2._l += Number(C.measure === "d" ? r.d : r.l) || 0; s2._u += Number(r.u) || 0; }
      });
      skuList = Object.keys(packs).map(function (k) {
        var s3 = packs[k];
        if (s3._u > 0) { s3.pl = s3._l / s3._u; s3.pln = 1; }
        return s3;
      }).sort(function (a, b) { return b.outV - a.outV; }).slice(0, 12);
      if (skuList.length) skuGrain = "product";
    }
    var skuBubbles = skuList.map(function (s) {
      return { name: s.name, x: s.inV, y: s.inV > 0 ? Math.min(999, Math.round(pct1(s.outV, s.inV))) : 0, z: Math.max(s.outV, 1) };
    }).filter(function (b) { return b.x > 0 && b.y > 0; });
    var mk = D.makers[mkName] || { curIn: 0, curOut: 0, prevIn: 0, prevOut: 0, yoyIn: 0, yoyOut: 0, baseOut: 0, baseIn: 0 };
    return { brandList: brandList, skuList: skuList, skuBubbles: skuBubbles, mk: mk, skuGrain: skuGrain };
  }, [rows, mp, C.eff, C.measure, C.priceKey, mkName, D]);
  var skuLbl = view.skuGrain === "product" ? "Product" : "SKU";
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="📦" title="Brand & SKU Economics" subtitle={"Portfolio deep-dive · " + C.eff.curLbl} />
      <STPanelNote mp={mp} />
      <STTotals items={[
        { label: "Selected Maker", value: mkName, icon: "🏭", grad: G_BLUE, sub: "pick another below" },
        { label: "Sell-Out — " + C.eff.curLbl, value: C.msrFmt(view.mk.curOut), icon: "💊", grad: G_VIOLET, pg: gpct(view.mk.curOut, view.mk.prevOut), yg: gpct(view.mk.curOut, view.mk.yoyOut) },
        { label: "Sell-In — " + C.eff.curLbl, value: C.msrFmt(view.mk.curIn), icon: "🚚", grad: G_NAVY, pg: gpct(view.mk.curIn, view.mk.prevIn), yg: gpct(view.mk.curIn, view.mk.yoyIn) },
        { label: "Maker Sell-Through", value: view.mk.curIn > 0 ? pct1(view.mk.curOut, view.mk.curIn).toFixed(0) + "%" : "—", icon: "🔄", grad: G_TEAL, sub: "above 100% = supply-constrained" },
        { label: "Share of Market", value: pct1(view.mk.curOut, D.tot.curOut).toFixed(1) + "%", icon: "🎯", grad: G_AMBER, sub: C.eff.curLbl + " sell-out" }
      ]} />
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <STMakerPicker label="Manufacturer" accent="#0F5C66" value={mkName} onChange={setSel}
          items={D.list.map(function (m, i) { return { name: m.name, rank: i + 1, sub: pct1(m.curOut, D.tot.curOut).toFixed(1) + "%" }; })} />
      </div>
      <STPanel title={mkName + " brands — sell-in vs sell-out & turn (" + C.eff.curLbl + ")"} sub="turn above 100% = brand pulling harder than it ships (supply-constrained)">
        {view.brandList.map(function (b, i) {
          var turn = b.inV > 0 ? pct1(b.outV, b.inV) : 0;
          var mx = Math.max(view.brandList[0].outV, view.brandList[0].inV, 1);
          return (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: "#0F5C66" }}>{b.name}</span>
                <span style={{ fontSize: 9.5, color: "#94a3b8" }}>{b.mol}</span>
                <span style={{ fontSize: 10, fontWeight: 800, color: turn >= 100 ? C_POS : C_WARN }}>{b.inV > 0 ? turn.toFixed(0) + "% turn" : "no sell-in"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
                <div style={{ width: 46, fontSize: 8.5, color: "#94a3b8", textAlign: "right" }}>sell-in</div>
                <div style={{ flex: 1, height: 8, background: "#f1f5f9", borderRadius: 4 }}><div style={{ width: Math.max(1, b.inV / mx * 100) + "%", height: "100%", background: C_IN, borderRadius: 3 }} /></div>
                <div style={{ width: 60, fontSize: 9.5, color: "#64748b" }}>{C.msrFmt(b.inV)}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                <div style={{ width: 46, fontSize: 8.5, color: "#94a3b8", textAlign: "right" }}>sell-out</div>
                <div style={{ flex: 1, height: 8, background: "#f1f5f9", borderRadius: 4 }}><div style={{ width: Math.max(1, b.outV / mx * 100) + "%", height: "100%", background: C_OUT, borderRadius: 3 }} /></div>
                <div style={{ width: 60, fontSize: 9.5, fontWeight: 700, color: "#0F5C66" }}>{C.msrFmt(b.outV)}</div>
              </div>
            </div>
          );
        })}
        {view.brandList.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No data for this selection.</div>}
      </STPanel>
      <STRow>
        <STPanel flex="1 1 420px" title={skuLbl + " sell-through vs scale"} sub="x = sell-in · y = sell-through % · bubble = sell-out · above 100% = rationed by supply">
          {view.skuBubbles.length === 0 && (
            <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#94a3b8", textAlign: "center", padding: 20 }}>
              {view.skuList.length === 0 ? "No " + skuLbl.toLowerCase() + " data for " + mkName + " in " + C.eff.curLbl + "." : "No sell-in recorded for " + mkName + " in " + C.eff.curLbl + " — sell-through can't be computed (see the table for sell-out)."}
            </div>
          )}
          {view.skuBubbles.length > 0 && <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ top: 18, right: 30, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis type="number" dataKey="x" tickFormatter={fmtM} tick={{ fontSize: 10 }} name="sell-in" domain={AX_NUM} height={36} label={axX("Sell-in — " + C.msrLbl)} />
              <YAxis type="number" dataKey="y" unit="%" tick={{ fontSize: 10 }} name="turn" domain={AX_NUM} width={64} label={axY("Turn — sell-out ÷ sell-in (%)")} />
              <ZAxis dataKey="z" range={[100, 800]} />
              <Tooltip content={function (o) {
                if (!o.active || !o.payload || !o.payload.length) return null;
                var p = o.payload[0].payload;
                return <div style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 12px", fontSize: 11 }}><b>{p.name}</b><div>turn {p.y}% · sell-in {C.msrFmt(p.x)}</div></div>;
              }} />
              <ReferenceLine y={100} stroke={GRAY} strokeDasharray="4 4" label={{ value: "100% — demand = supply", fontSize: 9, fill: "#64748b", position: "insideTopRight" }} />
              <Scatter data={view.skuBubbles} fill="#2E8792" fillOpacity={0.8}>{bubbleLbl()}</Scatter>
            </ScatterChart>
          </ResponsiveContainer>}
        </STPanel>
        <STPanel flex="1 1 420px" title={skuLbl + " economics — " + C.eff.curLbl} sub={"sell-out, turn and average price per pack" + (view.skuGrain === "product" ? " · product grain (no SKU detail in this data)" : "")}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>
              {[skuLbl, view.skuGrain === "product" ? "Molecule" : "Brand", "Sell-out", "Turn", "Avg price" + (C.measure === "d" ? " (USD)" : C.measure === "l" ? " (LC)" : "")].map(function (h, i) {
                return <th key={i} style={{ textAlign: i < 2 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>;
              })}
            </tr></thead>
            <tbody>
              {view.skuList.map(function (s, i) {
                var turn = s.inV > 0 ? pct1(s.outV, s.inV) : 0;
                return (
                  <tr key={i}>
                    <td style={{ padding: "6px 6px", fontWeight: 700, color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{s.name}</td>
                    <td style={{ padding: "6px 6px", color: "#64748b", borderBottom: "1px solid #f1f5f9" }}>{s.brand}</td>
                    <td style={{ padding: "6px 6px", textAlign: "right", fontWeight: 700, color: "#0F5C66", borderBottom: "1px solid #f1f5f9" }}>{C.msrFmt(s.outV)}</td>
                    <td style={{ padding: "6px 6px", textAlign: "right", fontWeight: 800, color: turn >= 100 ? C_POS : C_WARN, borderBottom: "1px solid #f1f5f9" }}>{s.inV > 0 ? turn.toFixed(0) + "%" : "—"}</td>
                    <td style={{ padding: "6px 6px", textAlign: "right", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{s.pln > 0 ? fmtM(s.pl / s.pln) : "—"}</td>
                  </tr>
                );
              })}
              {view.skuList.length === 0 && <tr><td colSpan={5} style={{ padding: 12, fontSize: 10.5, color: "#94a3b8" }}>{"No data for " + mkName + " in " + C.eff.curLbl + " under the current filters."}</td></tr>}
            </tbody>
          </table>
        </STPanel>
      </STRow>
      <STNote>SKUs above the 100% line are rationed by supply, not left on shelves — protect replenishment on the fastest-turning packs before they stock out.</STNote>
    </div>
  );
}

/* ════════════ 6. CHANNEL & DISTRIBUTION ════════════ */
export function STChannelDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  var OG = useMemo(function () { return stOutlet(mp, rows); }, [mp, rows]);
  var view = useMemo(function () {
    /* pharmacy grain has no period (whole window); the main-rows fallback does,
       so it is restricted to the selected window. Dim filters always apply. */
    var phAll = {}, byMf = {}, phClass = {};
    OG.rows.forEach(function (p) {
      if (p.fl !== "Sell-Out" || !C.eff.dimOk(p)) return;
      if (OG.perPeriod && !C.eff.inCur(p)) return;
      var k = OG.key(p); if (!k) return;
      var v = Number(p[C.measure === "gu" ? "gu" : C.measure]) || 0;
      if (!phAll[k]) phAll[k] = { v: 0, cls: p.phc || "" };
      phAll[k].v += v;
      if (p.phc) phAll[k].cls = p.phc;
      if (p.mf) {
        if (!byMf[p.mf]) byMf[p.mf] = { doors: {}, v: 0, sk: 0, skN: 0, mds: {} };
        byMf[p.mf].doors[k] = 1;
        byMf[p.mf].v += v;
        byMf[p.mf].sk += Number(p.sk) || 0; byMf[p.mf].skN += 1;
        if (p.md) byMf[p.mf].mds[p.md] = 1;
      }
    });
    Object.keys(phAll).forEach(function (ph) {
      /* group by class when the view carries one; with no class column the
         outlet key itself (governorate) is the only honest grouping */
      var c = phAll[ph].cls || (OG.real ? "Unclassified" : ph);
      if (!phClass[c]) phClass[c] = { cls: c, doors: 0, v: 0 };
      phClass[c].doors += 1; phClass[c].v += phAll[ph].v;
    });
    var panelDoors = Object.keys(phAll).length;
    var mfList = Object.keys(byMf).map(function (k) {
      var d = byMf[k];
      var doors = Object.keys(d.doors).length;
      /* SKU depth comes from the pharmacy grain's per-outlet SKU count; without
         that grain we report distinct products carried instead, and say so. */
      var depth = OG.hasSku ? (d.skN > 0 ? d.sk / d.skN : 0) : Object.keys(d.mds).length;
      return { name: k, doors: doors, v: d.v, perDoor: doors > 0 ? d.v / doors : 0, skuPerDoor: depth };
    }).sort(function (a, b) { return b.v - a.v; }).slice(0, 9);
    var phs = Object.keys(phAll).map(function (ph) { return phAll[ph].v; }).sort(function (a, b) { return b - a; });
    var totV = phs.reduce(function (a, b) { return a + b; }, 0);
    var cum = 0, curve = [{ p: 0, cum: 0 }];
    phs.forEach(function (v, i) { cum += v; curve.push({ p: +(((i + 1) / phs.length) * 100).toFixed(0), cum: +pct1(cum, totV).toFixed(1) }); });
    var top20idx = Math.max(1, Math.round(phs.length * 0.2));
    var top20 = pct1(phs.slice(0, top20idx).reduce(function (a, b) { return a + b; }, 0), totV);
    var classes = Object.keys(phClass).sort().map(function (c) { return phClass[c]; });
    return { panelDoors: panelDoors, mfList: mfList, curve: curve, top20: top20, classes: classes, totV: totV };
  }, [mp, rows, OG, C.eff, C.measure]);
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="🏪" title="Pharmacy Segmentation & Channel" subtitle="Doors, per-door yield, concentration & panel weight · whole data window" />
      <STPanelNote mp={mp} />
      {!OG.real && (
        <STDataGap diag={(mp && mp._stDiag) || null} title={"Shown at " + OG.unit + " level — no pharmacy identity in the view"}>
          Every panel here counts outlets, which needs a pharmacy identity in <code>vw_SalesTracker</code>. {OG.src === "main-rows"
            ? <span>The pharmacy-grain aggregate did not reach the browser at all, so these panels are computed from the main aggregate at <strong>{OG.unit}</strong> level, inside the selected period.</span>
            : <span>No pharmacy id carries values, so the dashboard falls back to the finest outlet proxy the view does carry — <strong>{OG.unit}</strong>.</span>} Every card below is labelled accordingly: the structure (reach, per-outlet yield, concentration, weight) is real, only the grain is coarser. To get true pharmacy counts, add the pharmacy id to the view (<strong>sales_tracker_usd_v4.sql, Part B</strong>) — or read the column list below and send me the name of the column that holds it, and I will wire it in.
        </STDataGap>
      )}
      {OG.real && view.panelDoors === 0 && (
        <STDataGap diag={(mp && mp._stDiag) || null}>
          The pharmacy aggregate returned no sell-out rows for the current filters, so the outlet panels are empty. Widen the filters (product / molecule / ATC / segment) or check the diagnostic line below.
        </STDataGap>
      )}
      <STTotals items={[
        { label: OG.panelLbl, value: view.panelDoors, icon: "🏪", grad: G_BLUE, sub: "dispensing the filtered set" },
        { label: (view.mfList.length ? view.mfList[0].name : "Leader") + " Distribution", value: view.mfList.length ? view.mfList[0].doors + "/" + view.panelDoors : "—", icon: "🚪", grad: G_NAVY, sub: OG.doors + " reached" },
        { label: "Leader Sell-Out per " + OG.doorS, value: view.mfList.length ? C.msrFmt(view.mfList[0].perDoor) : "—", icon: "💰", grad: G_TEAL, sub: C.msrLbl + " per stocking " + OG.unitS },
        { label: "Top-20% " + OG.doors + " Carry", value: view.top20.toFixed(0) + "%", icon: "📊", grad: G_AMBER, sub: "of dispensed " + C.msrLbl.toLowerCase() },
        { label: OG.hasSku ? ("Leader SKUs per " + OG.doorS) : "Leader products carried", value: view.mfList.length ? view.mfList[0].skuPerDoor.toFixed(OG.hasSku ? 1 : 0) : "—", icon: "📦", grad: G_VIOLET, sub: OG.hasSku ? "range depth on the average shelf" : "distinct products in the filtered set" }
      ]} />
      <STRow>
        <STPanel flex="1 1 380px" title={(OG.real ? "Pharmacies" : OG.unit.charAt(0).toUpperCase() + OG.unit.slice(1)) + " reached"} sub="numeric distribution by maker · filters apply">
          <STBars items={view.mfList.slice().sort(function (a, b) { return b.doors - a.doors; }).map(function (d, i) {
            return { name: d.name, val: d.doors, hl: view.mfList.length && d.name === view.mfList[0].name, lbl: d.doors + " " + OG.doors };
          })} labelW={130} valW={70} />
        </STPanel>
        <STPanel flex="1 1 380px" title={"Sell-out per stocking " + OG.unitS} sub={"yield per " + OG.doorS + " — depth, not reach"}>
          <STBars items={view.mfList.slice().sort(function (a, b) { return b.perDoor - a.perDoor; }).map(function (d) {
            return { name: d.name, val: d.perDoor, hl: view.mfList.length && d.name === view.mfList[0].name, lbl: C.msrFmt(d.perDoor) + " · " + (OG.hasSku ? d.skuPerDoor.toFixed(1) + " SKUs/" + OG.doorS : d.skuPerDoor.toFixed(0) + " products") };
          })} labelW={130} valW={150} />
        </STPanel>
      </STRow>
      <STRow>
        <STPanel flex="1 1 420px" title={"Concentration of demand across " + OG.unit} sub={"cumulative % of dispensed value, " + OG.doors + " ranked by size"}>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={view.curve} margin={{ top: 10, right: 24, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="p" unit="%" tick={{ fontSize: 10 }} height={36} label={axX("% of " + OG.unit + ", largest first")} />
              <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 10 }} width={66} label={axY("Cumulative % of sell-out")} />
              <Tooltip formatter={function (v) { return v + "%"; }} />
              <ReferenceLine x={20} stroke={C_WARN} strokeDasharray="4 4" label={{ value: "top 20% = " + view.top20.toFixed(0) + "%", fontSize: 9, fill: C_WARN, position: "insideTopLeft" }} />
              <Line dataKey="cum" name="cumulative" stroke="#0F5C66" strokeWidth={2.4} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </STPanel>
        <STPanel flex="1 1 380px" title={OG.anyCls ? "Panel weight by pharmacy class" : "Panel weight by " + OG.unitS} sub={OG.doors + " and dispensed value per " + (OG.anyCls ? "class" : OG.unitS)}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead><tr>
              {[OG.anyCls ? "Class" : OG.unitS.charAt(0).toUpperCase() + OG.unitS.slice(1), OG.unit.charAt(0).toUpperCase() + OG.unit.slice(1), "Sell-out", "% of value"].map(function (h, i) {
                return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 8px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>;
              })}
            </tr></thead>
            <tbody>
              {view.classes.map(function (c, i) {
                return (
                  <tr key={i}>
                    <td style={{ padding: "7px 8px", fontWeight: 800, color: "#0F5C66", borderBottom: "1px solid #f1f5f9" }}>{c.cls}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 700, color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{c.doors}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{C.msrFmt(c.v)}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 700, color: "#17707C", borderBottom: "1px solid #f1f5f9" }}>{pct1(c.v, view.totV).toFixed(1) + "%"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <STNote tag="PANEL" accent={C_ACC}>{"This table is the weight of the used panel — how many " + OG.unit + " the analysis stands on, and how much of the demand each " + (OG.anyCls ? "class" : OG.unitS) + " carries."}</STNote>
        </STPanel>
      </STRow>
    </div>
  );
}

/* ════════════ 7. INVENTORY & STOCK ════════════ */
function InventorySection({ rows, mp, C, D, showTotals }) {
  var [sel, setSel] = useState("");
  var allTime = useMemo(function () {
    var t = {};
    (rows || []).forEach(function (r) { if (r.fl === "Sell-Out" && C.eff.dimOk(r)) t[r.mf] = (t[r.mf] || 0) + (Number(C.hasUSD ? r.d : r.l) || 0); });
    return Object.keys(t).sort(function (a, b) { return t[b] - t[a]; });
  }, [rows, C.eff]);
  var mkName = sel || allTime[0] || "";
  var view = useMemo(function () {
    /* overhang by governorate — SELECTED WINDOW (gross units) */
    var govs = {};
    (rows || []).forEach(function (r) {
      if (r.mf !== mkName || !C.eff.dimOk(r) || !C.eff.inCur(r)) return;
      var g = r.gv || "—";
      if (!govs[g]) govs[g] = { name: g, inGU: 0, outGU: 0 };
      if (r.fl === "Sell-In") govs[g].inGU += Number(r.gu) || 0; else govs[g].outGU += Number(r.gu) || 0;
    });
    var govList = Object.keys(govs).map(function (k) { return govs[k]; })
      .filter(function (g) { return g.outGU > 0 || g.inGU > 0; })
      .map(function (g) { g.ov = g.outGU > 0 ? g.inGU / g.outGU : (g.inGU > 0 ? 9.99 : 0); return g; })
      .sort(function (a, b) { return b.ov - a.ov; }).slice(0, 12);
    /* channel stock build — FULL history (stock is cumulative by nature), dims filtered.
       Monthly grain when real months exist; quarterly fallback otherwise. */
    var mon = {};
    ((mp && mp._stExtra && mp._stExtra.monthly) || []).forEach(function (r) {
      if (r.mf !== mkName || !C.eff.dimOk(r)) return;
      var mnum = Number(r.mn);
      if (!(mnum >= 1 && mnum <= 12)) return;
      var k = r.yr * 100 + mnum;
      if (!mon[k]) mon[k] = { k: k, yr: r.yr, mn: mnum, inGU: 0, outGU: 0 };
      if (r.fl === "Sell-In") mon[k].inGU += Number(r.gu) || 0; else mon[k].outGU += Number(r.gu) || 0;
    });
    var keys = Object.keys(mon).map(Number).sort(function (a, b) { return a - b; });
    var cum = 0;
    var trend = keys.map(function (k) {
      var m = mon[k];
      cum += m.inGU - m.outGU;
      return { name: MONTH_S[m.mn - 1] + " " + String(m.yr).slice(2), net: m.inGU - m.outGU, stock: cum };
    });
    var isQtr = false;
    if (trend.length === 0) {
      isQtr = true;
      var qq = {};
      (rows || []).forEach(function (r) {
        if (r.mf !== mkName || !C.eff.dimOk(r)) return;
        var qk = r.yr * 10 + r.qr;
        if (!qq[qk]) qq[qk] = { yr: r.yr, qr: r.qr, inGU: 0, outGU: 0 };
        if (r.fl === "Sell-In") qq[qk].inGU += Number(r.gu) || 0; else qq[qk].outGU += Number(r.gu) || 0;
      });
      var cq = 0;
      trend = Object.keys(qq).map(Number).sort(function (a, b) { return a - b; }).map(function (qk) {
        var m = qq[qk];
        cq += m.inGU - m.outGU;
        return { name: "Q" + m.qr + " " + String(m.yr).slice(2), net: m.inGU - m.outGU, stock: cq };
      });
    }
    /* whole-window totals for current stock (dims filtered, ALL periods) */
    var inAll = 0, outAll = 0, outCurQ = 0;
    (rows || []).forEach(function (r) {
      if (r.mf !== mkName || !C.eff.dimOk(r)) return;
      var gu = Number(r.gu) || 0;
      if (r.fl === "Sell-In") inAll += gu; else { outAll += gu; if (C.eff.inCur(r)) outCurQ += gu; }
    });
    var current = inAll - outAll;
    var days = Object.keys(C.eff.curSet).length * 91;
    var perDay = days > 0 ? outCurQ / days : 0;
    var cover = perDay > 0 ? Math.round(current / perDay) : null;
    var mk = D.makers[mkName] || { curIn: 0, curOut: 0 };
    var mkOvCur = 0;
    var winIn = 0, winOut = 0;
    (rows || []).forEach(function (r) {
      if (r.mf !== mkName || !C.eff.dimOk(r) || !C.eff.inCur(r)) return;
      if (r.fl === "Sell-In") winIn += Number(r.gu) || 0; else winOut += Number(r.gu) || 0;
    });
    mkOvCur = winOut > 0 ? winIn / winOut : 0;
    return { govList: govList, trend: trend, current: current, cover: cover, mkOvCur: mkOvCur, isQtr: isQtr };
  }, [rows, mp, C.eff, mkName, D]);
  var maxOv = 2;
  view.govList.forEach(function (g) { if (g.ov > maxOv && g.ov < 9.9) maxOv = Math.min(g.ov, 5); });
  return (
    <div>
      {showTotals !== false && (
        <STTotals items={[
          { label: mkName + " Current Channel Stock", value: fmtN(view.current), icon: "📦", grad: G_NAVY, color: view.current < 0 ? C_NEG : undefined, sub: "Σ(sell-in+FOC) − Σ(sell-out+FOC), full history" },
          { label: "Days of Cover", value: view.cover == null ? "—" : view.cover + " days", icon: "⏳", grad: G_TEAL, color: view.cover != null && view.cover < 30 ? C_NEG : undefined, sub: "at " + C.eff.curLbl + " dispensing rate" },
          { label: mkName + " Overhang — " + C.eff.curLbl, value: view.mkOvCur > 0 ? view.mkOvCur.toFixed(2) + "×" : "—", icon: "⚖️", grad: G_AMBER, sub: "sell-in ÷ sell-out (gross units)" },
          { label: "Market Sell-Out — " + C.eff.curLbl, value: C.msrFmt(D.tot.curOut), icon: "💊", grad: G_VIOLET, pg: gpct(D.tot.curOut, D.tot.prevOut), yg: gpct(D.tot.curOut, D.tot.yoyOut) }
        ]} />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <STMakerPicker label="Manufacturer" accent="#0F5C66" value={mkName} onChange={setSel}
          items={allTime.map(function (name, i) { return { name: name, rank: i + 1 }; })} />
      </div>
      <STRow>
        <STPanel flex="1 1 420px" title={"Stock overhang by governorate — " + mkName + " (" + C.eff.curLbl + ")"} sub="sell-in ÷ sell-out (gross units) · left of 1.0 = running short, right = building stock">
          {view.govList.map(function (g, i) {
            var over = g.ov >= 1;
            var w = Math.min(100, Math.abs(g.ov - 1) / (maxOv - 1) * 100);
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: 7 }}>
                <div style={{ width: 100, fontSize: 10.5, fontWeight: 600, color: "#475569", textAlign: "right", paddingRight: 8 }}>{g.name}</div>
                <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <div style={{ width: "50%", display: "flex", justifyContent: "flex-end" }}>
                    {!over && <div style={{ width: w + "%", minWidth: 2, height: 9, background: "#d8973c", borderRadius: "3px 0 0 3px" }} />}
                  </div>
                  <div style={{ width: 1.5, height: 18, background: "#64748b" }} />
                  <div style={{ width: "50%" }}>
                    {over && <div style={{ width: w + "%", minWidth: 2, height: 9, background: g.ov > 1.5 ? "#dc2626" : "#6BBCC4", borderRadius: "0 3px 3px 0" }} />}
                  </div>
                </div>
                <div style={{ width: 52, fontSize: 10, fontWeight: 800, textAlign: "right", color: g.ov > 1.5 ? C_NEG : g.ov < 0.6 ? C_WARN : C_POS }}>{g.ov >= 9.9 ? ">9×" : g.ov.toFixed(2) + "×"}</div>
              </div>
            );
          })}
          {view.govList.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No data for this selection.</div>}
          <div style={{ fontSize: 8.5, color: "#94a3b8", marginTop: 4, textAlign: "center" }}>center line = balanced 1.0×</div>
        </STPanel>
        <STPanel flex="1 1 420px" title={"Channel stock build — " + mkName} sub={"bars = " + (view.isQtr ? "quarterly" : "monthly") + " net flow (in − out, gross units) · line = cumulative channel stock · full history"}>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={view.trend} margin={{ top: 12, right: 24, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} height={34} label={axX(view.isQtr ? "Quarter" : "Month")} />
              <YAxis tickFormatter={fmtM} tick={{ fontSize: 10 }} width={68} label={axY("Gross units")} />
              <Tooltip formatter={function (v) { return fmtN(v); }} />
              <Legend verticalAlign="top" align="right" height={22} iconSize={9} wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={0} stroke="#64748b" />
              <Bar dataKey="net" name={"net " + (view.isQtr ? "quarterly" : "monthly") + " flow"} maxBarSize={16}>
                {view.trend.map(function (t, i) { return <Cell key={i} fill={t.net >= 0 ? "#6BBCC4" : "#dc2626"} />; })}
              </Bar>
              <Line dataKey="stock" name="cumulative stock" stroke="#0F5C66" strokeWidth={2.4} dot={{ r: 2.5 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </STPanel>
      </STRow>
      <STNote>Negative cumulative stock means dispensing has exceeded shipments since the data began — the pharmacy shelf, not the factory, funded that demand. Without a supply step-up, dispensing must eventually fall to match shipments.</STNote>
    </div>
  );
}

export function STInventoryDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="📉" title="Inventory & Stock" subtitle={"Overhang " + C.eff.curLbl + " · stock build over full history"} />
      <STPanelNote mp={mp} />
      <InventorySection rows={rows} mp={mp} C={C} D={D} />
    </div>
  );
}

/* ════════════ R1 — SELL-IN vs SELL-OUT INTELLIGENCE (merge of Trajectory + Inventory + gap diagnostics) ════════════ */
export function STSiSoDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  var [gapDim, setGapDim] = useState("mf"); /* mf | md */
  var view = useMemo(function () {
    var ratio = D.tot.curOut > 0 ? D.tot.curIn / D.tot.curOut : null;
    var ratioB = D.tot.baseOut > 0 ? D.tot.baseIn / D.tot.baseOut : null;
    var gap = D.tot.curIn - D.tot.curOut;
    /* stock pressure = cumulative gross-unit gap ÷ average quarterly sell-out (gross units) */
    var inAll = 0, outAll = 0, qOut = {};
    (rows || []).forEach(function (r) { if (!C.eff.dimOk(r)) return; var gu = Number(r.gu) || 0; if (r.fl === "Sell-In") inAll += gu; else { outAll += gu; var qk = r.yr * 10 + r.qr; qOut[qk] = (qOut[qk] || 0) + gu; } });
    var qKeys = Object.keys(qOut); var avgQ = qKeys.length ? outAll / qKeys.length : 0;
    var pressure = avgQ > 0 ? (inAll - outAll) / avgQ : null; /* quarters of stock */
    /* gap table by manufacturer / product (current window) */
    var gapList = digestBy(rows, C.eff, C.measure, function (r) { return gapDim === "mf" ? r.mf : r.md; })
      .filter(function (o) { return o.curIn > 0 || o.curOut > 0; })
      .map(function (o) {
        var mkt = D.tot.curOut > 0 ? D.tot.curIn / D.tot.curOut : 1;
        return { name: o.name, inV: o.curIn, outV: o.curOut, gap: o.curIn - o.curOut, ratio: o.curOut > 0 ? o.curIn / o.curOut : null,
          share: pct1(o.curOut, D.tot.curOut), st: o.curIn > 0 ? pct1(o.curOut, o.curIn) : null,
          ratioB: o.baseOut > 0 ? o.baseIn / o.baseOut : null, mktRatio: mkt };
      }).sort(function (a, b) { return Math.abs(b.gap) - Math.abs(a.gap); });
    /* regional matrix */
    var reg = digestBy(rows, C.eff, C.measure, function (r) { return r.gv; }).map(function (o) { return { name: o.name, inV: o.curIn, outV: o.curOut, ratio: o.curOut > 0 ? o.curIn / o.curOut : null, gap: o.curIn - o.curOut }; }).sort(function (a, b) { return b.outV - a.outV; }).slice(0, 12);
    /* anomalies: ratio deviating > 2σ from market ratio (log space) OR sign flip of gap vs base */
    var pool = gapList.filter(function (g) { return g.ratio && g.ratio > 0 && g.share >= 0.5; });
    var logs = pool.map(function (g) { return Math.log(g.ratio); });
    var mean = logs.length ? logs.reduce(function (a, b) { return a + b; }, 0) / logs.length : 0;
    var sd = logs.length > 1 ? Math.sqrt(logs.reduce(function (a, b) { return a + (b - mean) * (b - mean); }, 0) / (logs.length - 1)) : 0;
    var anomalies = pool.map(function (g) {
      var z = sd > 0 ? (Math.log(g.ratio) - mean) / sd : 0;
      var flip = g.ratioB != null && ((g.ratio >= 1 && g.ratioB < 1) || (g.ratio < 1 && g.ratioB >= 1));
      var why = Math.abs(z) >= 2 ? (z > 0 ? "sell-in far above sell-out (" + g.ratio.toFixed(2) + "× vs market " + g.mktRatio.toFixed(2) + "×)" : "sell-out far above sell-in (" + g.ratio.toFixed(2) + "× vs market " + g.mktRatio.toFixed(2) + "×)") : flip ? "ratio flipped from " + g.ratioB.toFixed(2) + "× to " + g.ratio.toFixed(2) + "× vs " + C.eff.baseLbl : null;
      return why ? { name: g.name, z: z, why: why, share: g.share, sev: Math.abs(z) >= 3 ? "high" : "med" } : null;
    }).filter(Boolean).sort(function (a, b) { return Math.abs(b.z) - Math.abs(a.z); }).slice(0, 10);
    return { ratio: ratio, ratioB: ratioB, gap: gap, pressure: pressure, stock: inAll - outAll, gapList: gapList.slice(0, 15), reg: reg, anomalies: anomalies };
  }, [rows, D, C.eff, C.measure, gapDim]);
  var ratioCol = function (r) { return r == null ? "#64748b" : r > 1.5 ? C_WARN : r < 0.6 ? C_NEG : C_POS; };
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="🔁" title="Sell-In vs Sell-Out Intelligence" subtitle={"Flows, ratio, channel gap, stock pressure & anomalies · " + C.eff.curLbl + " vs " + C.eff.baseLbl} />
      <STPanelNote mp={mp} />
      <STTotals items={[
        { label: "Sell-In — " + C.eff.curLbl, value: C.msrFmt(D.tot.curIn), icon: "🚚", grad: G_NAVY, pg: gpct(D.tot.curIn, D.tot.prevIn), yg: gpct(D.tot.curIn, D.tot.yoyIn) },
        { label: "Sell-Out — " + C.eff.curLbl, value: C.msrFmt(D.tot.curOut), icon: "💊", grad: G_VIOLET, pg: gpct(D.tot.curOut, D.tot.prevOut), yg: gpct(D.tot.curOut, D.tot.yoyOut) },
        { label: "Sell-In / Sell-Out Ratio", value: view.ratio != null ? view.ratio.toFixed(2) + "×" : "—", icon: "⚖️", grad: G_TEAL, color: ratioCol(view.ratio), sub: "was " + (view.ratioB != null ? view.ratioB.toFixed(2) + "×" : "—") + " in " + C.eff.baseLbl + " · 1.0× balanced" },
        { label: "Channel Gap — " + C.eff.curLbl, value: (view.gap >= 0 ? "+" : "−") + C.msrFmt(Math.abs(view.gap)), icon: "↔️", grad: G_AMBER, color: view.gap < 0 ? C_NEG : undefined, sub: "sell-in − sell-out · + = stock building in the channel" },
        { label: "Stock Pressure", value: view.pressure != null ? view.pressure.toFixed(1) + " qtrs" : "—", icon: "📦", grad: view.pressure != null && (view.pressure > 1.5 || view.pressure < 0) ? "linear-gradient(90deg,#991b1b,#dc2626)" : G_BLUE, color: view.pressure != null && view.pressure < 0 ? C_NEG : undefined, sub: "cumulative stock ÷ avg quarterly sell-out (gross units) · " + fmtN(view.stock) + " units" }
      ]} />
      <TrajectorySection rows={rows} mp={mp} C={C} D={D} />
      <STRow>
        <STPanel flex="1 1 520px" title={"Channel gap by " + (gapDim === "mf" ? "manufacturer" : "product") + " — " + C.eff.curLbl} sub="sell-in, sell-out, gap, ratio and sell-through · sorted by |gap| · colour = ratio vs 1.0×" grad={G_AMBER}>
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            {[["mf", "Manufacturer"], ["md", "Product"]].map(function (o) { var on = gapDim === o[0]; return <div key={o[0]} onClick={function () { setGapDim(o[0]); }} style={{ padding: "3px 12px", borderRadius: 14, fontSize: 10, fontWeight: 700, cursor: "pointer", background: on ? "#0F5C66" : "#f1f5f9", color: on ? "#fff" : "#475569" }}>{o[1]}</div>; })}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>{[gapDim === "mf" ? "Manufacturer" : "Product", "Sell-in", "Sell-out", "Gap", "Ratio", "Sell-through", "Share"].map(function (h, i) { return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>; })}</tr></thead>
            <tbody>
              {view.gapList.map(function (g, i) {
                return (
                  <tr key={i}>
                    <td style={{ padding: "5px 6px", fontWeight: 700, color: "#334155", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{g.name}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: C_IN, borderBottom: "1px solid #f1f5f9" }}>{C.msrFmt(g.inV)}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: C_OUT, borderBottom: "1px solid #f1f5f9" }}>{C.msrFmt(g.outV)}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: g.gap >= 0 ? C_WARN : C_NEG, borderBottom: "1px solid #f1f5f9" }}>{(g.gap >= 0 ? "+" : "−") + C.msrFmt(Math.abs(g.gap))}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: ratioCol(g.ratio), borderBottom: "1px solid #f1f5f9" }}>{g.ratio != null ? g.ratio.toFixed(2) + "×" : "—"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{g.st != null ? g.st.toFixed(0) + "%" : "—"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#64748b", borderBottom: "1px solid #f1f5f9" }}>{g.share.toFixed(1) + "%"}</td>
                  </tr>
                );
              })}
              {view.gapList.length === 0 && <tr><td colSpan={7} style={{ padding: 12, color: "#94a3b8" }}>No data for this selection.</td></tr>}
            </tbody>
          </table>
        </STPanel>
        <STPanel flex="1 1 360px" title="Unusual sell-in / sell-out behaviour" sub={"ratio > 2σ from the market ratio, or a sign flip vs " + C.eff.baseLbl + " · items with ≥0.5% share"} grad="linear-gradient(90deg,#991b1b,#dc2626)">
          {view.anomalies.map(function (a, i) {
            var col = a.sev === "high" ? { bg: "#fef2f2", bd: "#fecaca", tx: "#991b1b" } : { bg: "#fffbeb", bd: "#fde68a", tx: "#92400e" };
            return (
              <div key={i} style={{ padding: "7px 10px", marginBottom: 6, background: col.bg, border: "1px solid " + col.bd, borderRadius: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: col.tx }}>{a.name}</span>
                  <span style={{ fontSize: 9, color: "#64748b" }}>{a.share.toFixed(1) + "% share · z " + a.z.toFixed(1)}</span>
                </div>
                <div style={{ fontSize: 10, color: col.tx, marginTop: 2 }}>{a.why}</div>
              </div>
            );
          })}
          {view.anomalies.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No unusual ratios in this window.</div>}
        </STPanel>
      </STRow>
      <STPanel title={"Regional sell-in vs sell-out — " + C.eff.curLbl} sub="per governorate · bars = sell-in (blue) and sell-out (green) · badge = ratio" grad={G_BLUE}>
        {(function () {
          var mx = 1; view.reg.forEach(function (r) { mx = Math.max(mx, r.inV, r.outV); });
          return view.reg.map(function (r, i) {
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 7 }}>
                <div style={{ width: 110, fontSize: 10.5, fontWeight: 700, color: "#475569", textAlign: "right" }}>{r.name}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ height: 6, background: "#f1f5f9", borderRadius: 4, marginBottom: 3 }}><div style={{ width: Math.max(1, r.inV / mx * 100) + "%", height: "100%", background: C_IN, borderRadius: 3 }} /></div>
                  <div style={{ height: 6, background: "#f1f5f9", borderRadius: 4 }}><div style={{ width: Math.max(1, r.outV / mx * 100) + "%", height: "100%", background: C_OUT, borderRadius: 4 }} /></div>
                </div>
                <div style={{ width: 150, fontSize: 9.5, color: "#334155", whiteSpace: "nowrap" }}>{C.msrFmt(r.inV) + " / " + C.msrFmt(r.outV)}</div>
                <span style={{ width: 54, textAlign: "center", padding: "2px 6px", borderRadius: 10, fontSize: 9.5, fontWeight: 800, color: "#fff", background: ratioCol(r.ratio) }}>{r.ratio != null ? r.ratio.toFixed(2) + "×" : "—"}</span>
              </div>
            );
          });
        })()}
        {view.reg.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No data for this selection.</div>}
      </STPanel>
      <InventorySection rows={rows} mp={mp} C={C} D={D} showTotals={true} />
    </div>
  );
}

/* ════════════ R1 — FOC INTELLIGENCE ════════════ */
export function STFocDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  var view = useMemo(function () {
    /* FOC lives on the SELL-IN flow: fu (FOC units), gu (gross = paid + FOC), u (paid units) */
    var agg = function (keyFn) {
      var o = {};
      (rows || []).forEach(function (r) {
        if (r.fl !== "Sell-In" || !C.eff.dimOk(r)) return;
        var cur = C.eff.inCur(r), base = C.eff.inBase(r); if (!cur && !base) return;
        var k = keyFn(r); if (k == null || k === "") k = "—";
        var x = o[k]; if (!x) x = o[k] = { name: k, fu: 0, gu: 0, u: 0, l: 0, d: 0, bfu: 0, bgu: 0, bu: 0 };
        if (cur) { x.fu += Number(r.fu) || 0; x.gu += Number(r.gu) || 0; x.u += Number(r.u) || 0; x.l += Number(r.l) || 0; x.d += Number(r.d) || 0; }
        if (base) { x.bfu += Number(r.fu) || 0; x.bgu += Number(r.gu) || 0; x.bu += Number(r.u) || 0; }
      });
      return Object.keys(o).map(function (k) { return o[k]; });
    };
    var all = agg(function () { return "ALL"; })[0] || { fu: 0, gu: 0, u: 0, l: 0, d: 0, bfu: 0, bgu: 0, bu: 0 };
    var mkt = { fu: all.fu, gu: all.gu, u: all.u, focPct: focPct9(all.fu, all.gu), focPctB: focPct9(all.bfu, all.bgu) };
    /* FOC value = FOC units × paid price (value ÷ paid units) in the active currency */
    var valKey = C.measure === "d" ? "d" : "l";
    var paidVal = valKey === "d" ? all.d : all.l;
    var price = all.u > 0 ? paidVal / all.u : 0;
    var focValue = all.fu * price;
    var byMk = agg(function (r) { return r.mf; }).map(function (x) {
      var vk = valKey === "d" ? x.d : x.l; var pr = x.u > 0 ? vk / x.u : 0;
      return { name: x.name, fu: x.fu, gu: x.gu, u: x.u, focPct: focPct9(x.fu, x.gu), focPctB: focPct9(x.bfu, x.bgu), focShare: pct1(x.fu, mkt.fu), grossShare: pct1(x.gu, mkt.gu), paidShare: pct1(x.u, mkt.u),
        index: mkt.focPct > 0 ? focPct9(x.fu, x.gu) / mkt.focPct * 100 : null, focVal: x.fu * pr };
    }).sort(function (a, b) { return b.gu - a.gu; });
    var byMd = agg(function (r) { return r.md; }).map(function (x) { return { name: x.name, fu: x.fu, gu: x.gu, focPct: focPct9(x.fu, x.gu), focShare: pct1(x.fu, mkt.fu) }; }).filter(function (x) { return x.gu > 0; }).sort(function (a, b) { return b.fu - a.fu; }).slice(0, 10);
    var byGv = agg(function (r) { return r.gv; }).map(function (x) { return { name: x.name, focPct: focPct9(x.fu, x.gu), gu: x.gu }; }).filter(function (x) { return x.gu > 0; }).sort(function (a, b) { return b.focPct - a.focPct; }).slice(0, 12);
    var byCl = agg(function (r) { return r.phc; }).map(function (x) { return { name: x.name === "—" ? "Unclassified" : "Class " + x.name, focPct: focPct9(x.fu, x.gu), gu: x.gu }; }).filter(function (x) { return x.gu > 0; }).sort(function (a, b) { return a.name.localeCompare(b.name); });
    /* FOC vs sell-out growth: per maker */
    var scat = byMk.slice(0, 15).map(function (m) { var dm = D.makers[m.name]; var g = dm ? gpct(dm.curOut, dm.baseOut) : null; return g == null ? null : { name: m.name, x: +m.focPct.toFixed(1), y: +Math.max(-100, Math.min(200, g)).toFixed(0), z: Math.max(m.gu, 1) }; }).filter(Boolean);
    /* FOC vs distribution: doors from pharma grain (Sell-In rows with FOC) */
    var doors = {}, focDoors = {}, _og = stOutlet(mp, rows);
    _og.rows.forEach(function (p) { if (p.fl !== "Sell-In" || !C.eff.dimOk(p) || !p.mf) return; if (_og.perPeriod && !C.eff.inCur(p)) return; var ok = _og.key(p); if (!ok) return; (doors[p.mf] = doors[p.mf] || {})[ok] = 1; if (Number(p.fu) > 0) (focDoors[p.mf] = focDoors[p.mf] || {})[ok] = 1; });
    var dist = byMk.slice(0, 10).map(function (m) { var dn = doors[m.name] ? Object.keys(doors[m.name]).length : 0; var fn = focDoors[m.name] ? Object.keys(focDoors[m.name]).length : 0; return { name: m.name, doors: dn, focDoors: fn, focPct: m.focPct }; });
    return { mkt: mkt, focValue: focValue, byMk: byMk, byMd: byMd, byGv: byGv, byCl: byCl, scat: scat, dist: dist, hasFocDoors: Object.keys(focDoors).length > 0 };
  }, [rows, mp, D, C.eff, C.measure]);
  var lead = view.byMk[0];
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="🎁" title="FOC Intelligence" subtitle={"Free-of-charge units on sell-in · " + C.eff.curLbl + " vs " + C.eff.baseLbl} />
      <STPanelNote mp={mp} />
      <STTotals items={[
        { label: "FOC Units — " + C.eff.curLbl, value: fmtN(view.mkt.fu), icon: "🎁", grad: G_AMBER, sub: "sell-in free units (bonus / FOC)" },
        { label: "FOC Value (at paid price)", value: fmtM(view.focValue), icon: "💸", grad: G_NAVY, sub: (C.measure === "d" ? "USD" : "LC") + " · FOC units × average paid price" },
        { label: "FOC Intensity", value: view.mkt.focPct.toFixed(1) + "%", icon: "%", grad: G_TEAL, color: view.mkt.focPct - view.mkt.focPctB > 3 ? C_WARN : undefined, sub: "FOC ÷ paid units · was " + view.mkt.focPctB.toFixed(1) + "% in " + C.eff.baseLbl },
        { label: "Gross vs Paid Share — leader", value: lead ? (lead.grossShare - lead.paidShare >= 0 ? "+" : "") + (lead.grossShare - lead.paidShare).toFixed(1) + " pts" : "—", icon: "🧮", grad: G_VIOLET, sub: lead ? lead.name + ": gross " + lead.grossShare.toFixed(1) + "% vs paid " + lead.paidShare.toFixed(1) + "%" : "" },
        { label: "Highest FOC / Sales Index", value: (function () { var t = view.byMk.slice(0, 10).filter(function (m) { return m.index != null; }).sort(function (a, b) { return b.index - a.index; })[0]; return t ? t.index.toFixed(0) : "—"; })(), icon: "📊", grad: G_BLUE, sub: "maker FOC % ÷ market FOC % × 100 · among top-10 by gross" }
      ]} />
      <STRow>
        <STPanel flex="1 1 520px" title={"FOC by manufacturer — " + C.eff.curLbl} sub="FOC %, FOC share, gross vs paid share and FOC/Sales index (100 = market)" grad={G_AMBER}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>{["Manufacturer", "Gross units", "FOC units", "FOC %", "Δ vs " + C.eff.baseLbl, "FOC share", "Gross − paid share", "Index"].map(function (h, i) { return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>; })}</tr></thead>
            <tbody>
              {view.byMk.slice(0, 12).map(function (m, i) {
                var dlt = m.focPct - m.focPctB, gp = m.grossShare - m.paidShare;
                return (
                  <tr key={i}>
                    <td style={{ padding: "5px 6px", fontWeight: 700, color: "#334155", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{m.name}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{fmtN(m.gu)}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: C_WARN, fontWeight: 700, borderBottom: "1px solid #f1f5f9" }}>{fmtN(m.fu)}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: "#0F5C66", borderBottom: "1px solid #f1f5f9" }}>{m.focPct.toFixed(1) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, color: dlt > 0.05 ? C_WARN : dlt < -0.05 ? C_POS : GRAY, borderBottom: "1px solid #f1f5f9" }}>{(dlt >= 0 ? "+" : "") + dlt.toFixed(1) + " pts"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{m.focShare.toFixed(1) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, color: gp > 0 ? C_WARN : gp < 0 ? C_POS : GRAY, borderBottom: "1px solid #f1f5f9" }}>{(gp >= 0 ? "+" : "") + gp.toFixed(1) + " pts"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: m.index != null && m.index > 130 ? C_WARN : m.index != null && m.index < 70 ? C_POS : "#334155", borderBottom: "1px solid #f1f5f9" }}>{m.index != null ? m.index.toFixed(0) : "—"}</td>
                  </tr>
                );
              })}
              {view.byMk.length === 0 && <tr><td colSpan={8} style={{ padding: 12, color: "#94a3b8" }}>No sell-in data for this selection.</td></tr>}
            </tbody>
          </table>
        </STPanel>
        <STPanel flex="1 1 340px" title="FOC by product — top 10 by FOC units" sub="FOC % of each product's gross · FOC share of market FOC" grad={G_NAVY}>
          <STBars items={view.byMd.map(function (p) { return { name: p.name, val: p.fu, lbl: fmtN(p.fu) + " · " + p.focPct.toFixed(1) + "% · " + p.focShare.toFixed(1) + "% of FOC", color: "#d8973c" }; })} labelW={130} valW={170} />
        </STPanel>
      </STRow>
      <STRow>
        <STPanel flex="1 1 300px" title="FOC % by governorate" sub="where the bonus is concentrated" grad={G_BLUE}>
          <STBars items={view.byGv.map(function (g) { return { name: g.name, val: g.focPct, lbl: g.focPct.toFixed(1) + "%", color: "#d8973c" }; })} labelW={100} valW={60} />
        </STPanel>
        <STPanel flex="1 1 300px" title="FOC % by pharmacy class" sub="does the bonus follow the big doors?" grad={G_TEAL}>
          <STBars items={view.byCl.map(function (g) { return { name: g.name, val: g.focPct, lbl: g.focPct.toFixed(1) + "% · " + fmtN(g.gu) + " gross", color: "#6BBCC4" }; })} labelW={100} valW={140} />
        </STPanel>
        <STPanel flex="1 1 380px" title="FOC intensity vs sell-out growth" sub={"x = FOC % · y = sell-out growth vs " + C.eff.baseLbl + " · bubble = gross units · does bonus buy demand?"} grad={G_VIOLET}>
          <ResponsiveContainer width="100%" height={240}>
            <ScatterChart margin={{ top: 18, right: 24, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis type="number" dataKey="x" name="FOC %" unit="%" domain={AX_NUM} tick={{ fontSize: 10 }} height={36} label={axX("FOC intensity (%)")} />
              <YAxis type="number" dataKey="y" name="growth" unit="%" domain={AX_NUM} tick={{ fontSize: 10 }} width={64} label={axY("Sell-out growth (%)")} />
              <ZAxis dataKey="z" range={[80, 600]} />
              <Tooltip content={function (o) { if (!o.active || !o.payload || !o.payload.length) return null; var p = o.payload[0].payload; return <div style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 12px", fontSize: 11 }}><b>{p.name}</b><div>FOC {p.x}% · growth {p.y}%</div></div>; }} />
              <ReferenceLine y={0} stroke="#475569" />
              <ReferenceLine x={view.mkt.focPct} stroke={GRAY} strokeDasharray="4 4" label={{ value: "market " + view.mkt.focPct.toFixed(1) + "%", fontSize: 9, fill: "#64748b", position: "insideTopRight" }} />
              <Scatter data={view.scat}>{view.scat.map(function (b, i) { return <Cell key={i} fill={b.y >= 0 ? "#6BBCC4" : "#dc2626"} fillOpacity={0.85} />; })}{bubbleLbl()}</Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </STPanel>
      </STRow>
      <STPanel title="FOC vs distribution — top makers" sub="doors supplied · doors that received FOC · FOC %" grad={G_AMBER}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
          <thead><tr>{["Manufacturer", "Doors supplied", "Doors with FOC", "% of doors with FOC", "FOC %"].map(function (h, i) { return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>; })}</tr></thead>
          <tbody>
            {view.dist.map(function (d, i) {
              return (
                <tr key={i}>
                  <td style={{ padding: "5px 6px", fontWeight: 700, color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{d.name}</td>
                  <td style={{ padding: "5px 6px", textAlign: "right", borderBottom: "1px solid #f1f5f9" }}>{d.doors}</td>
                  <td style={{ padding: "5px 6px", textAlign: "right", color: C_WARN, fontWeight: 700, borderBottom: "1px solid #f1f5f9" }}>{view.hasFocDoors ? d.focDoors : "—"}</td>
                  <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, borderBottom: "1px solid #f1f5f9" }}>{view.hasFocDoors && d.doors > 0 ? pct1(d.focDoors, d.doors).toFixed(0) + "%" : "—"}</td>
                  <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: "#0F5C66", borderBottom: "1px solid #f1f5f9" }}>{d.focPct.toFixed(1) + "%"}</td>
                </tr>
              );
            })}
            {view.dist.length === 0 && <tr><td colSpan={5} style={{ padding: 12, color: "#94a3b8" }}>No sell-in door data for this selection.</td></tr>}
          </tbody>
        </table>
      </STPanel>
      <STNote>FOC intensity is read against the market line: a maker above it with a rising Gross − Paid share gap is buying shelf presence with bonus; a maker below it that still grows sell-out is winning on demand. Compare the FOC vs sell-out growth chart with the FOC vs distribution table before concluding.</STNote>
    </div>
  );
}

/* ════════════ MARKET PERFORMANCE & TRENDS ════════════ */
export function STMarketDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  var [cDim, setCDim] = useState("mf");   /* contribution dimension: mf | md | mol | a3 */
  var valKey = C.measure === "d" ? "d" : "l";
  var view = useMemo(function () {
    /* market evolution: sell-in / sell-out per period + growth of sell-out */
    var ser = periodSeries(rows, (mp && mp._stExtra && mp._stExtra.monthly) || [], C.eff, function (p, r) {
      var v = Number(r[C.measure]) || 0;
      if (r.fl === "Sell-In") p.sellIn = (p.sellIn || 0) + v; else { p.sellOut = (p.sellOut || 0) + v; p.units = (p.units || 0) + (Number(r.u) || 0); p.val = (p.val || 0) + (Number(r[valKey]) || 0); }
    });
    var trend = ser.rows.slice(-12).map(function (p, i, arr) {
      var prev = arr[i - 1];
      return { name: p.name, sellIn: p.sellIn || 0, sellOut: p.sellOut || 0,
        gr: prev && prev.sellOut > 0 ? +(((p.sellOut || 0) - prev.sellOut) / prev.sellOut * 100).toFixed(1) : null,
        price: (p.units || 0) > 0 ? (p.val || 0) / p.units : null, units: p.units || 0 };
    });
    /* share evolution — top 6 makers by current sell-out */
    var top = D.list.slice(0, 6).map(function (m) { return m.name; });
    var ser2 = periodSeries(rows, (mp && mp._stExtra && mp._stExtra.monthly) || [], C.eff, function (p, r) {
      if (r.fl !== "Sell-Out") return;
      var v = Number(r[C.measure]) || 0;
      p.tot = (p.tot || 0) + v; p.mk = p.mk || {}; p.mk[r.mf] = (p.mk[r.mf] || 0) + v;
    });
    var shareRows = ser2.rows.slice(-12).map(function (p) {
      var o = { name: p.name };
      top.forEach(function (n) { o[n] = p.tot > 0 ? +(pct1((p.mk && p.mk[n]) || 0, p.tot)).toFixed(1) : 0; });
      return o;
    });
    /* contribution to growth */
    var keyFn = function (r) { return cDim === "mf" ? r.mf : cDim === "md" ? r.md : cDim === "mol" ? r.mol : r.a3; };
    var list = digestBy(rows, C.eff, C.measure, keyFn);
    var dTot = D.tot.curOut - D.tot.baseOut;
    var contrib = list.map(function (o) { return { name: o.name, d: o.curOut - o.baseOut, cur: o.curOut, base: o.baseOut,
      share: pct1(o.curOut, D.tot.curOut), growth: gpct(o.curOut, o.baseOut), c: dTot !== 0 ? (o.curOut - o.baseOut) / Math.abs(dTot) * 100 : 0 }; })
      .filter(function (x) { return x.cur > 0 || x.base > 0; }).sort(function (a, b) { return b.d - a.d; });
    var dec = decomposeValue(rows, C.eff, valKey, function (r) { return r.md || "—"; });
    return { trend: trend, shareRows: shareRows, top: top, contrib: contrib, dTot: dTot, dec: dec, monthly: ser.monthly };
  }, [rows, mp, D, C.eff, C.measure, cDim, valKey]);
  var COLS = PH_SERIES;
  var pos = view.contrib.filter(function (x) { return x.d > 0; }).slice(0, 8);
  var neg = view.contrib.filter(function (x) { return x.d < 0; }).slice(-8).reverse();
  var mxC = 1; pos.concat(neg).forEach(function (x) { mxC = Math.max(mxC, Math.abs(x.d)); });
  var cur = C.measure === "d" ? "USD" : "LC";
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="📊" title="Market Performance & Trends" subtitle={(view.monthly ? "monthly" : "quarterly") + " evolution, growth attribution & volume/price/mix · " + C.eff.curLbl + " vs " + C.eff.baseLbl} />
      <STPanelNote mp={mp} />
      <STTotals items={[
        { label: "Market Sell-Out — " + C.eff.curLbl, value: C.msrFmt(D.tot.curOut), icon: "💊", grad: G_VIOLET, pg: gpct(D.tot.curOut, D.tot.prevOut), yg: gpct(D.tot.curOut, D.tot.yoyOut) },
        { label: "Market Sell-In — " + C.eff.curLbl, value: C.msrFmt(D.tot.curIn), icon: "🚚", grad: G_NAVY, pg: gpct(D.tot.curIn, D.tot.prevIn), yg: gpct(D.tot.curIn, D.tot.yoyIn) },
        { label: "Value vs Unit growth", value: (view.dec.valGrowth == null ? "—" : view.dec.valGrowth.toFixed(0) + "%") + " / " + (view.dec.unitGrowth == null ? "—" : view.dec.unitGrowth.toFixed(0) + "%"), icon: "⚖️", grad: G_TEAL,
          sub: (view.dec.valGrowth != null && view.dec.unitGrowth != null) ? ((view.dec.valGrowth - view.dec.unitGrowth >= 0 ? "+" : "") + (view.dec.valGrowth - view.dec.unitGrowth).toFixed(0) + " pts = price / mix effect") : "value vs volume" },
        { label: "Average price", value: view.dec.priceCur == null ? "—" : fmtM(view.dec.priceCur), icon: "💲", grad: G_BLUE, sub: cur + " per unit · was " + (view.dec.priceBase == null ? "—" : fmtM(view.dec.priceBase)) },
        { label: "Top growth driver", value: pos.length ? pos[0].name : "—", icon: "🚀", grad: G_TEAL, sub: pos.length ? ("+" + C.msrFmt(pos[0].d) + " · " + pos[0].c.toFixed(0) + "% of market change") : "" },
        { label: "Biggest drag", value: neg.length ? neg[0].name : "—", icon: "🪫", grad: G_AMBER, color: neg.length ? C_NEG : undefined, sub: neg.length ? ("−" + C.msrFmt(Math.abs(neg[0].d)) + " · " + neg[0].c.toFixed(0) + "% of market change") : "no decline" }
      ]} />
      <STPanel title={"Market evolution — sell-in vs sell-out with growth"} sub={"bars = " + C.msrLbl.toLowerCase() + " by flow · line = sell-out growth vs previous period"} grad={G_NAVY}>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={view.trend} margin={{ top: 12, right: 16, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
            <XAxis dataKey="name" tick={{ fontSize: 9.5 }} height={34} label={axX("Period")} />
            <YAxis yAxisId="v" tickFormatter={fmtM} tick={{ fontSize: 10 }} width={68} label={axY(C.msrLbl)} />
            <YAxis yAxisId="g" orientation="right" unit="%" tick={{ fontSize: 10 }} width={58} label={axYR("Sell-out growth (%)")} />
            <Tooltip formatter={function (v, n) { return n === "growth" ? v + "%" : C.msrFmt(v); }} />
            <Legend verticalAlign="top" align="right" height={22} iconSize={9} wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="v" dataKey="sellIn" name="Sell-In" fill={C_IN} radius={[3, 3, 0, 0]} maxBarSize={20} />
            <Bar yAxisId="v" dataKey="sellOut" name="Sell-Out" fill={C_OUT} radius={[3, 3, 0, 0]} maxBarSize={20} />
            <Line yAxisId="g" dataKey="gr" name="growth" stroke={C_ACC} strokeWidth={2.6} dot={{ r: 3.2, fill: "#fff", stroke: C_ACC, strokeWidth: 2 }} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </STPanel>
      <STRow>
        <STPanel flex="1 1 470px" title="Contribution to market growth" sub={"who moved the market between " + C.eff.baseLbl + " and " + C.eff.curLbl + " (Δ " + (view.dTot >= 0 ? "+" : "−") + C.msrFmt(Math.abs(view.dTot)) + ")"} grad={G_TEAL}>
          <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
            {[["mf", "Manufacturer"], ["md", "Product"], ["mol", "Molecule"], ["a3", "ATC3"]].map(function (o) {
              var on = cDim === o[0];
              return <div key={o[0]} onClick={function () { setCDim(o[0]); }} style={{ padding: "3px 12px", borderRadius: 14, fontSize: 10, fontWeight: on ? 800 : 600, cursor: "pointer", background: on ? "#0F5C66" : "#f1f5f9", color: on ? "#fff" : "#475569" }}>{o[1]}</div>;
            })}
          </div>
          {pos.concat(neg).map(function (x, i) {
            var p = x.d >= 0;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                <div style={{ width: 140, fontSize: 10, fontWeight: 600, color: "#475569", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.name}</div>
                <div style={{ flex: 1, height: 8, background: "#f1f5f9", borderRadius: 4 }}><div style={{ width: Math.max(2, Math.abs(x.d) / mxC * 100) + "%", height: "100%", background: p ? "#6BBCC4" : "#dc2626", borderRadius: 3 }} /></div>
                <div style={{ width: 150, fontSize: 9.5, fontWeight: 700, color: p ? C_POS : C_NEG, whiteSpace: "nowrap" }}>
                  {(p ? "+" : "−") + C.msrFmt(Math.abs(x.d)) + " · " + (x.c >= 0 ? "+" : "") + x.c.toFixed(0) + "% · " + (x.growth == null ? "new" : x.growth.toFixed(0) + "%")}
                </div>
              </div>
            );
          })}
          {view.contrib.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No movement in this window.</div>}
        </STPanel>
        <STPanel flex="1 1 400px" title="Market share evolution" sub="top 6 makers · % of sell-out" grad={G_VIOLET}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={view.shareRows} margin={{ top: 12, right: 18, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 9.5 }} height={34} label={axX("Period")} />
              <YAxis unit="%" tick={{ fontSize: 10 }} width={62} label={axY("Market share (%)")} />
              <Tooltip formatter={function (v) { return v + "%"; }} />
              <Legend verticalAlign="top" align="right" height={22} iconSize={9} wrapperStyle={{ fontSize: 10 }} />
              {view.top.map(function (n, i) { return <Line key={n} dataKey={n} stroke={COLS[i % COLS.length]} strokeWidth={2} dot={{ r: 2.5 }} />; })}
            </LineChart>
          </ResponsiveContainer>
        </STPanel>
      </STRow>
      <STPanel title={"Growth decomposition — volume vs price vs mix (" + cur + " value)"} sub={"how the " + (view.dec.dVal >= 0 ? "increase" : "decrease") + " of " + fmtM(Math.abs(view.dec.dVal)) + " was produced · product-level prices"} grad={G_AMBER}>
        {(function () {
          var parts = [
            { n: C.eff.baseLbl + " value", v: view.dec.tot.valBase, t: "base" },
            { n: "Volume effect", v: view.dec.vol, t: "eff" },
            { n: "Price effect", v: view.dec.price, t: "eff" },
            { n: "Mix / other", v: view.dec.mix, t: "eff" },
            { n: C.eff.curLbl + " value", v: view.dec.tot.valCur, t: "base" }
          ];
          var mx = Math.max(view.dec.tot.valBase, view.dec.tot.valCur, 1);
          return parts.map(function (p, i) {
            var isBase = p.t === "base";
            var w = Math.max(1.5, Math.abs(p.v) / mx * 100);
            var col = isBase ? "#0F5C66" : (p.v >= 0 ? "#6BBCC4" : "#dc2626");
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 7 }}>
                <div style={{ width: 130, fontSize: 10.5, fontWeight: isBase ? 800 : 600, color: isBase ? "#0F5C66" : "#475569", textAlign: "right" }}>{p.n}</div>
                <div style={{ flex: 1, height: isBase ? 16 : 12, background: "#f1f5f9", borderRadius: 4 }}><div style={{ width: w + "%", height: "100%", background: col, borderRadius: 4 }} /></div>
                <div style={{ width: 130, fontSize: 10, fontWeight: 800, color: isBase ? "#0F5C66" : (p.v >= 0 ? C_POS : C_NEG), whiteSpace: "nowrap" }}>
                  {(isBase ? "" : (p.v >= 0 ? "+" : "−")) + fmtM(Math.abs(p.v)) + (isBase || view.dec.dVal === 0 ? "" : " · " + (p.v / Math.abs(view.dec.dVal) * 100).toFixed(0) + "%")}
                </div>
              </div>
            );
          });
        })()}
        <STNote tag="READ" accent={C_ACC}>
          {view.dec.dVal === 0 ? "No value change between the two windows." :
            ((Math.abs(view.dec.vol) >= Math.abs(view.dec.price) ? "Growth is mainly volume-driven" : "Growth is mainly price-driven") +
             " — volume " + (view.dec.vol >= 0 ? "+" : "−") + fmtM(Math.abs(view.dec.vol)) + ", price " + (view.dec.price >= 0 ? "+" : "−") + fmtM(Math.abs(view.dec.price)) +
             ", mix " + (view.dec.mix >= 0 ? "+" : "−") + fmtM(Math.abs(view.dec.mix)) + ". Mix carries new and lost products as well as the shift between cheaper and dearer packs.")}
        </STNote>
      </STPanel>
    </div>
  );
}

/* ════════════ PRICE, VOLUME & MIX ════════════ */
export function STPriceDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  var [lvl, setLvl] = useState("md");  /* price ladder level: md | mf | mol */
  var valKey = C.measure === "d" ? "d" : "l";
  var cur = C.measure === "d" ? "USD" : "LC";
  var view = useMemo(function () {
    var dec = decomposeValue(rows, C.eff, valKey, function (r) { return r.md || "—"; });
    /* price evolution: sell-in vs sell-out average price per period */
    var ser = periodSeries(rows, (mp && mp._stExtra && mp._stExtra.monthly) || [], C.eff, function (p, r) {
      var v = Number(r[valKey]) || 0, u = Number(r.u) || 0, gu = Number(r.gu) || 0;
      if (r.fl === "Sell-In") { p.inV = (p.inV || 0) + v; p.inU = (p.inU || 0) + u; p.inG = (p.inG || 0) + gu; }
      else { p.outV = (p.outV || 0) + v; p.outU = (p.outU || 0) + u; }
    });
    var trend = ser.rows.slice(-12).map(function (p) {
      return { name: p.name,
        inP: (p.inU || 0) > 0 ? +((p.inV || 0) / p.inU).toFixed(2) : null,
        outP: (p.outU || 0) > 0 ? +((p.outV || 0) / p.outU).toFixed(2) : null,
        grossP: (p.inG || 0) > 0 ? +((p.inV || 0) / p.inG).toFixed(2) : null };
    });
    /* price ladder at the chosen level (sell-out) */
    var lad = {};
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-Out" || !C.eff.dimOk(r) || !C.eff.inCur(r)) return;
      var k = (lvl === "md" ? r.md : lvl === "mf" ? r.mf : r.mol) || "—";
      var x = lad[k]; if (!x) x = lad[k] = { name: k, v: 0, u: 0, vB: 0, uB: 0 };
      x.v += Number(r[valKey]) || 0; x.u += Number(r.u) || 0;
    });
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-Out" || !C.eff.dimOk(r) || !C.eff.inBase(r)) return;
      var k = (lvl === "md" ? r.md : lvl === "mf" ? r.mf : r.mol) || "—";
      var x = lad[k]; if (!x) return;
      x.vB += Number(r[valKey]) || 0; x.uB += Number(r.u) || 0;
    });
    var mktP = dec.priceCur || 0;
    var ladder = Object.keys(lad).map(function (k) {
      var x = lad[k];
      var p = x.u > 0 ? x.v / x.u : null, pB = x.uB > 0 ? x.vB / x.uB : null;
      return { name: k, price: p, priceBase: pB, idx: (p && mktP > 0) ? p / mktP * 100 : null,
        dPrice: (p != null && pB != null && pB > 0) ? (p - pB) / pB * 100 : null, val: x.v, units: x.u };
    }).filter(function (x) { return x.price != null; }).sort(function (a, b) { return b.price - a.price; });
    /* segment price comparison (Local vs Imported) */
    var segs = {};
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-Out" || !C.eff.dimOk(r) || !C.eff.inCur(r) || !r.li) return;
      var x = segs[r.li]; if (!x) x = segs[r.li] = { name: r.li, v: 0, u: 0 };
      x.v += Number(r[valKey]) || 0; x.u += Number(r.u) || 0;
    });
    var segList = Object.keys(segs).map(function (k) { var x = segs[k]; return { name: k, price: x.u > 0 ? x.v / x.u : 0, val: x.v }; }).sort(function (a, b) { return b.price - a.price; });
    /* FOC effect: paid price vs gross (realised) price on sell-in */
    var inV = 0, inU = 0, inG = 0;
    (rows || []).forEach(function (r) { if (r.fl !== "Sell-In" || !C.eff.dimOk(r) || !C.eff.inCur(r)) return; inV += Number(r[valKey]) || 0; inU += Number(r.u) || 0; inG += Number(r.gu) || 0; });
    var paidP = inU > 0 ? inV / inU : null, realP = inG > 0 ? inV / inG : null;
    return { dec: dec, trend: trend, ladder: ladder, segList: segList, paidP: paidP, realP: realP, monthly: ser.monthly };
  }, [rows, mp, C.eff, C.measure, lvl, valKey]);
  var mxIdx = 1; view.ladder.forEach(function (x) { if (x.idx) mxIdx = Math.max(mxIdx, x.idx); });
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="💲" title="Price, Volume & Mix" subtitle={"Average price, evolution and growth decomposition · " + C.eff.curLbl + " vs " + C.eff.baseLbl} />
      <STPanelNote mp={mp} />
      <STTotals items={[
        { label: "Average price — " + C.eff.curLbl, value: view.dec.priceCur == null ? "—" : fmtM(view.dec.priceCur), icon: "💲", grad: G_NAVY,
          sub: cur + " per unit (sell-out) · was " + (view.dec.priceBase == null ? "—" : fmtM(view.dec.priceBase)) },
        { label: "Price change", value: (view.dec.priceCur != null && view.dec.priceBase) ? ((view.dec.priceCur - view.dec.priceBase) / view.dec.priceBase * 100).toFixed(1) + "%" : "—", icon: "📈", grad: G_TEAL,
          color: (view.dec.priceCur != null && view.dec.priceBase && view.dec.priceCur < view.dec.priceBase) ? C_NEG : undefined, sub: "vs " + C.eff.baseLbl },
        { label: "Value growth", value: view.dec.valGrowth == null ? "—" : view.dec.valGrowth.toFixed(1) + "%", icon: "💰", grad: G_VIOLET, sub: cur + " sell-out" },
        { label: "Unit growth", value: view.dec.unitGrowth == null ? "—" : view.dec.unitGrowth.toFixed(1) + "%", icon: "📦", grad: G_BLUE, sub: "paid units sold-out" },
        { label: "Volume effect", value: (view.dec.vol >= 0 ? "+" : "−") + fmtM(Math.abs(view.dec.vol)), icon: "🧮", grad: G_TEAL, color: view.dec.vol < 0 ? C_NEG : undefined, sub: view.dec.dVal !== 0 ? (view.dec.vol / Math.abs(view.dec.dVal) * 100).toFixed(0) + "% of the change" : "" },
        { label: "Price effect", value: (view.dec.price >= 0 ? "+" : "−") + fmtM(Math.abs(view.dec.price)), icon: "🏷️", grad: G_AMBER, color: view.dec.price < 0 ? C_NEG : undefined, sub: view.dec.dVal !== 0 ? (view.dec.price / Math.abs(view.dec.dVal) * 100).toFixed(0) + "% of the change" : "" },
        { label: "Mix / other", value: (view.dec.mix >= 0 ? "+" : "−") + fmtM(Math.abs(view.dec.mix)), icon: "🔀", grad: G_NAVY, color: view.dec.mix < 0 ? C_NEG : undefined, sub: "new / lost products & pack shift" },
        { label: "FOC effect on realised price", value: (view.paidP && view.realP) ? ((view.realP - view.paidP) / view.paidP * 100).toFixed(1) + "%" : "—", icon: "🎁", grad: G_AMBER,
          color: (view.paidP && view.realP && view.realP < view.paidP) ? C_WARN : undefined, sub: (view.paidP ? "paid " + fmtM(view.paidP) : "—") + " → realised " + (view.realP ? fmtM(view.realP) : "—") + " per gross unit" }
      ]} />
      <STRow>
        <STPanel flex="1 1 470px" title={(view.monthly ? "Monthly" : "Quarterly") + " price evolution"} sub={"average " + cur + " per unit · sell-in, sell-out and realised (after FOC)"} grad={G_BLUE}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={view.trend} margin={{ top: 12, right: 20, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 9.5 }} height={34} label={axX("Period")} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtM} width={66} label={axY("Price per unit")} />
              <Tooltip formatter={function (v) { return v == null ? "—" : fmtM(v); }} />
              <Legend verticalAlign="top" align="right" height={22} iconSize={9} wrapperStyle={{ fontSize: 10 }} />
              <Line dataKey="inP" name="Sell-in price" stroke={C_IN} strokeWidth={2.2} dot={{ r: 2.5 }} connectNulls />
              <Line dataKey="outP" name="Sell-out price" stroke={C_OUT} strokeWidth={2.2} dot={{ r: 2.5 }} connectNulls />
              <Line dataKey="grossP" name="Realised (after FOC)" stroke="#b45309" strokeWidth={1.8} strokeDasharray="5 3" dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </STPanel>
        <STPanel flex="1 1 400px" title="Price ladder" sub={"average sell-out price · index vs market (100) · " + C.eff.curLbl} grad={G_VIOLET}>
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            {[["md", "Product"], ["mf", "Manufacturer"], ["mol", "Molecule"]].map(function (o) {
              var on = lvl === o[0];
              return <div key={o[0]} onClick={function () { setLvl(o[0]); }} style={{ padding: "3px 12px", borderRadius: 14, fontSize: 10, fontWeight: on ? 800 : 600, cursor: "pointer", background: on ? "#0F5C66" : "#f1f5f9", color: on ? "#fff" : "#475569" }}>{o[1]}</div>;
            })}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>{["Name", "Avg price", "Index", "Δ price", "Units"].map(function (h, i) {
              return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>;
            })}</tr></thead>
            <tbody>
              {view.ladder.slice(0, 12).map(function (x, i) {
                return (
                  <tr key={i}>
                    <td style={{ padding: "5px 6px", fontWeight: 700, color: "#334155", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{x.name}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, color: "#0F5C66", borderBottom: "1px solid #f1f5f9" }}>{fmtM(x.price)}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: x.idx > 110 ? C_WARN : x.idx < 90 ? C_ACC : "#334155", borderBottom: "1px solid #f1f5f9" }}>{x.idx == null ? "—" : x.idx.toFixed(0)}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, color: x.dPrice == null ? GRAY : x.dPrice >= 0 ? C_POS : C_NEG, borderBottom: "1px solid #f1f5f9" }}>{x.dPrice == null ? "—" : (x.dPrice >= 0 ? "+" : "") + x.dPrice.toFixed(1) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#64748b", borderBottom: "1px solid #f1f5f9" }}>{fmtN(x.units)}</td>
                  </tr>
                );
              })}
              {view.ladder.length === 0 && <tr><td colSpan={5} style={{ padding: 12, color: "#94a3b8" }}>No price data for this selection.</td></tr>}
            </tbody>
          </table>
        </STPanel>
      </STRow>
      <STRow>
        <STPanel flex="1 1 340px" title="Segment price comparison" sub={"average sell-out price by origin segment · " + cur} grad={G_TEAL}>
          <STBars items={view.segList.map(function (s) { return { name: s.name, val: s.price, lbl: fmtM(s.price) + " · " + C.msrFmt(s.val), color: s.name === "Local" ? "#0F5C66" : "#17707C" }; })} labelW={90} valW={150} />
          {view.segList.length === 2 && (
            <STNote tag="GAP" accent={C_ACC}>
              {view.segList[0].name + " is priced " + (((view.segList[0].price - view.segList[1].price) / (view.segList[1].price || 1)) * 100).toFixed(0) + "% above " + view.segList[1].name + " per unit in " + C.eff.curLbl + "."}
            </STNote>
          )}
        </STPanel>
        <STPanel flex="1 1 520px" title="Product-level effects" sub={"value change split into volume and price · top movers · " + cur} grad={G_AMBER}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>{["Product", "Δ value", "Volume eff.", "Price eff.", "Price now", "Price base", "Units Δ"].map(function (h, i) {
              return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>;
            })}</tr></thead>
            <tbody>
              {view.dec.items.slice(0, 12).map(function (x, i) {
                return (
                  <tr key={i}>
                    <td style={{ padding: "5px 6px", fontWeight: 700, color: "#334155", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>{x.name}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: x.dVal >= 0 ? C_POS : C_NEG, borderBottom: "1px solid #f1f5f9" }}>{(x.dVal >= 0 ? "+" : "−") + fmtM(Math.abs(x.dVal))}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: x.volEff >= 0 ? C_POS : C_NEG, borderBottom: "1px solid #f1f5f9" }}>{(x.volEff >= 0 ? "+" : "−") + fmtM(Math.abs(x.volEff))}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: x.priceEff >= 0 ? C_POS : C_NEG, borderBottom: "1px solid #f1f5f9" }}>{(x.priceEff >= 0 ? "+" : "−") + fmtM(Math.abs(x.priceEff))}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{x.priceCur == null ? "—" : fmtM(x.priceCur)}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#64748b", borderBottom: "1px solid #f1f5f9" }}>{x.priceBase == null ? "—" : fmtM(x.priceBase)}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#64748b", borderBottom: "1px solid #f1f5f9" }}>{fmtN(x.uCur - x.uBase)}</td>
                  </tr>
                );
              })}
              {view.dec.items.length === 0 && <tr><td colSpan={7} style={{ padding: 12, color: "#94a3b8" }}>No product data for this selection.</td></tr>}
            </tbody>
          </table>
        </STPanel>
      </STRow>
      <STNote>Price effect isolates money made from higher prices at last period's volumes; volume effect isolates extra boxes at last period's prices; mix carries the shift between cheap and dear packs plus products that entered or left. A realised price below the paid price is the cost of FOC.</STNote>
    </div>
  );
}

/* ════════════ ORIGIN & SEGMENT INTELLIGENCE ════════════
   Local vs Imported today; the same machinery renders Generic vs Originator as
   soon as the view carries a product-type flag (mp.productType → rows.pt). */
export function STOriginDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  var hasPt = !!(mp && mp.productType);
  var [lens, setLens] = useState("li");   /* li = origin · pt = generic/originator (when available) */
  var segKey = lens === "pt" ? "pt" : "li";
  var valKey = C.measure === "d" ? "d" : "l";
  var view = useMemo(function () {
    var agg = function (extra, flow) {
      var o = {};
      (rows || []).forEach(function (r) {
        if (!C.eff.dimOk(r)) return;
        if (flow && r.fl !== flow) return;
        var seg = r[segKey]; if (!seg) return;
        var cur = C.eff.inCur(r), base = C.eff.inBase(r); if (!cur && !base) return;
        var k = seg + "||" + (extra ? extra(r) : "ALL");
        var x = o[k]; if (!x) x = o[k] = { seg: seg, key: extra ? extra(r) : "ALL", cur: 0, base: 0, u: 0, val: 0, fu: 0, gu: 0, curIn: 0 };
        var v = Number(r[C.measure]) || 0;
        if (r.fl === "Sell-Out") { if (cur) { x.cur += v; x.u += Number(r.u) || 0; x.val += Number(r[valKey]) || 0; } if (base) x.base += v; }
        else if (cur) { x.curIn += v; x.fu += Number(r.fu) || 0; x.gu += Number(r.gu) || 0; }
      });
      return Object.keys(o).map(function (k) { return o[k]; });
    };
    var totals = agg(null);
    var tot = totals.reduce(function (a, x) { return a + x.cur; }, 0);
    var segs = totals.map(function (x) {
      return { name: x.seg, cur: x.cur, base: x.base, share: pct1(x.cur, tot), growth: gpct(x.cur, x.base),
        price: x.u > 0 ? x.val / x.u : null, foc: focPct9(x.fu, x.gu), st: x.curIn > 0 ? pct1(x.cur, x.curIn) : null };
    }).sort(function (a, b) { return b.cur - a.cur; });
    /* share evolution of each segment */
    var ser = periodSeries(rows, (mp && mp._stExtra && mp._stExtra.monthly) || [], C.eff, function (p, r) {
      if (r.fl !== "Sell-Out") return;
      var v = Number(r[C.measure]) || 0; p.tot = (p.tot || 0) + v;
      var seg = r[segKey]; if (!seg) return; p.s = p.s || {}; p.s[seg] = (p.s[seg] || 0) + v;
    });
    var names = segs.map(function (s) { return s.name; });
    var trend = ser.rows.slice(-12).map(function (p) {
      var o = { name: p.name };
      names.forEach(function (n) { o[n] = p.tot > 0 ? +(pct1((p.s && p.s[n]) || 0, p.tot)).toFixed(1) : 0; });
      return o;
    });
    /* by region / by class / by molecule */
    var pivot = function (extra) {
      var rowsO = {};
      agg(extra).forEach(function (x) {
        var k = x.key || "—";
        var r0 = rowsO[k]; if (!r0) r0 = rowsO[k] = { name: k, tot: 0, seg: {} };
        r0.tot += x.cur; r0.seg[x.seg] = (r0.seg[x.seg] || 0) + x.cur;
      });
      return Object.keys(rowsO).map(function (k) { return rowsO[k]; }).sort(function (a, b) { return b.tot - a.tot; });
    };
    var byGov = pivot(function (r) { return r.gv; }).slice(0, 10);
    var byCls = pivot(function (r) { return r.phc; }).filter(function (x) { return x.name && x.name !== "—"; });
    var byMol = pivot(function (r) { return r.mol; }).slice(0, 10);
    /* manufacturer position: each maker's segment mix */
    var mkO = {};
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-Out" || !C.eff.dimOk(r) || !C.eff.inCur(r)) return;
      var x = mkO[r.mf]; if (!x) x = mkO[r.mf] = { name: r.mf, tot: 0, seg: {} };
      var v = Number(r[C.measure]) || 0; x.tot += v; if (r[segKey]) x.seg[r[segKey]] = (x.seg[r[segKey]] || 0) + v;
    });
    var makers = Object.keys(mkO).map(function (k) { return mkO[k]; }).sort(function (a, b) { return b.tot - a.tot; }).slice(0, 12);
    return { segs: segs, tot: tot, trend: trend, names: names, byGov: byGov, byCls: byCls, byMol: byMol, makers: makers, monthly: ser.monthly };
  }, [rows, mp, C.eff, C.measure, segKey, valKey]);
  /* two-segment lenses use the AMS blue/green pair, like every other paired series */
  var SEGC = { Local: "#0F5C66", Imported: "#3FA3AD", Generic: "#3FA3AD", Originator: "#0F5C66" };
  var segColor = function (n, i) { return SEGC[n] || PH_COLS[i % PH_COLS.length]; };
  var stack = function (r0) {
    return (
      <div style={{ display: "flex", height: 9, borderRadius: 5, overflow: "hidden", background: "#f1f5f9" }}>
        {view.names.map(function (n, i) {
          var w = r0.tot > 0 ? pct1(r0.seg[n] || 0, r0.tot) : 0;
          return w > 0 ? <div key={n} title={n + " " + w.toFixed(1) + "%"} style={{ width: w + "%", background: segColor(n, i) }} /> : null;
        })}
      </div>
    );
  };
  var lead = view.segs[0];
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="🌍" title="Origin & Segment Intelligence" subtitle={(lens === "pt" ? "Generic vs Originator" : "Local vs Imported") + " · share, growth, price, FOC, region & class · " + C.eff.curLbl} />
      <STPanelNote mp={mp} />
      {hasPt && (
        <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center" }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: "#0F5C66", textTransform: "uppercase", letterSpacing: ".05em" }}>Lens</span>
          {[["li", "Local vs Imported"], ["pt", "Generic vs Originator"]].map(function (o) {
            var on = lens === o[0];
            return <div key={o[0]} onClick={function () { setLens(o[0]); }} style={{ padding: "4px 12px", borderRadius: 14, fontSize: 10, fontWeight: on ? 800 : 600, cursor: "pointer", background: on ? "#0F5C66" : "#fff", color: on ? "#fff" : "#475569", border: "1.5px solid " + (on ? "#0F5C66" : "#cbd5e1") }}>{o[1]}</div>;
          })}
        </div>
      )}
      {!hasPt && (
        <STDataGap title="Generic vs Originator not available yet">
          The data carries no product-type flag, so this dashboard shows the <strong>Local vs Imported</strong> lens only. Add an Originator / Generic column to the product master (or a mapping table) and it will appear here as a second lens automatically — no code change needed.
        </STDataGap>
      )}
      <STTotals items={view.segs.slice(0, 2).map(function (s, i) {
        return { label: s.name + " share of sell-out", value: s.share.toFixed(1) + "%", icon: i === 0 ? "🥇" : "🥈", grad: i === 0 ? G_NAVY : G_BLUE,
          sub: C.msrFmt(s.cur) + " · growth " + (s.growth == null ? "—" : (s.growth >= 0 ? "+" : "") + s.growth.toFixed(0) + "%") };
      }).concat(view.segs.slice(0, 2).map(function (s) {
        return { label: s.name + " average price", value: s.price == null ? "—" : fmtM(s.price), icon: "💲", grad: G_VIOLET, sub: (C.measure === "d" ? "USD" : "LC") + " per unit · sell-out" };
      })).concat([
        { label: "Price premium", value: (view.segs.length > 1 && view.segs[0].price && view.segs[1].price) ? (((view.segs[0].price - view.segs[1].price) / view.segs[1].price) * 100).toFixed(0) + "%" : "—", icon: "⚖️", grad: G_AMBER,
          sub: view.segs.length > 1 ? view.segs[0].name + " vs " + view.segs[1].name : "" },
        { label: "FOC intensity gap", value: view.segs.length > 1 ? ((view.segs[0].foc - view.segs[1].foc >= 0 ? "+" : "") + (view.segs[0].foc - view.segs[1].foc).toFixed(1) + " pts") : "—", icon: "🎁", grad: G_TEAL,
          sub: view.segs.map(function (s) { return s.name + " " + s.foc.toFixed(1) + "%"; }).join(" · ") }
      ])} />
      <STRow>
        <STPanel flex="1 1 470px" title="Segment share evolution" sub={(view.monthly ? "monthly" : "quarterly") + " share of sell-out — the substitution trend"} grad={G_NAVY}>
          <ResponsiveContainer width="100%" height={270}>
            <LineChart data={view.trend} margin={{ top: 12, right: 20, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 9.5 }} height={34} label={axX("Period")} />
              <YAxis unit="%" tick={{ fontSize: 10 }} width={64} label={axY("Share of sell-out (%)")} />
              <Tooltip formatter={function (v) { return v + "%"; }} />
              <Legend verticalAlign="top" align="right" height={22} iconSize={9} wrapperStyle={{ fontSize: 10 }} />
              {view.names.map(function (n, i) { return <Line key={n} dataKey={n} stroke={segColor(n, i)} strokeWidth={2.4} dot={{ r: 3 }} />; })}
            </LineChart>
          </ResponsiveContainer>
        </STPanel>
        <STPanel flex="1 1 400px" title="Segment scorecard" sub={"sell-out, share, growth, sell-through, price and FOC · " + C.eff.curLbl} grad={G_TEAL}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>{["Segment", "Sell-out", "Share", "Growth", "Sell-through", "Avg price", "FOC %"].map(function (h, i) {
              return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>;
            })}</tr></thead>
            <tbody>
              {view.segs.map(function (s, i) {
                return (
                  <tr key={i}>
                    <td style={{ padding: "6px", fontWeight: 800, color: segColor(s.name, i), borderBottom: "1px solid #f1f5f9" }}>{s.name}</td>
                    <td style={{ padding: "6px", textAlign: "right", fontWeight: 700, color: "#0F5C66", borderBottom: "1px solid #f1f5f9" }}>{C.msrFmt(s.cur)}</td>
                    <td style={{ padding: "6px", textAlign: "right", fontWeight: 800, borderBottom: "1px solid #f1f5f9" }}>{s.share.toFixed(1) + "%"}</td>
                    <td style={{ padding: "6px", textAlign: "right", fontWeight: 700, color: s.growth == null ? GRAY : s.growth >= 0 ? C_POS : C_NEG, borderBottom: "1px solid #f1f5f9" }}>{s.growth == null ? "—" : (s.growth >= 0 ? "+" : "") + s.growth.toFixed(0) + "%"}</td>
                    <td style={{ padding: "6px", textAlign: "right", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{s.st == null ? "—" : s.st.toFixed(0) + "%"}</td>
                    <td style={{ padding: "6px", textAlign: "right", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{s.price == null ? "—" : fmtM(s.price)}</td>
                    <td style={{ padding: "6px", textAlign: "right", color: s.foc > 0 ? C_WARN : GRAY, borderBottom: "1px solid #f1f5f9" }}>{s.foc.toFixed(1) + "%"}</td>
                  </tr>
                );
              })}
              {view.segs.length === 0 && <tr><td colSpan={7} style={{ padding: 12, color: "#94a3b8" }}>No segment data for this selection.</td></tr>}
            </tbody>
          </table>
        </STPanel>
      </STRow>
      <STRow>
        <STPanel flex="1 1 320px" title="Segment mix by governorate" sub="share of each governorate's sell-out" grad={G_BLUE}>
          {view.byGov.map(function (g, i) {
            return (
              <div key={i} style={{ marginBottom: 7 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
                  <span style={{ fontWeight: 700, color: "#475569" }}>{g.name}</span>
                  <span style={{ color: "#94a3b8" }}>{C.msrFmt(g.tot)}</span>
                </div>
                {stack(g)}
              </div>
            );
          })}
          {view.byGov.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No data.</div>}
        </STPanel>
        <STPanel flex="1 1 300px" title="Segment mix by pharmacy class" sub="who buys which origin" grad={G_TEAL}>
          {view.byCls.map(function (g, i) {
            return (
              <div key={i} style={{ marginBottom: 7 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
                  <span style={{ fontWeight: 700, color: "#475569" }}>{"Class " + g.name}</span>
                  <span style={{ color: "#94a3b8" }}>{C.msrFmt(g.tot)}</span>
                </div>
                {stack(g)}
              </div>
            );
          })}
          {view.byCls.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>Pharmacy class not available in this data.</div>}
        </STPanel>
        <STPanel flex="1 1 320px" title="Molecule-level competition" sub="segment split inside each molecule — where substitution happens" grad={G_VIOLET}>
          {view.byMol.map(function (g, i) {
            return (
              <div key={i} style={{ marginBottom: 7 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
                  <span style={{ fontWeight: 700, color: "#475569" }}>{g.name}</span>
                  <span style={{ color: "#94a3b8" }}>{C.msrFmt(g.tot)}</span>
                </div>
                {stack(g)}
              </div>
            );
          })}
          {view.byMol.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No data.</div>}
        </STPanel>
      </STRow>
      <STPanel title="Manufacturer positioning by segment" sub="bar length = sell-out size · fill = origin mix" grad={G_NAVY}>
        {view.makers.map(function (m, i) {
          /* length encodes size, fill encodes mix. A full-width bar per maker told
             the reader nothing, because each maker's mix always sums to 100%. */
          var mxT = view.makers[0] ? view.makers[0].tot : 1;
          var wpc = mxT > 0 ? Math.max(2, m.tot / mxT * 100) : 2;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 5 }}>
              <div style={{ width: 150, fontSize: 10.5, fontWeight: 700, color: "#334155", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name}</div>
              <div style={{ flex: 1 }}><div style={{ width: wpc + "%" }}>{stack(m)}</div></div>
              <div style={{ width: 90, fontSize: 9.5, color: "#64748b", textAlign: "right" }}>{C.msrFmt(m.tot)}</div>
              <div style={{ width: 120, fontSize: 9.5, fontWeight: 700, color: "#475569", whiteSpace: "nowrap" }}>
                {view.names.map(function (n) { return m.tot > 0 && m.seg[n] ? n.charAt(0) + " " + pct1(m.seg[n], m.tot).toFixed(0) + "%" : null; }).filter(Boolean).join(" · ")}
              </div>
            </div>
          );
        })}
        {view.makers.length === 0 && <div style={{ fontSize: 10.5, color: "#94a3b8" }}>No data.</div>}
      </STPanel>
      <STNote>
        {lead ? (lead.name + " leads with " + lead.share.toFixed(1) + "% of sell-out" + (lead.growth != null ? " and " + (lead.growth >= 0 ? "grew " : "declined ") + Math.abs(lead.growth).toFixed(0) + "%" : "") + " in " + C.eff.curLbl + ". " +
          (view.segs.length > 1 && view.segs[0].price && view.segs[1].price ? "The price gap between " + view.segs[0].name + " and " + view.segs[1].name + " is " + (((view.segs[0].price - view.segs[1].price) / view.segs[1].price) * 100).toFixed(0) + "% per unit — read it together with the molecule panel, where substitution actually happens." : "")) : "No segment data for this selection."}
      </STNote>
    </div>
  );
}

/* ════════════ DISTRIBUTION & COMMERCIAL OPPORTUNITY ════════════ */
export function STDistDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var D = useMemo(function () { return digestMakers(rows, C.eff, C.measure); }, [rows, C.eff, C.measure]);
  var [mkSel, setMkSel] = useState("");
  var [lvl, setLvl] = useState("md");    /* distribution level: md (product) | mf (maker) */
  var focus = (mkSel && D.list.some(function (m) { return m.name === mkSel; })) ? mkSel : (D.list[0] ? D.list[0].name : "");
  var diag = (mp && mp._stDiag) || null;
  var OG = useMemo(function () { return stOutlet(mp, rows); }, [mp, rows]);
  var view = useMemo(function () {
    var ph = OG.rows;
    /* panel: outlets that dispensed anything in the filtered set */
    var panel = {}, panelVal = {}, byKey = {}, byMkGov = {}, clsPanel = {}, clsFocus = {};
    ph.forEach(function (p) {
      if (p.fl !== "Sell-Out" || !C.eff.dimOk(p)) return;
      if (OG.perPeriod && !C.eff.inCur(p)) return;
      var oid = OG.key(p); if (!oid) return;
      var v = Number(p[C.measure]) || 0;
      panel[oid] = 1; panelVal[oid] = (panelVal[oid] || 0) + v;
      var cls = p.phc || (OG.real ? "" : p.gv);
      if (cls) { (clsPanel[cls] = clsPanel[cls] || {})[oid] = 1; }
      var k = (lvl === "md" ? p.md : p.mf) || "—";
      var x = byKey[k]; if (!x) x = byKey[k] = { name: k, doors: {}, val: 0, maker: p.mf };
      x.doors[oid] = 1; x.val += v;
      if (p.mf === focus) {
        if (cls) (clsFocus[cls] = clsFocus[cls] || {})[oid] = 1;
        if (p.gv) { var g = byMkGov[p.gv] || (byMkGov[p.gv] = { doors: {}, val: 0 }); g.doors[oid] = 1; g.val += v; }
      }
    });
    var panelN = Object.keys(panel).length;
    var panelValTot = Object.keys(panelVal).reduce(function (a, k) { return a + panelVal[k]; }, 0);
    /* demand per key from the main rows (current window) */
    var demand = {};
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-Out" || !C.eff.dimOk(r) || !C.eff.inCur(r)) return;
      var k = (lvl === "md" ? r.md : r.mf) || "—";
      demand[k] = (demand[k] || 0) + (Number(r[C.measure]) || 0);
    });
    var demTot = Object.keys(demand).reduce(function (a, k) { return a + demand[k]; }, 0);
    var list = Object.keys(byKey).map(function (k) {
      var x = byKey[k];
      var doors = Object.keys(x.doors);
      var wVal = doors.reduce(function (a, d) { return a + (panelVal[d] || 0); }, 0);
      return { name: k, maker: x.maker, doors: doors.length, numeric: panelN > 0 ? pct1(doors.length, panelN) : null,
        weighted: panelValTot > 0 ? pct1(wVal, panelValTot) : null, val: demand[k] || x.val,
        share: demTot > 0 ? pct1(demand[k] || 0, demTot) : 0, perDoor: doors.length > 0 ? (demand[k] || x.val) / doors.length : null };
    }).sort(function (a, b) { return b.val - a.val; });
    /* white spaces: governorate × (product|maker) with demand in the governorate but the item absent */
    var govTot = {}, govItem = {};
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-Out" || !C.eff.dimOk(r) || !C.eff.inCur(r) || !r.gv) return;
      var v = Number(r[C.measure]) || 0;
      govTot[r.gv] = (govTot[r.gv] || 0) + v;
      var k = (lvl === "md" ? r.md : r.mf) || "—";
      (govItem[k] = govItem[k] || {})[r.gv] = (govItem[k][r.gv] || 0) + v;
    });
    var top = list.slice(0, 8);
    var white = [];
    top.forEach(function (it) {
      Object.keys(govTot).forEach(function (g) {
        var has = govItem[it.name] && govItem[it.name][g];
        if (!has && govTot[g] > 0) white.push({ item: it.name, gov: g, size: govTot[g] });
      });
    });
    white.sort(function (a, b) { return b.size - a.size; });
    var clsRows = Object.keys(clsPanel).sort().map(function (c) {
      var pn = Object.keys(clsPanel[c]).length, fn = clsFocus[c] ? Object.keys(clsFocus[c]).length : 0;
      return { cls: c, panel: pn, focus: fn, pen: pn > 0 ? pct1(fn, pn) : 0, gap: pn - fn };
    });
    var fRow = list.filter(function (x) { return lvl === "mf" ? x.name === focus : x.maker === focus; });
    var fDoors = {}; ph.forEach(function (p) { if (p.fl === "Sell-Out" && C.eff.dimOk(p) && p.mf === focus && !(OG.perPeriod && !C.eff.inCur(p))) { var fk = OG.key(p); if (fk) fDoors[fk] = 1; } });
    var fDoorsN = Object.keys(fDoors).length;
    var fWVal = Object.keys(fDoors).reduce(function (a, d) { return a + (panelVal[d] || 0); }, 0);
    return { panelN: panelN, panelValTot: panelValTot, list: list, white: white.slice(0, 12), clsRows: clsRows,
      focusDoors: fDoorsN, focusNumeric: panelN > 0 ? pct1(fDoorsN, panelN) : null, focusWeighted: panelValTot > 0 ? pct1(fWVal, panelValTot) : null,
      focusItems: fRow.length, demTot: demTot };
  }, [rows, mp, OG, C.eff, C.measure, lvl, focus]);
  var noPh = view.panelN === 0;
  var fMk = D.list.filter(function (m) { return m.name === focus; })[0] || null;
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="🧭" title="Distribution & Commercial Opportunity" subtitle={"Numeric & weighted distribution, penetration, white spaces · " + C.eff.curLbl} />
      <STPanelNote mp={mp} />
      {!OG.real && (
        <STDataGap diag={diag} title={"Distribution measured at " + OG.unit + " level"}>
          Numeric and weighted distribution count outlets, which needs a pharmacy identity in <code>vw_SalesTracker</code>. {OG.src === "main-rows"
            ? <span>The pharmacy-grain aggregate did not reach the browser, so distribution is computed from the main aggregate against <strong>{OG.unit}</strong>.</span>
            : <span>None is populated, so distribution is measured against <strong>{OG.unit}</strong> instead of pharmacies.</span>} The ranking and the weighted-minus-numeric gap still read correctly, but the percentages are coverage of {OG.unit}, not of the pharmacy panel. Add the pharmacy id to the view (<strong>sales_tracker_usd_v4.sql, Part B</strong>) or send me the column name from the list below and true numeric distribution switches on with no other change. The white-space panel is unaffected — it is built from governorate coverage.
        </STDataGap>
      )}
      {OG.real && noPh && (
        <STDataGap diag={diag}>
          The pharmacy aggregate returned no sell-out rows for the current filters, so distribution cannot be computed. Widen the filters (product / molecule / ATC / segment) or check the diagnostic line below.
        </STDataGap>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <STMakerPicker label="Focus manufacturer" value={focus} onChange={setMkSel}
          items={D.list.map(function (m, i) { return { name: m.name, rank: i + 1, sub: pct1(m.curOut, D.tot.curOut).toFixed(1) + "%" }; })} />
        <div style={{ width: 1, height: 20, background: "#e2e8f0" }} />
        <span style={{ fontSize: 9, fontWeight: 800, color: "#0F5C66", textTransform: "uppercase", letterSpacing: ".05em" }}>Distribution level</span>
        {[["md", "Product"], ["mf", "Manufacturer"]].map(function (o) {
          var on = lvl === o[0];
          return <div key={o[0]} onClick={function () { setLvl(o[0]); }} style={{ padding: "4px 12px", borderRadius: 14, fontSize: 10, fontWeight: on ? 800 : 600, cursor: "pointer", background: on ? "#0F5C66" : "#fff", color: on ? "#fff" : "#475569", border: "1.5px solid " + (on ? "#0F5C66" : "#cbd5e1") }}>{o[1]}</div>;
        })}
      </div>
      <STTotals items={[
        { label: OG.panelLbl, value: view.panelN, icon: "🏪", grad: G_NAVY, sub: "dispensing the filtered set" },
        { label: focus + " numeric distribution", value: view.focusNumeric == null ? "—" : view.focusNumeric.toFixed(0) + "%", icon: "🚪", grad: G_TEAL, sub: view.focusDoors + " of " + view.panelN + " " + OG.unit + " stock it" },
        { label: focus + " weighted distribution", value: view.focusWeighted == null ? "—" : view.focusWeighted.toFixed(0) + "%", icon: "⚖️", grad: G_BLUE, sub: "% of panel sell-out value in stocking " + OG.unit },
        { label: "Sell-out per stocking " + OG.unitS, value: (fMk && view.focusDoors > 0) ? C.msrFmt(fMk.curOut / view.focusDoors) : "—", icon: "💰", grad: G_VIOLET, sub: C.msrLbl + " · depth, not reach" },
        { label: "Distribution gap", value: (view.focusNumeric != null && view.focusWeighted != null) ? ((view.focusWeighted - view.focusNumeric >= 0 ? "+" : "") + (view.focusWeighted - view.focusNumeric).toFixed(0) + " pts") : "—", icon: "🎯", grad: G_AMBER,
          sub: "weighted − numeric · positive = present in the big " + OG.doors },
        { label: "White spaces", value: view.white.length, icon: "🗺️", grad: G_AMBER, sub: "governorate × item cells with demand but no presence" }
      ]} />
      <STRow>
        <STPanel flex="1 1 560px" title={"Distribution table by " + (lvl === "md" ? "product" : "manufacturer")} sub={OG.doors + ", numeric %, weighted %, sell-out, share and per-" + OG.doorS + " yield"} grad={G_NAVY}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>{[(lvl === "md" ? "Product" : "Manufacturer"), OG.doors.charAt(0).toUpperCase() + OG.doors.slice(1), "Numeric", "Weighted", "Sell-out", "Share", "Per " + OG.doorS].map(function (h, i) {
              return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>;
            })}</tr></thead>
            <tbody>
              {view.list.slice(0, 14).map(function (x, i) {
                var isF = lvl === "mf" ? x.name === focus : x.maker === focus;
                var bg = isF ? "#E2F1F3" : undefined;
                return (
                  <tr key={i}>
                    <td style={{ padding: "5px 6px", fontWeight: isF ? 800 : 700, color: isF ? "#0F5C66" : "#334155", background: bg, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis" }}>{x.name}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", background: bg, borderBottom: "1px solid #f1f5f9" }}>{x.doors}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, background: bg, borderBottom: "1px solid #f1f5f9" }}>{x.numeric == null ? "—" : x.numeric.toFixed(0) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, color: "#17707C", background: bg, borderBottom: "1px solid #f1f5f9" }}>{x.weighted == null ? "—" : x.weighted.toFixed(0) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#0F5C66", fontWeight: 700, background: bg, borderBottom: "1px solid #f1f5f9" }}>{C.msrFmt(x.val)}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#64748b", background: bg, borderBottom: "1px solid #f1f5f9" }}>{x.share.toFixed(1) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#334155", background: bg, borderBottom: "1px solid #f1f5f9" }}>{x.perDoor == null ? "—" : C.msrFmt(x.perDoor)}</td>
                  </tr>
                );
              })}
              {view.list.length === 0 && <tr><td colSpan={7} style={{ padding: 12, color: "#94a3b8" }}>{noPh ? "The outlet aggregate is empty for these filters (see the note above)." : "No data for this selection."}</td></tr>}
            </tbody>
          </table>
        </STPanel>
        <STPanel flex="1 1 380px" title="Distribution vs demand" sub={"x = numeric distribution % · y = share of sell-out · top-left = high demand, low distribution (" + OG.unit + ")"} grad={G_TEAL}>
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ top: 18, right: 24, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis type="number" dataKey="x" name="distribution" unit="%" domain={AX_NUM} tick={{ fontSize: 10 }} height={36} label={axX("Numeric distribution (%)")} />
              <YAxis type="number" dataKey="y" name="share" unit="%" domain={AX_NUM} tick={{ fontSize: 10 }} width={64} label={axY("Share of sell-out (%)")} />
              <ZAxis dataKey="z" range={[90, 560]} />
              <Tooltip content={function (o) {
                if (!o.active || !o.payload || !o.payload.length) return null;
                var p = o.payload[0].payload;
                return <div style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 12px", fontSize: 11 }}><b>{p.name}</b><div>{"distribution " + p.x + "% · share " + p.y + "%"}</div></div>;
              }} />
              <Scatter data={view.list.slice(0, 20).filter(function (x) { return x.numeric != null; }).map(function (x) { return { name: x.name, x: +x.numeric.toFixed(0), y: +x.share.toFixed(1), z: Math.max(x.val, 1), gap: x.share - x.numeric }; })}>
                {view.list.slice(0, 20).filter(function (x) { return x.numeric != null; }).map(function (x, i) { return <Cell key={i} fill={x.share > x.numeric ? "#d8973c" : "#6BBCC4"} fillOpacity={0.85} />; })}
                {bubbleLbl()}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          {(function () {
            var opp = view.list.filter(function (x) { return x.numeric != null && x.share > x.numeric + 2; }).slice(0, 5);
            return opp.length ? (
              <STNote tag="OPPORTUNITY" accent={C_WARN}>
                {"High demand vs low distribution: " + opp.map(function (x) { return x.name + " (" + x.share.toFixed(1) + "% share on " + x.numeric.toFixed(0) + "% of " + OG.doors + ")"; }).join(" · ") + " — each extra " + OG.doorS + " should convert at the current per-" + OG.doorS + " yield."}
              </STNote>
            ) : null;
          })()}
        </STPanel>
      </STRow>
      <STRow>
        <STPanel flex="1 1 420px" title="Regional white spaces" sub={"governorates with demand where the " + (lvl === "md" ? "product" : "manufacturer") + " has no sell-out · biggest first"} grad={G_AMBER}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>{[(lvl === "md" ? "Product" : "Manufacturer"), "Governorate", "Governorate demand"].map(function (h, i) {
              return <th key={i} style={{ textAlign: i === 2 ? "right" : "left", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>;
            })}</tr></thead>
            <tbody>
              {view.white.map(function (w, i) {
                return (
                  <tr key={i}>
                    <td style={{ padding: "5px 6px", fontWeight: 700, color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{w.item}</td>
                    <td style={{ padding: "5px 6px", color: "#475569", borderBottom: "1px solid #f1f5f9" }}>{w.gov}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, color: C_WARN, borderBottom: "1px solid #f1f5f9" }}>{C.msrFmt(w.size)}</td>
                  </tr>
                );
              })}
              {view.white.length === 0 && <tr><td colSpan={3} style={{ padding: 12, color: "#94a3b8" }}>No white spaces — the top items are present in every governorate with demand.</td></tr>}
            </tbody>
          </table>
        </STPanel>
        <STPanel flex="1 1 380px" title={(OG.anyCls ? "Pharmacy-class opportunity — " : (OG.unitS.charAt(0).toUpperCase() + OG.unitS.slice(1)) + " opportunity — ") + focus} sub={"penetration of the focus maker inside each " + (OG.anyCls ? "class" : OG.unitS) + " and the untapped count"} grad={G_VIOLET}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>{[(OG.anyCls ? "Class" : OG.unitS.charAt(0).toUpperCase() + OG.unitS.slice(1)), "Panel", focus, "Penetration", "Not reached"].map(function (h, i) {
              return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>;
            })}</tr></thead>
            <tbody>
              {view.clsRows.map(function (c, i) {
                return (
                  <tr key={i}>
                    <td style={{ padding: "6px", fontWeight: 800, color: "#0F5C66", borderBottom: "1px solid #f1f5f9" }}>{c.cls}</td>
                    <td style={{ padding: "6px", textAlign: "right", borderBottom: "1px solid #f1f5f9" }}>{c.panel}</td>
                    <td style={{ padding: "6px", textAlign: "right", fontWeight: 700, color: "#0F5C66", borderBottom: "1px solid #f1f5f9" }}>{c.focus}</td>
                    <td style={{ padding: "6px", textAlign: "right", fontWeight: 800, color: c.pen >= 60 ? C_POS : c.pen >= 30 ? C_WARN : C_NEG, borderBottom: "1px solid #f1f5f9" }}>{c.pen.toFixed(0) + "%"}</td>
                    <td style={{ padding: "6px", textAlign: "right", fontWeight: 700, color: C_WARN, borderBottom: "1px solid #f1f5f9" }}>{c.gap}</td>
                  </tr>
                );
              })}
              {view.clsRows.length === 0 && <tr><td colSpan={5} style={{ padding: 12, color: "#94a3b8" }}>Pharmacy class not available in this data.</td></tr>}
            </tbody>
          </table>
        </STPanel>
      </STRow>
      <STNote>Numeric distribution counts doors; weighted distribution counts the value behind those doors. A weighted figure well above numeric means the item sits in the big pharmacies — a smaller door count than it looks. The reverse means presence in small doors and an unexploited opportunity in the large ones.</STNote>
    </div>
  );
}

/* ════════════ FOC EFFECT ON SHARE (st_foc_effect) ════════════
   Question (Noura 2026-09-17): inside ONE therapeutic battleground (ATC3, ATC4
   or molecule), how much of each competitor's market share is created by FOC?
   Gross share (paid + free units) vs paid-only share, per competitor — the gap
   is the share bought with bonus. FOC lives on the SELL-IN flow. */
export function STFocEffectDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var [grain, setGrain] = useState("a3");      /* a3 | a4 | mol | md (product) */
  var [gVal, setGVal] = useState("");
  var [pLvl, setPLvl] = useState("mol");       /* product mode: competition level (mol | a3 | a4) */
  var [compBy, setCompBy] = useState("mf");    /* competitor grain: mf | md (product) | sku */
  var pill = function (on) {
    return { padding: "4px 11px", borderRadius: 14, fontSize: 10, fontWeight: on ? 800 : 600, cursor: "pointer", userSelect: "none",
      background: on ? "#0F5C66" : "#fff", color: on ? "#fff" : "#475569", border: "1.5px solid " + (on ? "#0F5C66" : "#cbd5e1") };
  };
  var gOpts = useMemo(function () {
    var o = {};
    (rows || []).forEach(function (r) { if (r.fl !== "Sell-In" || !C.eff.dimOk(r) || !C.eff.inCur(r)) return; var v = r[grain]; if (v) o[v] = (o[v] || 0) + (Number(r.gu) || 0); });
    return Object.keys(o).sort(function (a, b) { return o[b] - o[a]; });
  }, [rows, C.eff, grain]);
  useEffect(function () { if (gOpts.length && gOpts.indexOf(gVal) < 0) setGVal(gOpts[0]); }, [grain, gOpts.join("|")]);
  var GRAIN_LBL = { a3: "ATC3", a4: "ATC4", mol: "Molecule", md: "Product" };
  /* product mode (Noura 2026-10-04): the picked product's dominant molecule /
     ATC3 / ATC4 defines its battleground; competitors re-aggregate on the
     chosen grain (manufacturer / product / SKU) */
  var pAnchor = useMemo(function () {
    if (grain !== "md" || !gVal) return "";
    var o = {};
    (rows || []).forEach(function (r) {
      if (!C.eff.dimOk(r) || String(r.md || "") !== gVal) return;
      var v = r[pLvl]; if (v) o[v] = (o[v] || 0) + (Number(r.u) || 0) + 1;
    });
    var best = "", bv = -1;
    Object.keys(o).forEach(function (k) { if (o[k] > bv) { bv = o[k]; best = k; } });
    return best;
  }, [rows, C.eff, grain, gVal, pLvl]);
  var kOf = function (r) { return compBy === "mf" ? r.mf : compBy === "md" ? (r.md || r.mf) : (r.md ? r.md + (r.sku ? " \u00b7 " + r.sku : "") : (r.sku || r.mf)); };
  var isFoc9 = function (n) { return !!(grain === "md" && gVal && String(n || "").indexOf(gVal) === 0); };

  var V = useMemo(function () {
    var inBg = function (r) {
      if (!C.eff.dimOk(r)) return false;
      if (grain === "md") {
        /* product mode: the battleground is every competitor sharing the
           picked product's molecule / ATC3 / ATC4 (fallback: the product) */
        if (!gVal) return true;
        return pAnchor ? String(r[pLvl] || "") === pAnchor : String(r.md || "") === gVal;
      }
      return !gVal || String(r[grain] || "") === gVal;
    };
    var mk = {}, tot = { fu: 0, gu: 0, u: 0, bfu: 0, bgu: 0, bu: 0 };
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-In" || !inBg(r)) return;
      var cur = C.eff.inCur(r), base = C.eff.inBase(r); if (!cur && !base) return;
      var k9 = kOf(r) || "\u2014"; var m = mk[k9]; if (!m) m = mk[k9] = { name: k9, fu: 0, gu: 0, u: 0, bfu: 0, bgu: 0, bu: 0 };
      if (cur) { m.fu += Number(r.fu) || 0; m.gu += Number(r.gu) || 0; m.u += Number(r.u) || 0; tot.fu += Number(r.fu) || 0; tot.gu += Number(r.gu) || 0; tot.u += Number(r.u) || 0; }
      if (base) { m.bfu += Number(r.fu) || 0; m.bgu += Number(r.gu) || 0; m.bu += Number(r.u) || 0; tot.bfu += Number(r.fu) || 0; tot.bgu += Number(r.gu) || 0; tot.bu += Number(r.u) || 0; }
    });
    var list = Object.keys(mk).map(function (k) {
      var m = mk[k];
      m.grossSh = pct1(m.gu, tot.gu); m.paidSh = pct1(m.u, tot.u);
      m.dSh = m.grossSh - m.paidSh;                 /* share points created by FOC */
      m.focPct = focPct9(m.fu, m.gu); m.focPctB = focPct9(m.bfu, m.bgu);
      m.bGrossSh = pct1(m.bgu, tot.bgu);
      return m;
    }).filter(function (m) { return m.gu > 0 || m.bgu > 0; }).sort(function (a, b) { return b.gu - a.gu; });
    var leadGross = list.slice().sort(function (a, b) { return b.grossSh - a.grossSh; })[0] || null;
    var leadPaid = list.slice().sort(function (a, b) { return b.paidSh - a.paidSh; })[0] || null;
    var biggest = list.slice(0, 12).sort(function (a, b) { return b.dSh - a.dSh; })[0] || null;
    /* quarterly FOC % lines for the top 5 makers inside the battleground */
    var top5 = list.slice(0, 5).map(function (m) { return m.name; });
    var perQ = {};
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-In" || !inBg(r) || !r.yr || !r.qr) return;
      var kT9 = kOf(r) || "\u2014";
      if (top5.indexOf(kT9) < 0) return;
      var qk = r.yr * 10 + r.qr;
      var q = perQ[qk]; if (!q) q = perQ[qk] = { qk: qk, name: "Q" + (qk % 10) + " " + Math.floor(qk / 10) };
      q[kT9 + "_fu"] = (q[kT9 + "_fu"] || 0) + (Number(r.fu) || 0);
      q[kT9 + "_gu"] = (q[kT9 + "_gu"] || 0) + (Number(r.gu) || 0);
    });
    var trend = Object.keys(perQ).map(Number).sort(function (a, b) { return a - b; }).slice(-8).map(function (qk) {
      var q = perQ[qk], o = { name: q.name };
      top5.forEach(function (n) { o[n] = q[n + "_gu"] > 0 ? +pct1(q[n + "_fu"] || 0, q[n + "_gu"]).toFixed(1) : null; });
      return o;
    });
    return { list: list, tot: tot, mktFoc: focPct9(tot.fu, tot.gu), mktFocB: focPct9(tot.bfu, tot.bgu),
      leadGross: leadGross, leadPaid: leadPaid, biggest: biggest, top5: top5, trend: trend };
  }, [rows, C.eff, grain, gVal, pLvl, pAnchor, compBy]);

  var LN_COLS = ["#0F5C66", "#3FA3AD", "#D98E32", "#7BC6CE", "#b45309"];
  var bgLbl = grain === "md"
    ? (gVal ? gVal + " \u2014 competition in " + ({ mol: "Molecule", a3: "ATC3", a4: "ATC4" })[pLvl] + (pAnchor ? " " + pAnchor : "") : "current selection")
    : (gVal ? (GRAIN_LBL[grain] + " " + gVal) : "current selection");
  var leaderFlips = V.leadGross && V.leadPaid && V.leadGross.name !== V.leadPaid.name;
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="🎯" title="FOC Effect on Share" subtitle={"Gross vs paid share per competitor inside " + bgLbl + " · " + C.eff.curLbl} />
      <STPanelNote mp={mp} />
      <div style={Object.assign({}, CARD_ST, { padding: "10px 14px", marginBottom: 14, overflow: "visible", zIndex: 5 })}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: G_AMBER, borderRadius: "18px 18px 0 0" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: "#0F5C66", textTransform: "uppercase", letterSpacing: ".05em" }}>Battleground</span>
          {[["a3", "ATC3"], ["a4", "ATC4"], ["mol", "Molecule"], ["md", "Product"]].map(function (o) {
            return <div key={o[0]} onClick={function () { setGrain(o[0]); }} style={pill(grain === o[0])}>{o[1]}</div>;
          })}
          <select value={gVal} onChange={function (e) { setGVal(e.target.value); }}
            style={{ padding: "4px 10px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 10.5, fontWeight: 700, color: "#17707C", background: "#fff", fontFamily: "inherit", outline: "none", maxWidth: 260 }}>
            {gOpts.map(function (v) { return <option key={v} value={v}>{v}</option>; })}
            {gOpts.length === 0 && <option value="">no values</option>}
          </select>
          {grain === "md" ? <span style={{ fontSize: 9, fontWeight: 800, color: "#0F5C66", textTransform: "uppercase", letterSpacing: ".05em", marginLeft: 6 }}>Competition level</span> : null}
          {grain === "md" ? [["mol", "Molecule"], ["a3", "ATC3"], ["a4", "ATC4"]].map(function (o) {
            return <div key={"pl" + o[0]} onClick={function () { setPLvl(o[0]); }} style={pill(pLvl === o[0])}>{o[1]}</div>;
          }) : null}
          <span style={{ fontSize: 9, fontWeight: 800, color: "#0F5C66", textTransform: "uppercase", letterSpacing: ".05em", marginLeft: 6 }}>Competitors by</span>
          {[["mf", "Manufacturer"], ["md", "Product"], ["sku", "SKU"]].map(function (o) {
            return <div key={"cb" + o[0]} onClick={function () { setCompBy(o[0]); }} style={pill(compBy === o[0])}>{o[1]}</div>;
          })}
          <span style={{ marginLeft: "auto", fontSize: 9.5, color: "#64748b" }}>{V.list.length + " competitors · FOC on sell-in flow"}</span>
        </div>
      </div>
      <STTotals items={[
        { label: "FOC Intensity — " + bgLbl, value: V.mktFoc.toFixed(1) + "%", icon: "🎁", grad: G_AMBER, color: V.mktFoc - V.mktFocB > 3 ? C_WARN : undefined, sub: "FOC ÷ paid units · was " + V.mktFocB.toFixed(1) + "% in " + C.eff.baseLbl },
        { label: "FOC Units — " + C.eff.curLbl, value: fmtN(V.tot.fu), icon: "📦", grad: G_NAVY, sub: "free units inside the battleground" },
        { label: "Leader — gross vs paid", value: leaderFlips ? "FLIPS" : "SAME", icon: "🏆", grad: G_VIOLET, color: leaderFlips ? C_WARN : undefined, sub: leaderFlips ? (V.leadGross.name + " leads gross · " + V.leadPaid.name + " leads paid") : (V.leadGross ? V.leadGross.name + " leads both rankings" : "—") },
        { label: "Biggest FOC-made share", value: V.biggest ? "+" + V.biggest.dSh.toFixed(1) + " pts" : "—", icon: "🧮", grad: G_BLUE, sub: V.biggest ? V.biggest.name + ": gross " + V.biggest.grossSh.toFixed(1) + "% vs paid " + V.biggest.paidSh.toFixed(1) + "%" : "" }
      ]} />
      <STRow>
        <STPanel flex="1 1 560px" title={"Gross vs paid share — " + bgLbl} sub="gross = paid + FOC units · the gap is share created by bonus, not by demand" grad={G_AMBER}>
          {V.list.slice(0, 12).map(function (m, i) {
            var mx = Math.max(V.list[0] ? V.list[0].grossSh : 1, 1);
            return (
              <div key={i} style={{ marginBottom: 9 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 2 }}>
                  <span style={{ fontWeight: isFoc9(m.name) ? 800 : 700, color: isFoc9(m.name) ? "#0F5C66" : "#334155" }}>{(isFoc9(m.name) ? "\u2605 " : "") + m.name}</span>
                  <span style={{ color: m.dSh > 0.5 ? C_WARN : "#64748b", fontWeight: 700 }}>{"FOC " + m.focPct.toFixed(1) + "% · " + (m.dSh >= 0 ? "+" : "") + m.dSh.toFixed(1) + " pts from FOC"}</span>
                </div>
                <div style={{ background: "#eef2f5", borderRadius: 3, height: 10, marginBottom: 2 }}>
                  <div style={{ width: Math.min(100, m.grossSh / mx * 100) + "%", height: 10, borderRadius: 3, background: "#D98E32" }} title={"Gross share " + m.grossSh.toFixed(1) + "%"} />
                </div>
                <div style={{ background: "#eef2f5", borderRadius: 3, height: 10 }}>
                  <div style={{ width: Math.min(100, m.paidSh / mx * 100) + "%", height: 10, borderRadius: 3, background: "#0F5C66" }} title={"Paid share " + m.paidSh.toFixed(1) + "%"} />
                </div>
              </div>
            );
          })}
          <div style={{ display: "flex", gap: 14, fontSize: 9.5, color: "#64748b", marginTop: 6 }}>
            <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#D98E32", borderRadius: 2, marginRight: 5, verticalAlign: "middle" }} />Gross share (with FOC)</span>
            <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#0F5C66", borderRadius: 2, marginRight: 5, verticalAlign: "middle" }} />Paid share (without FOC)</span>
          </div>
        </STPanel>
        <STPanel flex="1 1 420px" title="Competitor detail" sub={"share, FOC and change vs " + C.eff.baseLbl} grad={G_NAVY}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>{["Competitor", "Gross sh.", "Paid sh.", "Δ from FOC", "FOC %", "Δ FOC vs " + C.eff.baseLbl].map(function (h, i) { return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>; })}</tr></thead>
            <tbody>
              {V.list.slice(0, 12).map(function (m, i) {
                var df = m.focPct - m.focPctB;
                return (
                  <tr key={i}>
                    <td style={{ padding: "5px 6px", fontWeight: isFoc9(m.name) ? 800 : 700, color: isFoc9(m.name) ? "#0F5C66" : "#334155", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{(isFoc9(m.name) ? "\u2605 " : "") + m.name}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: "#0F5C66", borderBottom: "1px solid #f1f5f9" }}>{m.grossSh.toFixed(1) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{m.paidSh.toFixed(1) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, color: m.dSh > 0.5 ? C_WARN : m.dSh < -0.5 ? C_POS : GRAY, borderBottom: "1px solid #f1f5f9" }}>{(m.dSh >= 0 ? "+" : "") + m.dSh.toFixed(1) + " pts"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: C_WARN, fontWeight: 700, borderBottom: "1px solid #f1f5f9" }}>{m.focPct.toFixed(1) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, color: df > 0.05 ? C_WARN : df < -0.05 ? C_POS : GRAY, borderBottom: "1px solid #f1f5f9" }}>{(df >= 0 ? "+" : "") + df.toFixed(1) + " pts"}</td>
                  </tr>
                );
              })}
              {V.list.length === 0 && <tr><td colSpan={6} style={{ padding: 12, color: "#94a3b8" }}>No sell-in data for this battleground.</td></tr>}
            </tbody>
          </table>
        </STPanel>
      </STRow>
      <STPanel title={"FOC intensity by quarter — top " + V.top5.length + " competitors in " + bgLbl} sub="who escalates the bonus war and who steps out of it" grad={G_TEAL}>
        <ResponsiveContainer width="100%" height={230}>
          <LineChart data={V.trend} margin={{ top: 12, right: 24, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} height={30} />
            <YAxis tick={{ fontSize: 10 }} width={44} unit="%" />
            <Tooltip formatter={function (v) { return v == null ? "—" : v + "%"; }} contentStyle={{ borderRadius: 8, fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {V.top5.map(function (n, i) { return <Line key={n} type="monotone" dataKey={n} stroke={LN_COLS[i % LN_COLS.length]} strokeWidth={2} dot={{ r: 2.5 }} connectNulls />; })}
          </LineChart>
        </ResponsiveContainer>
      </STPanel>
      <STNote>{"Read the twin bars first: where the amber (gross) bar runs ahead of the navy (paid) bar, that competitor's position in " + bgLbl + " is partly manufactured by free goods — the Δ-from-FOC column quantifies it in share points. " + (V.biggest && V.biggest.dSh > 0.5 ? V.biggest.name + " gains the most from FOC here (+" + V.biggest.dSh.toFixed(1) + " pts)" + (leaderFlips ? ", and the battleground leadership flips once FOC is stripped out." : ".") : "FOC currently moves shares by less than half a point in this battleground.")}</STNote>
    </div>
  );
}

/* ════════════ PRICE EFFECT ON SHARE (st_price_effect) ════════════
   Same battleground lens, sell-out flow: each competitor's average price and
   price move vs the base window, against its unit-share change — does cutting
   price buy share in this class, and who wins on price vs on demand? */
export function STPriceEffectDash({ rows, mp }) {
  var C = useSTControls(rows, mp);
  var [grain, setGrain] = useState("a3");      /* a3 | a4 | mol | md (product) */
  var [gVal, setGVal] = useState("");
  var [pLvl, setPLvl] = useState("mol");       /* product mode: competition level (mol | a3 | a4) */
  var [compBy, setCompBy] = useState("mf");    /* competitor grain: mf | md (product) | sku */
  var pill = function (on) {
    return { padding: "4px 11px", borderRadius: 14, fontSize: 10, fontWeight: on ? 800 : 600, cursor: "pointer", userSelect: "none",
      background: on ? "#0F5C66" : "#fff", color: on ? "#fff" : "#475569", border: "1.5px solid " + (on ? "#0F5C66" : "#cbd5e1") };
  };
  var valKey = C.hasUSD ? "d" : "l";
  var curLbl = C.hasUSD ? "USD" : "LC";
  var gOpts = useMemo(function () {
    var o = {};
    (rows || []).forEach(function (r) { if (r.fl !== "Sell-Out" || !C.eff.dimOk(r) || !C.eff.inCur(r)) return; var v = r[grain]; if (v) o[v] = (o[v] || 0) + (Number(r[valKey]) || 0); });
    return Object.keys(o).sort(function (a, b) { return o[b] - o[a]; });
  }, [rows, C.eff, grain, valKey]);
  useEffect(function () { if (gOpts.length && gOpts.indexOf(gVal) < 0) setGVal(gOpts[0]); }, [grain, gOpts.join("|")]);
  var GRAIN_LBL = { a3: "ATC3", a4: "ATC4", mol: "Molecule", md: "Product" };
  /* product mode (Noura 2026-10-04): the picked product's dominant molecule /
     ATC3 / ATC4 defines its battleground; competitors re-aggregate on the
     chosen grain (manufacturer / product / SKU) */
  var pAnchor = useMemo(function () {
    if (grain !== "md" || !gVal) return "";
    var o = {};
    (rows || []).forEach(function (r) {
      if (!C.eff.dimOk(r) || String(r.md || "") !== gVal) return;
      var v = r[pLvl]; if (v) o[v] = (o[v] || 0) + (Number(r.u) || 0) + 1;
    });
    var best = "", bv = -1;
    Object.keys(o).forEach(function (k) { if (o[k] > bv) { bv = o[k]; best = k; } });
    return best;
  }, [rows, C.eff, grain, gVal, pLvl]);
  var kOf = function (r) { return compBy === "mf" ? r.mf : compBy === "md" ? (r.md || r.mf) : (r.md ? r.md + (r.sku ? " \u00b7 " + r.sku : "") : (r.sku || r.mf)); };
  var isFoc9 = function (n) { return !!(grain === "md" && gVal && String(n || "").indexOf(gVal) === 0); };

  var V = useMemo(function () {
    var inBg = function (r) {
      if (!C.eff.dimOk(r)) return false;
      if (grain === "md") {
        /* product mode: the battleground is every competitor sharing the
           picked product's molecule / ATC3 / ATC4 (fallback: the product) */
        if (!gVal) return true;
        return pAnchor ? String(r[pLvl] || "") === pAnchor : String(r.md || "") === gVal;
      }
      return !gVal || String(r[grain] || "") === gVal;
    };
    var mk = {}, tot = { v: 0, u: 0, bv: 0, bu: 0 };
    (rows || []).forEach(function (r) {
      if (r.fl !== "Sell-Out" || !inBg(r)) return;
      var cur = C.eff.inCur(r), base = C.eff.inBase(r); if (!cur && !base) return;
      var k9 = kOf(r) || "\u2014"; var m = mk[k9]; if (!m) m = mk[k9] = { name: k9, v: 0, u: 0, bv: 0, bu: 0 };
      if (cur) { m.v += Number(r[valKey]) || 0; m.u += Number(r.u) || 0; tot.v += Number(r[valKey]) || 0; tot.u += Number(r.u) || 0; }
      if (base) { m.bv += Number(r[valKey]) || 0; m.bu += Number(r.u) || 0; tot.bv += Number(r[valKey]) || 0; tot.bu += Number(r.u) || 0; }
    });
    var avgP = tot.u > 0 ? tot.v / tot.u : 0, avgPB = tot.bu > 0 ? tot.bv / tot.bu : 0;
    var list = Object.keys(mk).map(function (k) {
      var m = mk[k];
      m.price = m.u > 0 ? m.v / m.u : null; m.priceB = m.bu > 0 ? m.bv / m.bu : null;
      m.dPr = (m.price != null && m.priceB != null && m.priceB > 0) ? (m.price - m.priceB) / m.priceB * 100 : null;
      m.idx = (m.price != null && avgP > 0) ? m.price / avgP * 100 : null;
      m.sh = pct1(m.u, tot.u); m.bSh = pct1(m.bu, tot.bu); m.dSh = m.sh - m.bSh;
      return m;
    }).filter(function (m) { return m.u > 0 || m.bu > 0; }).sort(function (a, b) { return b.u - a.u; });
    var top = list.slice(0, 12);
    var cutters = top.filter(function (m) { return m.dPr != null && m.dPr < -1; });
    var cutWinners = cutters.filter(function (m) { return m.dSh > 0.2; });
    var raisers = top.filter(function (m) { return m.dPr != null && m.dPr > 1; });
    var raiseHolders = raisers.filter(function (m) { return m.dSh > -0.2; });
    var bestGain = top.slice().sort(function (a, b) { return b.dSh - a.dSh; })[0] || null;
    var scat = top.filter(function (m) { return m.dPr != null; }).map(function (m) {
      return { name: m.name, x: +Math.max(-60, Math.min(60, m.dPr)).toFixed(1), y: +m.dSh.toFixed(1), z: Math.max(m.v, 1) };
    });
    return { list: list, top: top, avgP: avgP, avgPB: avgPB, dAvg: avgPB > 0 ? (avgP - avgPB) / avgPB * 100 : null,
      cutters: cutters, cutWinners: cutWinners, raisers: raisers, raiseHolders: raiseHolders, bestGain: bestGain, scat: scat };
  }, [rows, C.eff, grain, gVal, valKey, pLvl, pAnchor, compBy]);

  var bgLbl = grain === "md"
    ? (gVal ? gVal + " \u2014 competition in " + ({ mol: "Molecule", a3: "ATC3", a4: "ATC4" })[pLvl] + (pAnchor ? " " + pAnchor : "") : "current selection")
    : (gVal ? (GRAIN_LBL[grain] + " " + gVal) : "current selection");
  var fmtP = function (v) { return v == null ? "—" : (curLbl === "USD" ? "$" : "") + Number(v).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 }) + (curLbl === "LC" ? " LC" : ""); };
  return (
    <div style={{ padding: 20, background: PAGE_BG, minHeight: "100%", borderRadius: 4 }}>
      <STHeader ctl={C} icon="⚖️" title="Price Effect on Share" subtitle={"Price moves vs unit-share moves per competitor inside " + bgLbl + " · " + C.eff.curLbl + " vs " + C.eff.baseLbl} />
      <STPanelNote mp={mp} />
      <div style={Object.assign({}, CARD_ST, { padding: "10px 14px", marginBottom: 14, overflow: "visible", zIndex: 5 })}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: G_BLUE, borderRadius: "18px 18px 0 0" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: "#0F5C66", textTransform: "uppercase", letterSpacing: ".05em" }}>Battleground</span>
          {[["a3", "ATC3"], ["a4", "ATC4"], ["mol", "Molecule"], ["md", "Product"]].map(function (o) {
            return <div key={o[0]} onClick={function () { setGrain(o[0]); }} style={pill(grain === o[0])}>{o[1]}</div>;
          })}
          <select value={gVal} onChange={function (e) { setGVal(e.target.value); }}
            style={{ padding: "4px 10px", borderRadius: 8, border: "1.5px solid #cbd5e1", fontSize: 10.5, fontWeight: 700, color: "#17707C", background: "#fff", fontFamily: "inherit", outline: "none", maxWidth: 260 }}>
            {gOpts.map(function (v) { return <option key={v} value={v}>{v}</option>; })}
            {gOpts.length === 0 && <option value="">no values</option>}
          </select>
          {grain === "md" ? <span style={{ fontSize: 9, fontWeight: 800, color: "#0F5C66", textTransform: "uppercase", letterSpacing: ".05em", marginLeft: 6 }}>Competition level</span> : null}
          {grain === "md" ? [["mol", "Molecule"], ["a3", "ATC3"], ["a4", "ATC4"]].map(function (o) {
            return <div key={"pl" + o[0]} onClick={function () { setPLvl(o[0]); }} style={pill(pLvl === o[0])}>{o[1]}</div>;
          }) : null}
          <span style={{ fontSize: 9, fontWeight: 800, color: "#0F5C66", textTransform: "uppercase", letterSpacing: ".05em", marginLeft: 6 }}>Competitors by</span>
          {[["mf", "Manufacturer"], ["md", "Product"], ["sku", "SKU"]].map(function (o) {
            return <div key={"cb" + o[0]} onClick={function () { setCompBy(o[0]); }} style={pill(compBy === o[0])}>{o[1]}</div>;
          })}
          <span style={{ marginLeft: "auto", fontSize: 9.5, color: "#64748b" }}>{V.list.length + " competitors · sell-out flow · prices in " + curLbl}</span>
        </div>
      </div>
      <STTotals items={[
        { label: "Average Price — " + bgLbl, value: fmtP(V.avgP), icon: "💲", grad: G_BLUE, sub: (V.dAvg != null ? (V.dAvg >= 0 ? "+" : "") + V.dAvg.toFixed(1) + "% vs " + C.eff.baseLbl : "—") + " · sell-out value ÷ units" },
        { label: "Price Cutters", value: String(V.cutters.length), icon: "✂️", grad: G_AMBER, sub: V.cutWinners.length + " of them gained unit share (top-12 competitors, cut > 1%)" },
        { label: "Price Raisers", value: String(V.raisers.length), icon: "📈", grad: G_TEAL, sub: V.raiseHolders.length + " held or grew share while raising (raise > 1%)" },
        { label: "Biggest Share Gain", value: V.bestGain ? "+" + V.bestGain.dSh.toFixed(1) + " pts" : "—", icon: "🏆", grad: G_VIOLET, sub: V.bestGain ? V.bestGain.name + " · price " + (V.bestGain.dPr == null ? "n/a" : (V.bestGain.dPr >= 0 ? "+" : "") + V.bestGain.dPr.toFixed(1) + "%") : "" }
      ]} />
      <STRow>
        <STPanel flex="1 1 520px" title={"Price vs share — " + bgLbl} sub={"price, index vs class average (100 = average), price move and unit-share move vs " + C.eff.baseLbl} grad={G_BLUE}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
            <thead><tr>{["Competitor", "Avg price", "Index", "Δ price", "Unit share", "Δ share"].map(function (h, i) { return <th key={i} style={{ textAlign: i === 0 ? "left" : "right", padding: "5px 6px", color: "#64748b", fontSize: 9, textTransform: "uppercase", borderBottom: "1.5px solid #e2e8f0" }}>{h}</th>; })}</tr></thead>
            <tbody>
              {V.top.map(function (m, i) {
                return (
                  <tr key={i}>
                    <td style={{ padding: "5px 6px", fontWeight: isFoc9(m.name) ? 800 : 700, color: isFoc9(m.name) ? "#0F5C66" : "#334155", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{(isFoc9(m.name) ? "\u2605 " : "") + m.name}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: "#0F5C66", borderBottom: "1px solid #f1f5f9" }}>{fmtP(m.price)}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, color: m.idx != null && m.idx > 115 ? C_WARN : m.idx != null && m.idx < 85 ? C_POS : "#334155", borderBottom: "1px solid #f1f5f9" }}>{m.idx != null ? m.idx.toFixed(0) : "—"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700, color: m.dPr != null && m.dPr < -1 ? C_POS : m.dPr != null && m.dPr > 1 ? C_WARN : GRAY, borderBottom: "1px solid #f1f5f9" }}>{m.dPr != null ? (m.dPr >= 0 ? "+" : "") + m.dPr.toFixed(1) + "%" : "—"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", color: "#334155", borderBottom: "1px solid #f1f5f9" }}>{m.sh.toFixed(1) + "%"}</td>
                    <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 800, color: m.dSh > 0.2 ? C_POS : m.dSh < -0.2 ? C_NEG : GRAY, borderBottom: "1px solid #f1f5f9" }}>{(m.dSh >= 0 ? "+" : "") + m.dSh.toFixed(1) + " pts"}</td>
                  </tr>
                );
              })}
              {V.top.length === 0 && <tr><td colSpan={6} style={{ padding: 12, color: "#94a3b8" }}>No sell-out data for this battleground.</td></tr>}
            </tbody>
          </table>
        </STPanel>
        <STPanel flex="1 1 420px" title="Does price buy share here?" sub={"x = price move vs " + C.eff.baseLbl + " · y = unit-share move (pts) · bubble = sell-out value"} grad={G_VIOLET}>
          <ResponsiveContainer width="100%" height={260}>
            <ScatterChart margin={{ top: 18, right: 24, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis type="number" dataKey="x" name="price move" unit="%" domain={AX_NUM} tick={{ fontSize: 10 }} height={36} label={axX("Price change (%)")} />
              <YAxis type="number" dataKey="y" name="share move" domain={AX_NUM} tick={{ fontSize: 10 }} width={54} label={axY("Share change (pts)")} />
              <ZAxis dataKey="z" range={[80, 600]} />
              <Tooltip content={function (o) { if (!o.active || !o.payload || !o.payload.length) return null; var p = o.payload[0].payload; return <div style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 12px", fontSize: 11 }}><b>{p.name}</b><div>{"price " + (p.x >= 0 ? "+" : "") + p.x + "% · share " + (p.y >= 0 ? "+" : "") + p.y + " pts"}</div></div>; }} />
              <ReferenceLine y={0} stroke="#475569" />
              <ReferenceLine x={0} stroke="#475569" />
              <Scatter data={V.scat}>{V.scat.map(function (b, i) { return <Cell key={i} fill={b.y >= 0 ? "#0F5C66" : "#dc2626"} fillOpacity={0.85} />; })}{bubbleLbl()}</Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 9.5, color: "#64748b", marginTop: 4 }}>Bottom-left = cut price AND lost share (price is not the problem) · top-left = price cut bought share · top-right = pricing power.</div>
        </STPanel>
      </STRow>
      <STPanel title={"Price ladder — " + bgLbl} sub="average sell-out price per competitor · amber = priced above the class average" grad={G_TEAL}>
        <STBars items={V.top.filter(function (m) { return m.price != null; }).slice().sort(function (a, b) { return b.price - a.price; }).map(function (m) {
          return { name: m.name, val: m.price, lbl: fmtP(m.price) + " · index " + (m.idx != null ? m.idx.toFixed(0) : "—") + " · " + m.sh.toFixed(1) + "% share", color: m.idx != null && m.idx >= 100 ? "#D98E32" : "#3FA3AD" };
        })} labelW={130} valW={210} />
      </STPanel>
      <STNote>{(function () {
        var n = V.cutters.length, w = V.cutWinners.length;
        var a = n > 0 ? (w > 0 && w >= Math.ceil(n / 2) ? "Price is buying share in " + bgLbl + ": " + w + " of the " + n + " price cutters gained unit share." : w === 0 ? "Price is NOT buying share in " + bgLbl + ": none of the " + n + " price cutters gained meaningful unit share — demand sits elsewhere (brand, distribution, FOC)." : "Mixed picture: only " + w + " of " + n + " price cutters converted the cut into share.") : "No competitor moved price by more than 1% vs " + C.eff.baseLbl + " — share shifts here are not price-driven.";
        var b = V.raiseHolders.length > 0 ? " " + V.raiseHolders.map(function (m) { return m.name; }).slice(0, 2).join(" and ") + " raised price and still held share — pricing power worth noting." : "";
        return a + b + " Cross-check the FOC Effect dashboard: a share gain that price does not explain is often bonus-driven.";
      })()}</STNote>
    </div>
  );
}
